import React, { useState, useEffect } from 'react'
import { BrowserRouter, Routes, Route, Link, useNavigate, useParams, useLocation } from 'react-router-dom'
import { auth, products, cart, orders, vendors, payments } from './api'
import './App.css'
import translations from './translations';
import Terms from './pages/Terms'
import { PaymentSuccess, PaymentFailed } from './PaymentPages'
import Home from './pages/Home'
import Privacy from './pages/Privacy'
import RefundPolicy from './pages/RefundPolicy'
import Shipping from './pages/Shipping'
import Contact from './pages/Contact'
import CookiePolicy from './pages/CookiePolicy'
import VendorPolicy from './pages/VendorPolicy'
import AccountDeletionPolicy from './pages/AccountDeletionPolicy'
import SiteFooter from './components/SiteFooter'
import ScrollToTop from './components/ScrollToTop'
import { Browser } from '@capacitor/browser';
import { App as CapApp } from '@capacitor/app';
import { COMING_SOON_MODE } from './comingSoonConfig'
const formatQAR = (amount) => `QAR ${Number(amount).toLocaleString('en-QA')}`
const API_ORIGIN = 'https://mobimart-backend-production.up.railway.app'

// Fallback used when a product has no image URL OR when the image fails to load.
// Uses an inline SVG data URI so it ALWAYS renders even with no network,
// blocked external images, or Android WebView CORS restrictions.
const FALLBACK_IMG = 'data:image/svg+xml;utf8,' + encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 300 300" width="300" height="300">' +
  '<rect width="300" height="300" fill="#0f1923"/>' +
  '<text x="150" y="140" font-family="Arial,sans-serif" font-size="28" font-weight="700" fill="#f97316" text-anchor="middle">JASPR</text>' +
  '<text x="150" y="175" font-family="Arial,sans-serif" font-size="20" font-weight="600" fill="#cbd5e1" text-anchor="middle">Market</text>' +
  '<text x="150" y="210" font-family="Arial,sans-serif" font-size="12" fill="#94a3b8" text-anchor="middle">Image unavailable</text>' +
  '</svg>'
)

const getImageSrc = (img) => {
  if (!img) return null

  let value = img
  if (Array.isArray(value)) value = value[0]
  if (value && typeof value === 'object') {
    // Some APIs wrap images in objects like { url: '...' } or { src: '...' }
    value = value.url || value.src || value.path || null
  }
  if (typeof value !== 'string') return null

  value = value.trim()
  if (!value) return null

  // Some APIs can return JSON-stringified image arrays.
  if (value.startsWith('[')) {
    try {
      const parsed = JSON.parse(value)
      return getImageSrc(parsed?.[0])
    } catch (e) {}
  }

  if (value.startsWith('http://') || value.startsWith('https://')) return value
  if (value.startsWith('//')) return `https:${value}`
  if (value.startsWith('/')) return `${API_ORIGIN}${value}`
  return `${API_ORIGIN}/${value}`
}

const getProductImageSrc = (product, index = 0) => {
  if (!product) return null
  const imgs = product.images
  if (Array.isArray(imgs)) return getImageSrc(imgs[index])
  return getImageSrc(imgs)
}

const conditionLabel = (c, lang = 'EN') => {
  const ar = lang === 'AR'
  if (c === 'NEW')      return { text: ar ? 'جديد' : 'New',        bg: '#d1fae5', color: '#065f46' }
  if (c === 'LIKE_NEW') return { text: ar ? 'شبه جديد' : 'Like New', bg: '#fef9c3', color: '#854d0e' }
  if (c === 'GOOD')     return { text: ar ? 'جيد' : 'Good',        bg: '#fed7aa', color: '#9a3412' }
  if (c === 'FAIR')     return { text: ar ? 'مقبول' : 'Fair',       bg: '#fee2e2', color: '#991b1b' }
  return { text: ar ? 'جديد' : 'New', bg: '#d1fae5', color: '#065f46' }
}

// Map DB category names (English) to Arabic display labels. Falls back to the
// original name if no translation exists — so unknown categories still show.
const CATEGORY_AR = {
  'mobiles': 'الجوالات',
  'smartphones': 'الهواتف الذكية',
  'mobile covers': 'أغطية الجوال',
  'accessories': 'الإكسسوارات',
  'tablets': 'الأجهزة اللوحية',
  'computers': 'أجهزة الكمبيوتر',
  'laptops': 'لابتوب',
  'keyboard': 'لوحات المفاتيح',
  'mouse': 'الفأرة',
  'audio': 'الصوتيات',
  'headphones': 'سماعات الرأس',
  'bluetooth headphones': 'سماعات بلوتوث',
  'wearables': 'الأجهزة القابلة للارتداء',
  'smart watch': 'ساعة ذكية',
  'smart watches': 'الساعات الذكية',
  'fashion': 'الأزياء',
  'beauty': 'الجمال',
  'home & kitchen': 'المنزل والمطبخ',
  'kids': 'الأطفال',
  'perfumes': 'العطور'
}
const catLabel = (name, lang = 'EN') => {
  if (!name) return ''
  if (lang === 'AR') return CATEGORY_AR[name.trim().toLowerCase()] || name
  return name
}

function Navbar({ user, cartCount, onLogout, language, setLanguage, t, theme = 'light', toggleTheme = () => {}, onCartClick = null }) {
  const navigate = useNavigate()
  const isMobile = window.innerWidth <= 768
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false)
  const [navCategories, setNavCategories] = React.useState([])
  const [catExpanded, setCatExpanded] = React.useState(false)

  React.useEffect(() => {
    products.getCategories()
      .then(r => {
        const list = Array.isArray(r.data) ? r.data : []
        // Only parent categories (no parentId) for the menu
        const parents = list.filter(c => !c.parentId)
        setNavCategories(parents.length > 0 ? parents : list.slice(0, 14))
      })
      .catch(() => {})
  }, [])

  const closeMobileMenu = () => { setMobileMenuOpen(false); setCatExpanded(false) }

  return (
    <nav style={isMobile ? styles.navMobile : styles.nav}>
      <Link to="/" style={isMobile ? styles.logoMobile : styles.logo} onClick={closeMobileMenu}>
        JASPR <span style={{ color:'#f97316' }}>Market</span>
      </Link>

      {!isMobile && (
        <div style={styles.navLinks}>
          <Link to="/products" style={styles.navLink}>{t('shop')}</Link>

          {user ? (
            <>
              {user.role === 'ADMIN' && (
                <Link to="/admin" style={{ ...styles.navLink, color:'#f97316' }}>
                  {t('admin')}
                </Link>
              )}

              {user.role === 'VENDOR' && (
                <Link to="/vendor" style={styles.navLink}>
                  {t('myStore')}
                </Link>
              )}

              <Link to="/wishlist" style={styles.navLink}>Wishlist</Link>

              {onCartClick ? (
                <button
                  onClick={onCartClick}
                  style={{ ...styles.navLink, background:'none', border:'none', cursor:'pointer', fontSize:14, padding:0, fontFamily:'inherit' }}
                >
                  {t('cart')} {cartCount > 0 && <span style={styles.badge}>{cartCount}</span>}
                </button>
              ) : (
                <Link to="/cart" style={styles.navLink}>
                  {t('cart')} {cartCount > 0 && <span style={styles.badge}>{cartCount}</span>}
                </Link>
              )}

              <Link to="/orders" style={styles.navLink}>{t('myOrders')}</Link>
              <button onClick={onLogout} style={styles.logoutBtn}>{t('logout')}</button>
            </>
          ) : (
            <>
              <Link to="/login" style={styles.navLink}>{t('login')}</Link>
              <Link to="/register" style={styles.registerBtn}>{t('register')}</Link>
            </>
          )}
        </div>
      )}

      <div style={styles.mobileActions}>
        {isMobile && (
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            style={styles.menuBtn}
            aria-label="Open menu"
          >
            ☰
          </button>
        )}

        <button
          onClick={toggleTheme}
          title={theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
          aria-label={theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
          style={styles.smallBtn}
        >
          {theme === 'light' ? '🌙' : '☀️'}
        </button>

        <button
          onClick={() => setLanguage(language === 'EN' ? 'AR' : 'EN')}
          style={styles.smallBtn}
        >
          {language === 'EN' ? 'AR' : 'EN'}
        </button>
      </div>

      {isMobile && mobileMenuOpen && (
        <>
          <div style={styles.mobileMenuBackdrop} onClick={closeMobileMenu} />
          <div style={styles.mobileMenu}>
            <div style={styles.mobileMenuHeader}>
              <strong>JASPR <span style={{ color:'#f97316' }}>Market</span></strong>
              <button onClick={closeMobileMenu} style={styles.mobileCloseBtn}>×</button>
            </div>

            <Link to="/" style={styles.mobileMenuItem} onClick={closeMobileMenu}>Home</Link>
            <Link to="/products" style={styles.mobileMenuItem} onClick={closeMobileMenu}>Shop</Link>

            {/* Categories accordion */}
            {navCategories.length > 0 && (
              <div>
                <button
                  onClick={() => setCatExpanded(!catExpanded)}
                  style={{...styles.mobileMenuItem, width:'100%', textAlign:'left', background:'none', border:'none', cursor:'pointer', display:'flex', justifyContent:'space-between', alignItems:'center', fontFamily:'inherit'}}
                >
                  <span>Categories</span>
                  <span style={{fontSize:12, color:'#94a3b8'}}>{catExpanded ? '▲' : '▼'}</span>
                </button>
                {catExpanded && (
                  <div
                    style={{
                      padding:'10px 14px 14px',
                      margin:'0 12px 10px',
                      borderLeft:'3px solid #f97316',
                      background:'rgba(255,255,255,0.04)',
                      borderRadius:12,
                      display:'grid',
                      gridTemplateColumns:'repeat(2, minmax(0, 1fr))',
                      gap:8
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {navCategories.map(c => (
                      <button
                        key={c.id}
                        type="button"
                        onClick={(e) => {
                          e.preventDefault()
                          e.stopPropagation()
                          navigate(`/products?category=${encodeURIComponent(c.name)}`)
                          closeMobileMenu()
                        }}
                        style={{
                          border:'1px solid rgba(255,255,255,0.08)',
                          background:'rgba(255,255,255,0.06)',
                          color:'#e5e7eb',
                          borderRadius:10,
                          padding:'9px 8px',
                          fontSize:11,
                          fontWeight:700,
                          textAlign:'center',
                          cursor:'pointer',
                          lineHeight:1.2,
                          fontFamily:'inherit'
                        }}
                      >
                        {catLabel(c.name, language)}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            <Link to="/wishlist" style={styles.mobileMenuItem} onClick={closeMobileMenu}>Wishlist</Link>

            {onCartClick ? (
              <button
                onClick={() => {
                  closeMobileMenu()
                  onCartClick()
                }}
                style={styles.mobileMenuButton}
              >
                Cart {cartCount > 0 ? `(${cartCount})` : ''}
              </button>
            ) : (
              <Link to="/cart" style={styles.mobileMenuItem} onClick={closeMobileMenu}>
                Cart {cartCount > 0 ? `(${cartCount})` : ''}
              </Link>
            )}

            {user ? (
              <>
                {user.role === 'ADMIN' && (
                  <Link to="/admin" style={styles.mobileMenuItem} onClick={closeMobileMenu}>Admin</Link>
                )}

                {user.role === 'VENDOR' && (
                  <Link to="/login" style={styles.mobileMenuItem} onClick={closeMobileMenu}>My Store</Link>
                )}

                <Link to="/orders" style={styles.mobileMenuItem} onClick={closeMobileMenu}>My Orders</Link>

                <button
                  onClick={() => {
                    closeMobileMenu()
                    onLogout()
                  }}
                  style={styles.mobileMenuButton}
                >
                  Logout
                </button>
              </>
            ) : (
              <>
                <Link to="/login" style={styles.mobileMenuItem} onClick={closeMobileMenu}>Login</Link>
                <Link to="/register" style={styles.mobileMenuPrimary} onClick={closeMobileMenu}>Register</Link>
              </>
            )}
          </div>
        </>
      )}
    </nav>
  )
}

function Products({
  t = (k) => k,
  language = 'EN',
  wishlist = [],
  toggleWishlist = () => {},
  wishlistOnly = false
}) {
  const location = useLocation()
  const isMobile = window.innerWidth <= 768
  const [items, setItems] = useState([])
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(true)
  const [categories, setCategories] = useState([])
  const [selectedCategory, setSelectedCategory] = useState('')
  const [selectedCondition, setSelectedCondition] = useState('')
  const [selectedVendor, setSelectedVendor] = useState('')
  const [priceRange, setPriceRange] = useState({ min: '', max: '' })
  const [toast, setToast] = useState('')
  const [quickViewProduct, setQuickViewProduct] = useState(null)
  const [quickQty, setQuickQty] = useState(1)
  const [showSuggest, setShowSuggest] = useState(false)
  // Tracks the raw ?category=<name> from the URL so we can pass it to the API
  // immediately even before the categories list has loaded.
  const [urlCategoryName, setUrlCategoryName] = useState('')

  useEffect(() => {
    products.getCategories().then(r => setCategories(r.data)).catch(() => {})
  }, [])

  // When the URL has ?category=<name>, capture it. If the name is a known
  // PARENT (Mobiles, Computers, Audio, Wearables, etc.), leave selectedCategory
  // empty so the descendant-aware urlCategoryFiltered block below handles it
  // by matching child category names. Only set selectedCategory directly when
  // the URL name matches a category exactly AND isn't a parent.
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const name = params.get('category')
    setUrlCategoryName(name || '')
    if (!name) {
      if (selectedCategory) setSelectedCategory('')
      return
    }
    if (categories.length === 0) return
    const requested = name.trim().toLowerCase()
    const isParent = !!(['mobiles','computers','audio','wearables','fashion','beauty','home & kitchen','accessories','kids','perfumes'].includes(requested))
    if (isParent) {
      // Don't pin to a single category id — let the descendant filter handle it.
      if (selectedCategory) setSelectedCategory('')
      return
    }
    const match = categories.find(c => c.name.toLowerCase() === requested)
    if (match && match.id !== selectedCategory) {
      setSelectedCategory(match.id)
    }
  }, [location.search, categories]) // eslint-disable-line

useEffect(() => {
  setLoading(true)

  products.getAll()
    .then(r => {
      setItems(Array.isArray(r.data) ? r.data : [])
      setLoading(false)
    })
    .catch(err => {
      alert('Axios error: ' + (err.message || 'unknown'))
      setItems([])
      setLoading(false)
    })
}, [])

  const vendorOptions = [...new Set(items.map(p => p.vendor?.storeName).filter(Boolean))]

  const parentCategoryMap = {
    mobiles: ['mobiles', 'smartphones', 'mobile covers', 'tablets'],
    computers: ['computers', 'laptops', 'keyboard', 'mouse'],
    audio: ['audio', 'headphones', 'bluetooth headphones'],
    wearables: ['wearables', 'smart watch', 'smart watches'],
    accessories: ['accessories', 'mobile covers', 'keyboard', 'mouse'],
    fashion: ['fashion'],
    beauty: ['beauty'],
    'home & kitchen': ['home & kitchen'],
    kids: ['kids', 'kids toys', 'baby', 'toys', 'kids accessories'],
    perfumes: ['perfumes', 'fragrances', 'cologne', 'perfume']
  }

const baseFiltered = items.filter(p => {

  if (wishlistOnly && !wishlist.includes(p.id)) {
    return false
  }

  if (selectedCategory && p.categoryId !== selectedCategory) {
    return false
  }

  if (priceRange.min && p.price < parseFloat(priceRange.min)) {
    return false
  }

  if (priceRange.max && p.price > parseFloat(priceRange.max)) {
    return false
  }

  if (selectedCondition && p.condition !== selectedCondition) {
    return false
  }

  if (selectedVendor && p.vendor?.storeName !== selectedVendor) {
    return false
  }

  if (search.trim()) {
    const q = search.trim().toLowerCase()
    const haystack = [p.name, p.vendor?.storeName, p.category?.name, p.description, p.brand]
      .filter(Boolean)
      .join(' ')
      .toLowerCase()
    if (!haystack.includes(q)) return false
  }

  return true
})

// If user came from a homepage parent category card like Mobiles / Computers,
// try to filter by matching child category names. If that parent has no matching
// products yet, show all products instead of showing "0 products found".
const urlCategoryFiltered = (!selectedCategory && urlCategoryName)
  ? baseFiltered.filter(p => {
      const requested = urlCategoryName.trim().toLowerCase()
      const productCategory = (p.category?.name || '').trim().toLowerCase()
      const allowedCategories = parentCategoryMap[requested] || [requested]
      return allowedCategories.includes(productCategory)
    })
  : baseFiltered

const filtered = urlCategoryFiltered

const showToast = (message) => {
  setToast(message)

  setTimeout(() => {
    setToast('')
  }, 2500)
}
const quickViewModal = quickViewProduct ? (() => {
  const qvCond = conditionLabel(quickViewProduct.condition, language)
  const qvImg = getProductImageSrc(quickViewProduct)
  return (
  <div
    onClick={() => setQuickViewProduct(null)}
    style={{
      position:'fixed',
      inset:0,
      background:'rgba(15,25,35,0.65)',
      zIndex:9999,
      display:'flex',
      alignItems:'center',
      justifyContent:'center',
      padding: isMobile ? 12 : 20,
      backdropFilter:'blur(6px)'
    }}
  >
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        background:'#fff',
        borderRadius: isMobile ? 18 : 24,
        maxWidth: 900,
        width:'100%',
        maxHeight: isMobile ? '92vh' : '90vh',
        overflowY: 'auto',
        display:'grid',
        gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
        boxShadow:'0 20px 60px rgba(0,0,0,0.25)',
        position:'relative'
      }}
    >
      {/* Close button */}
      <button
        onClick={() => setQuickViewProduct(null)}
        style={{
          position:'absolute', top:12, right:12, zIndex:2,
          width:36, height:36, borderRadius:'50%',
          background:'rgba(255,255,255,0.95)', border:'1px solid #e2e8f0',
          fontSize:22, lineHeight:1, color:'#0f1923', cursor:'pointer',
          display:'flex', alignItems:'center', justifyContent:'center',
          boxShadow:'0 2px 8px rgba(0,0,0,0.12)'
        }}
        aria-label="Close"
      >×</button>

      {/* Image side */}
      <div style={{
        background:'linear-gradient(180deg,#f8fafc,#eef2f7)',
        padding: isMobile ? 24 : 40,
        display:'flex',
        alignItems:'center',
        justifyContent:'center',
        position:'relative',
        minHeight: isMobile ? 240 : 360
      }}>
        <span style={{
          position:'absolute', top:14, left:14,
          padding:'4px 10px', borderRadius:6, fontSize:11, fontWeight:700,
          background: qvCond.bg, color: qvCond.color
        }}>{qvCond.text}</span>
        <img
          src={qvImg || FALLBACK_IMG}
          alt={quickViewProduct.name}
          style={{maxWidth:'100%', maxHeight: isMobile ? 240 : 420, objectFit:'contain'}}
          onError={(e) => { if (e.currentTarget.src !== FALLBACK_IMG) e.currentTarget.src = FALLBACK_IMG }}
        />
      </div>

      {/* Details side */}
      <div style={{padding: isMobile ? 22 : 40}}>
        <div style={{fontSize:12, color:'#94a3b8', marginBottom:8, textTransform:'uppercase', letterSpacing:'0.5px'}}>
          {quickViewProduct.vendor?.storeName}
        </div>

        <h2 style={{fontSize: isMobile ? 22 : 28, marginBottom:14, color:'#0f172a', lineHeight:1.3}}>
          {quickViewProduct.name}
        </h2>

        <div style={{fontSize: isMobile ? 26 : 32, fontWeight:800, color:'#f97316', marginBottom:18}}>
          {formatQAR(quickViewProduct.price)}
        </div>

        <p style={{color:'#64748b', lineHeight:1.6, marginBottom:22, fontSize: isMobile ? 14 : 15}}>
          {language === 'AR'
  ? 'منتج عالي الجودة مع توصيل سريع في جميع أنحاء قطر.'
  : 'Premium quality product with fast delivery across Qatar.'}
        </p>

        <div style={{display:'flex', alignItems:'center', gap:12, marginBottom:18}}>
          <span style={{fontWeight:600, color:'#0f1923'}}>
            {language === 'AR' ? 'الكمية' : 'Qty'}
          </span>
          <div style={{display:'flex', alignItems:'center', gap:8, border:'1px solid #e2e8f0', borderRadius:10, padding:'2px 4px'}}>
            <button onClick={() => setQuickQty(Math.max(1, quickQty - 1))}
              style={{width:32, height:32, border:'none', background:'transparent', cursor:'pointer', fontSize:18, color:'#0f1923'}}>−</button>
            <strong style={{minWidth:24, textAlign:'center', color:'#0f1923'}}>{quickQty}</strong>
            <button onClick={() => setQuickQty(quickQty + 1)}
              style={{width:32, height:32, border:'none', background:'transparent', cursor:'pointer', fontSize:18, color:'#0f1923'}}>+</button>
          </div>
        </div>

        <div style={{display:'flex', gap:10, flexDirection: isMobile ? 'column' : 'row'}}>
          <button
            onClick={async () => {
              try {
                await cart.add({ productId: quickViewProduct.id, quantity: quickQty })
                showToast(`Added ${quickQty} item(s) to cart`)
                setQuickViewProduct(null)
                try {
                  const r = await cart.get()
                  const count = (r.data.items || []).reduce((s, i) => s + i.quantity, 0)
                  window.dispatchEvent(new CustomEvent('jaspr-cart-updated', { detail: count }))
                } catch (e) {}
              } catch (e) {
                showToast(language === 'AR' ? 'يرجى تسجيل الدخول أولاً' : 'Please log in to add items')
              }
            }}
            style={{flex:1, padding:'14px 18px', borderRadius:14, border:'none', background:'#f97316', color:'#fff', fontWeight:700, cursor:'pointer', fontSize:15}}
          >
            {language === 'AR' ? 'أضف إلى السلة' : 'Add to Cart'}
          </button>

          <button
            onClick={() => {
              window.location.href = `/products/${quickViewProduct.id}`
            }}
            style={{flex:1, padding:'14px 18px', borderRadius:14, border:'1px solid #dfe3e8', background:'#fff', color:'#0f1923', fontWeight:700, cursor:'pointer', fontSize:15}}
          >
            {language === 'AR' ? 'عرض التفاصيل' : 'View Details'}
          </button>

          <button
            onClick={() => toggleWishlist(quickViewProduct.id)}
            style={{padding:'14px 18px', borderRadius:14, border:'1px solid #dfe3e8', background:'#fff', cursor:'pointer', fontWeight:700}}
          >
            ♥
          </button>
        </div>
      </div>
    </div>
  </div>
  )
})() : null

  const clearFilters = () => {
    setSelectedCategory('')
    setSelectedVendor('')
    setSelectedCondition('')
    setPriceRange({ min: '', max: '' })
    setSearch('')
  }
  const categoryName = (name) => {
  if (language !== 'AR') return name

  const map = {
    'Accessories': 'الإكسسوارات',
    'Audio': 'الصوتيات',
    'Beauty': 'الجمال',
    'Bluetooth Headphones': 'سماعات بلوتوث',
    'Computers': 'أجهزة الكمبيوتر',
    'Fashion': 'الأزياء',
    'Headphones': 'سماعات الرأس',
    'Keyboard': 'لوحات المفاتيح',
    'Home & Kitchen': 'المنزل والمطبخ',
    'Mobile Covers': 'أغطية الجوال',
    'Laptops': 'اللابتوبات',
    'Mouse': 'الفأرة',
    'Mobiles': 'الجوالات',
    'Smartphones': 'الهواتف الذكية',
    'Smart Watch': 'ساعة ذكية',
    'Smart Watches': 'الساعات الذكية',
    'Tablets': 'الأجهزة اللوحية',
    'Wearables': 'الأجهزة القابلة للارتداء',
    'Kids': 'الأطفال',
    'Perfumes': 'العطور'
  }

  return map[name] || name
}

  return (
  <div className="jm-page" style={{background:'#f8f9fa', minHeight:'100vh'}}>
    {toast && (
      <div
        style={{
          position:'fixed',
          bottom:20,
          right:20,
          background:'#0f1923',
          color:'#fff',
          padding:'12px 18px',
          borderRadius:10,
          zIndex:9999,
          fontWeight:600,
          boxShadow:'0 6px 20px rgba(0,0,0,0.2)'
        }}
      >
        {toast}
      </div>
    )}

    {quickViewModal}
      {/* Hero Banner — hidden when browsing a category */}
      {!urlCategoryName && !selectedCategory && !search && (
      <div style={{background:'linear-gradient(135deg, #0f1923 0%, #1e3a5f 100%)', padding:'40px 32px', marginBottom:32, position:'relative', overflow:'hidden'}}>
        <div style={{position:'absolute', top:0, left:0, right:0, bottom:0, display:'flex', flexWrap:'wrap', opacity:0.06, pointerEvents:'none', overflow:'hidden', alignContent:'flex-start'}}>
          {['📱','💻','🎧','⌨️','🖱️','📷','🔋','🎮','⌚','🖥️','📡','💾'].map((emoji, i) => (
            Array.from({length:8}).map((_, j) => (
              <span key={i+'-'+j} style={{fontSize:44, padding:'10px 14px', display:'inline-block'}}>{emoji}</span>
            ))
          ))}
        </div>
        <div style={{position:'relative', zIndex:1, maxWidth:1100, margin:'0 auto'}}>
          <h1 style={{fontSize:36, fontWeight:800, color:'#fff', marginBottom:10, lineHeight:1.2}}>{t('heroTitle')}</h1>
          <p style={{color:'#94a3b8', fontSize:16, marginBottom:24}}>{t('heroSub')}</p>
          <div style={{display:'flex', gap:16, flexWrap:'wrap'}}>
            {[['📱','500+','Products'],['🏪','50+','Vendors'],['⭐','4.8','Avg Rating'],['🚚','Free','Delivery']].map(([icon,val,label]) => (
              <div key={label} style={{background:'rgba(255,255,255,0.08)', borderRadius:10, padding:'12px 20px', textAlign:'center'}}>
                <div style={{fontSize:20}}>{icon}</div>
                <div style={{color:'#f97316', fontWeight:800, fontSize:18}}>{val}</div>
                <div style={{color:'#94a3b8', fontSize:11}}>{label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
      )}

      <div style={{maxWidth:1100, margin:'0 auto', padding:'0 32px 100px'}}>
        <RecentlyViewedStrip items={items} t={t} />
        <div
  style={{
    position:'relative',
    zIndex:5,
    padding:'8px 12px',
    backdropFilter:'blur(10px)',
    background:'rgba(248,249,250,0.92)',
    borderRadius:20,
    marginBottom:20,
    maxWidth:1100,
    margin:'0 auto 20px',
    boxShadow:'0 8px 24px rgba(15,25,35,0.08)',
    border:'1px solid #eef0f3',
  }}
>
        <input
          style={{...styles.search, background:'#fff', boxShadow:'0 2px 8px rgba(0,0,0,0.08)'}}
          placeholder="Search products, brands, categories..."
          value={search}
          onChange={e => { setSearch(e.target.value); setShowSuggest(true) }}
          onFocus={() => setShowSuggest(true)}
          onBlur={() => setTimeout(() => setShowSuggest(false), 180)}
        />
        {showSuggest && search.trim().length >= 1 && (() => {
          const q = search.trim().toLowerCase()
          const matches = items.filter(p =>
            p.name?.toLowerCase().includes(q) ||
            p.vendor?.storeName?.toLowerCase().includes(q) ||
            p.category?.name?.toLowerCase().includes(q)
          ).slice(0, 6)
          if (matches.length === 0) return null
          return (
            <div className="jm-card" style={{position:'absolute', top:'100%', left:12, right:12, background:'#fff', border:'1px solid #eef0f3', borderRadius:14, boxShadow:'0 12px 32px rgba(15,25,35,0.18)', marginTop:6, overflow:'hidden', zIndex:10}}>
              {matches.map(p => {
                const img = p.images?.[0]
                const src = getImageSrc(img)
                return (
                  <Link
                    key={p.id}
                    to={`/products/${p.id}`}
                    onMouseDown={(e) => e.preventDefault()}
                    style={{display:'flex', alignItems:'center', gap:12, padding:'10px 14px', textDecoration:'none', color:'inherit', borderBottom:'1px solid #f1f5f9'}}
                  >
                    <div style={{width:38, height:38, borderRadius:8, background:'#f8fafc', display:'flex', alignItems:'center', justifyContent:'center', overflow:'hidden', flexShrink:0}}>
                      <img src={src || FALLBACK_IMG} alt="" style={{width:'100%', height:'100%', objectFit:'contain'}} onError={(e) => { if (e.currentTarget.src !== FALLBACK_IMG) e.currentTarget.src = FALLBACK_IMG }} />
                    </div>
                    <div style={{flex:1, minWidth:0}}>
                      <div className="jm-card-title" style={{fontSize:13, fontWeight:600, color:'#0f1923', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>{p.name}</div>
                      <div className="jm-card-meta" style={{fontSize:11, color:'#94a3b8'}}>{p.vendor?.storeName} · {catLabel(p.category?.name, language)}</div>
                    </div>
                    <div style={{fontSize:13, fontWeight:700, color:'#f97316', whiteSpace:'nowrap'}}>{formatQAR(p.price)}</div>
                  </Link>
                )
              })}
            </div>
          )
        })()}
        </div>

        {!loading && items.length > 0 && <RecommendedForYou items={items} t={t} />}

        <div className="jm-shop-layout">
          <aside className="jm-shop-filters">
        <div style={{display:'flex', gap:12, marginBottom:24, flexWrap:'wrap', alignItems:'center'}}>
          <select value={selectedVendor} onChange={e => setSelectedVendor(e.target.value)} style={{padding:'10px 14px', borderRadius:8, border:'1px solid #ddd', fontSize:14, background:'#fff', cursor:'pointer', minWidth:160}}>
            <option value="">{language === 'AR' ? 'جميع البائعين' : 'All Vendors'}</option>
            {vendorOptions.map(v => <option key={v} value={v}>{v}</option>)}
          </select>

          <select value={selectedCondition} onChange={e => setSelectedCondition(e.target.value)} style={{padding:'10px 14px', borderRadius:8, border:'1px solid #ddd', fontSize:14, background:'#fff', cursor:'pointer', minWidth:140}}>
            <option value="">{language === 'AR' ? 'جميع الحالات' : 'All Conditions'}</option>
            <option value="NEW">{t('conditionNew')}</option>
            <option value="LIKE_NEW">{t('conditionLikeNew')}</option>
            <option value="GOOD">{t('conditionGood')}</option>
            <option value="FAIR">{t('conditionFair')}</option>
          </select>

          <input style={{padding:'10px 14px', borderRadius:8, border:'1px solid #ddd', fontSize:14, width:120}} placeholder={t('minPrice')} type="number" value={priceRange.min} onChange={e => setPriceRange({...priceRange, min: e.target.value})} />
          <span style={{color:'#666'}}>{t('to')}</span>
          <input style={{padding:'10px 14px', borderRadius:8, border:'1px solid #ddd', fontSize:14, width:120}} placeholder={t('maxPrice')} type="number" value={priceRange.max} onChange={e => setPriceRange({...priceRange, max: e.target.value})} />

          {(selectedCategory || selectedVendor || priceRange.min || priceRange.max || search || selectedCondition) && (
            <button onClick={clearFilters} style={{padding:'10px 16px', borderRadius:8, border:'1px solid #ddd', background:'#f8f9fa', cursor:'pointer', fontSize:14, color:'#666'}}>
              {t('clearFilters')}
            </button>
          )}

          <span style={{color:'#888', fontSize:14, marginLeft:'auto'}}>
            {loading ? t('loading') : `${filtered.length} ${t('found')}`}
          </span>
        </div>


          </aside>
          <main className="jm-shop-main">

        {loading ? (
          <div style={isMobile ? styles.gridMobile : styles.grid}>
            {Array.from({length: 8}).map((_, i) => <ProductSkeleton key={i} />)}
          </div>
        ) : filtered.length === 0 ? (
          <div className="jm-card" style={{textAlign:'center', padding:'80px 24px', background:'#fff', borderRadius:24, boxShadow:'0 12px 32px rgba(15,25,35,0.08)', border:'1px solid #eef0f3', maxWidth:620, margin:'40px auto'}}>
            <div style={{fontSize:54, marginBottom:16}}>🛒</div>
            <h3 className="jm-card-title" style={{fontSize:26, color:'#0f1923', marginBottom:10}}>{t('noProducts')}</h3>
            <p className="jm-card-meta" style={{color:'#667085', marginBottom:24}}>Try changing your filters or check back soon for new verified products.</p>
            <button onClick={clearFilters} style={{...styles.heroBtn, padding:'14px 28px', borderRadius:14}}>{t('clearFilters')}</button>
          </div>
        ) : (
          <div style={isMobile ? styles.gridMobile : styles.grid}>
            {filtered.map(p => {
              const cond = conditionLabel(p.condition, language)
              const imgSrc = getProductImageSrc(p)
              const avgRating = Math.round(p.avgRating || 0)

              return (
                <Link
                  key={p.id}
                  to={`/products/${p.id}`}
                  style={{textDecoration:'none', color:'inherit', display:'block'}}
                >
                  <article
                    style={{
                      border:'1px solid #eef0f3',
                      borderRadius: isMobile ? 16 : 20,
                      overflow:'hidden',
                      background:'#fff',
                      boxShadow:'0 6px 18px rgba(15,25,35,0.06)',
                      display:'flex',
                      flexDirection:'column',
                      height: isMobile ? 260 : 340,
                      cursor:'pointer'
                    }}
                  >
                    {/* IMAGE — primary visual, fills most of the card */}
                    <div style={{
                      flex:1,
                      background:'#f8fafc',
                      position:'relative',
                      overflow:'hidden',
                      display:'flex',
                      alignItems:'center',
                      justifyContent:'center'
                    }}>
                      <img
                        src={imgSrc || FALLBACK_IMG}
                        alt={p.name}
                        loading="lazy"
                        style={{
                          width:'100%',
                          height:'100%',
                          objectFit:'cover',
                          display:'block'
                        }}
                        onError={(e) => { if (e.currentTarget.src !== FALLBACK_IMG) e.currentTarget.src = FALLBACK_IMG }}
                      />
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault()
                          e.stopPropagation()
                          toggleWishlist(p.id)
                          showToast(wishlist.includes(p.id) ? 'Removed from wishlist' : 'Added to wishlist')
                        }}
                        style={{
                          position:'absolute',
                          top:8,
                          right:8,
                          width:32,
                          height:32,
                          borderRadius:'50%',
                          border:'none',
                          background:'rgba(255,255,255,0.92)',
                          display:'flex',
                          alignItems:'center',
                          justifyContent:'center',
                          cursor:'pointer',
                          boxShadow:'0 2px 6px rgba(0,0,0,0.12)',
                          padding:0
                        }}
                        aria-label="Toggle wishlist"
                      >
                        <span style={{color: wishlist.includes(p.id) ? '#ff4d6d' : '#94a3b8', fontSize:16, lineHeight:1}}>♥</span>
                      </button>

                      {/* Quick View button — bottom of image on hover/tap */}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault()
                          e.stopPropagation()
                          setQuickViewProduct(p)
                        }}
                        style={{
                          position:'absolute',
                          bottom:8,
                          left:'50%',
                          transform:'translateX(-50%)',
                          padding:'6px 14px',
                          borderRadius:20,
                          border:'none',
                          background:'rgba(15,25,35,0.82)',
                          color:'#fff',
                          fontSize:11,
                          fontWeight:600,
                          cursor:'pointer',
                          whiteSpace:'nowrap',
                          letterSpacing:0.3,
                          boxShadow:'0 2px 8px rgba(0,0,0,0.2)'
                        }}
                      >
                        Quick View
                      </button>
                    </div>

                    {/* BOTTOM — name + price only */}
                    <div style={{padding: isMobile ? '10px 12px 12px' : '14px 16px 16px', display:'flex', flexDirection:'column', gap:6}}>
                      <div
                        style={{
                          fontSize: isMobile ? 13 : 14,
                          fontWeight:600,
                          color:'#0f1923',
                          lineHeight:1.3,
                          overflow:'hidden',
                          textOverflow:'ellipsis',
                          display:'-webkit-box',
                          WebkitLineClamp:2,
                          WebkitBoxOrient:'vertical',
                          minHeight: isMobile ? 32 : 36
                        }}
                      >
                        {p.name}
                      </div>
                      <div style={{fontSize: isMobile ? 16 : 18, fontWeight:800, color:'#f97316'}}>
                        {formatQAR(p.price)}
                      </div>
                    </div>
                  </article>
                </Link>
              )
            })}
          </div>
        )}
          </main>
        </div>
      </div>
    </div>
  )
}

function Login({ onLogin, t = (k) => k }) {
  const [form, setForm] = useState({ email: '', password: '' })
  const [error, setError] = useState('')
  const isMobile = window.innerWidth <= 768

  const handleSubmit = async (e) => {
    e.preventDefault()
    try {
      const res = await auth.login(form)
      localStorage.setItem('token', res.data.token)
      onLogin(res.data.user)
      window.location.href = '/'
    } catch (err) {
      setError(err.response?.data?.error || t('invalidCredentials'))
    }
  }

  return (
    <div style={{minHeight:'100vh', background: isMobile ? '#fff' : '#f8f9fa', display:'flex', flexDirection:'column'}}>
      {/* Orange header strip */}
      <div style={{background:'#f97316', padding: isMobile ? '24px 20px 20px' : '36px 40px 32px', color:'#fff'}}>
        <div style={{maxWidth: isMobile ? 'none' : 460, margin: isMobile ? 0 : '0 auto'}}>
          <div style={{fontSize:22, fontWeight:800, letterSpacing:0.5}}>JASPR <span style={{fontWeight:400}}>Market</span></div>
          <div style={{fontSize:13, opacity:0.9, marginTop:4}}>Qatar's Trusted Tech Marketplace</div>
        </div>
      </div>

      <div style={{flex:1, display:'flex', justifyContent:'center', padding: isMobile ? '28px 24px' : '48px 24px'}}>
        <div style={{width:'100%', maxWidth: isMobile ? '100%' : 460, background:'#fff', borderRadius: isMobile ? 0 : 16, padding: isMobile ? 0 : 36, boxShadow: isMobile ? 'none' : '0 4px 24px rgba(15,23,42,0.08)', boxSizing:'border-box'}}>
        <h2 style={{fontSize:22, fontWeight:700, marginBottom:6, color:'#0f1923'}}>Sign in</h2>
        <p style={{fontSize:13, color:'#64748b', marginBottom:24}}>Welcome back! Sign in to continue shopping.</p>

        {error && <div style={{background:'#fef2f2', border:'1px solid #fca5a5', color:'#dc2626', borderRadius:8, padding:'10px 14px', marginBottom:16, fontSize:13}}>{error}</div>}

        <input
          style={{...styles.input, fontSize:15}}
          placeholder="Email address"
          value={form.email}
          onChange={e => setForm({...form, email: e.target.value})}
          autoComplete="email"
        />
        <input
          style={{...styles.input, fontSize:15}}
          type="password"
          placeholder="Password"
          value={form.password}
          onChange={e => setForm({...form, password: e.target.value})}
          autoComplete="current-password"
        />

        <button style={{...styles.submitBtn, fontSize:15, padding:'14px', borderRadius:10}} onClick={handleSubmit}>
          Sign In
        </button>

        <div style={{textAlign:'center', marginTop:16, fontSize:13, color:'#64748b'}}>
          Don't have an account? <Link to="/register" style={{color:'#f97316', fontWeight:600}}>Register</Link>
        </div>

        {/* Trust badges */}
        <div style={{marginTop:40, display:'flex', justifyContent:'center', gap:28, flexWrap:'wrap'}}>
          {['🔒 Secure Login', '🛡️ Data Protected', '✅ Verified Vendors'].map(b => (
            <div key={b} style={{fontSize:12, color:'#94a3b8', display:'flex', alignItems:'center', gap:4}}>{b}</div>
          ))}
        </div>
        </div>
      </div>
    </div>
  )
}

function Profile({ user, onLogout, t = (k) => k, language = 'EN' }) {
  const navigate = useNavigate()
  const displayUser = user || {}
  const initial = (displayUser.name || displayUser.email || 'U')[0].toUpperCase()

  const menuItems = [
    { icon: '📦', label: 'Your Orders', to: '/orders' },
    { icon: '❤️', label: 'Wishlist', to: '/wishlist' },
    { icon: '📍', label: 'Saved Addresses', action: () => navigate('/checkout') },
    { icon: '💳', label: 'Payment Methods', action: () => {} },
    { icon: '🔔', label: 'Notifications', action: () => {} },
  ]

  const policyItems = [
    { icon: '📄', label: 'Terms & Conditions', to: '/terms' },
    { icon: '🔐', label: 'Privacy Policy', to: '/privacy' },
    { icon: '↩️', label: 'Refund Policy', to: '/refund-policy' },
    { icon: '🚚', label: 'Shipping Policy', to: '/shipping' },
    { icon: '🍪', label: 'Cookie Policy', to: '/cookie-policy' },
    { icon: '🏪', label: 'Vendor Policy', to: '/vendor-policy' },
    { icon: '🗑️', label: 'Account Deletion', to: '/account-deletion' },
    { icon: '📞', label: 'Contact Us', to: '/contact' },
  ]

  const handleLogout = () => {
    localStorage.removeItem('token')
    if (onLogout) onLogout()
    navigate('/')
    window.location.reload()
  }

  if (!localStorage.getItem('token')) {
    return (
      <div style={{...styles.page, textAlign:'center', paddingTop:60}}>
        <div style={{fontSize:64, marginBottom:16}}>👤</div>
        <h2 style={{marginBottom:8}}>Sign in to your account</h2>
        <p style={{color:'#64748b', marginBottom:24}}>View orders, wishlist, and manage your profile</p>
        <Link to="/login" style={{display:'inline-block', background:'#f97316', color:'#fff', padding:'13px 32px', borderRadius:10, fontWeight:700, textDecoration:'none', fontSize:15}}>Sign In</Link>
        <div style={{marginTop:12}}>
          <Link to="/register" style={{color:'#f97316', fontSize:13}}>New customer? Register here</Link>
        </div>
      </div>
    )
  }

  return (
    <div style={{background:'#f8f9fa', minHeight:'100vh', paddingBottom:80}}>
      {/* Profile header */}
      <div style={{background:'linear-gradient(135deg, #0f1923 0%, #1e3a5f 100%)', padding:'28px 20px 24px', color:'#fff'}}>
        <div style={{display:'flex', alignItems:'center', gap:16}}>
          <div style={{width:60, height:60, borderRadius:'50%', background:'#f97316', display:'flex', alignItems:'center', justifyContent:'center', fontSize:24, fontWeight:700, color:'#fff', flexShrink:0}}>
            {initial}
          </div>
          <div>
            <div style={{fontSize:18, fontWeight:700}}>{displayUser.name || 'My Account'}</div>
            <div style={{fontSize:13, opacity:0.7, marginTop:2}}>{displayUser.email || ''}</div>
            {displayUser.phone && <div style={{fontSize:12, opacity:0.6}}>{displayUser.phone}</div>}
          </div>
        </div>

        {/* Stats row */}
        <div style={{display:'flex', gap:0, marginTop:20, background:'rgba(255,255,255,0.08)', borderRadius:12, overflow:'hidden'}}>
          {[
            { label: 'Orders', icon: '📦', to: '/orders' },
            { label: 'Wishlist', icon: '❤️', to: '/wishlist' },
            { label: 'Cart', icon: '🛒', to: '/cart' },
          ].map((s, i) => (
            <Link key={s.label} to={s.to} style={{flex:1, textAlign:'center', padding:'14px 8px', color:'#fff', textDecoration:'none', borderRight: i < 2 ? '1px solid rgba(255,255,255,0.12)' : 'none'}}>
              <div style={{fontSize:20}}>{s.icon}</div>
              <div style={{fontSize:11, marginTop:4, opacity:0.8}}>{s.label}</div>
            </Link>
          ))}
        </div>
      </div>

      {/* Main menu */}
      <div style={{margin:'16px 16px 0', background:'#fff', borderRadius:14, overflow:'hidden', boxShadow:'0 2px 8px rgba(0,0,0,0.06)'}}>
        {menuItems.map((item, i) => (
          item.to ? (
            <Link key={item.label} to={item.to} style={{display:'flex', alignItems:'center', gap:14, padding:'15px 18px', color:'#0f1923', textDecoration:'none', borderBottom: i < menuItems.length-1 ? '1px solid #f1f5f9' : 'none'}}>
              <span style={{fontSize:20, width:28, textAlign:'center'}}>{item.icon}</span>
              <span style={{flex:1, fontSize:15, fontWeight:500}}>{item.label}</span>
              <span style={{color:'#cbd5e1', fontSize:16}}>›</span>
            </Link>
          ) : (
            <button key={item.label} onClick={item.action} style={{display:'flex', alignItems:'center', gap:14, padding:'15px 18px', color:'#0f1923', background:'none', border:'none', width:'100%', cursor:'pointer', borderBottom: i < menuItems.length-1 ? '1px solid #f1f5f9' : 'none', textAlign:'left'}}>
              <span style={{fontSize:20, width:28, textAlign:'center'}}>{item.icon}</span>
              <span style={{flex:1, fontSize:15, fontWeight:500}}>{item.label}</span>
              <span style={{color:'#cbd5e1', fontSize:16}}>›</span>
            </button>
          )
        ))}
      </div>

      {/* Policies section */}
      <div style={{margin:'16px 16px 0', background:'#fff', borderRadius:14, overflow:'hidden', boxShadow:'0 2px 8px rgba(0,0,0,0.06)'}}>
        <div style={{padding:'12px 18px', fontSize:12, fontWeight:700, color:'#94a3b8', letterSpacing:0.8, textTransform:'uppercase'}}>Legal & Policies</div>
        {policyItems.map((item, i) => (
          <Link key={item.label} to={item.to} style={{display:'flex', alignItems:'center', gap:14, padding:'13px 18px', color:'#334155', textDecoration:'none', borderTop:'1px solid #f1f5f9'}}>
            <span style={{fontSize:17, width:28, textAlign:'center'}}>{item.icon}</span>
            <span style={{flex:1, fontSize:14}}>{item.label}</span>
            <span style={{color:'#cbd5e1', fontSize:16}}>›</span>
          </Link>
        ))}
      </div>

      {/* Sign out */}
      <div style={{margin:'16px 16px 0', background:'#fff', borderRadius:14, overflow:'hidden', boxShadow:'0 2px 8px rgba(0,0,0,0.06)'}}>
        <button onClick={handleLogout} style={{display:'flex', alignItems:'center', gap:14, padding:'15px 18px', color:'#ef4444', background:'none', border:'none', width:'100%', cursor:'pointer', fontSize:15, fontWeight:600}}>
          <span style={{fontSize:20, width:28, textAlign:'center'}}>🚪</span>
          Sign Out
        </button>
      </div>

      <div style={{textAlign:'center', padding:'20px 0', fontSize:11, color:'#cbd5e1'}}>
        JASPR Market · Qatar's Trusted Tech Marketplace
      </div>
    </div>
  )
}

function Register({ onLogin, t = (k) => k }) {
  const [form, setForm] = useState({ name: '', email: '', password: '', phone: '', role: 'CUSTOMER' })
  const [error, setError] = useState('')
  const [submitted, setSubmitted] = useState(false)

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    try {
      const res = await auth.register(form)
      // New accounts are PENDING admin approval — no auto-login.
      if (res.data.pending || !res.data.token) {
        setSubmitted(true)
        return
      }
      // (fallback, should not happen while approval is required)
      localStorage.setItem('token', res.data.token)
      onLogin(res.data.user)
      window.location.href = '/'
    } catch (err) {
      setError(err.response?.data?.error || t('registrationFailed'))
    }
  }

  if (submitted) {
    return (
      <div style={styles.formPage}>
        <div style={{...styles.formBox, textAlign:'center'}}>
          <div style={{fontSize:48, marginBottom:12}}>✅</div>
          <h2 style={styles.formTitle}>Account Created</h2>
          <p style={{color:'#475569', lineHeight:1.6, marginBottom:20}}>
            Your account has been created and is <strong>pending admin approval</strong>.
            You will be able to sign in and start shopping once your account is approved.
          </p>
          <Link to="/login" style={{display:'inline-block', background:'#f97316', color:'#fff', padding:'12px 28px', borderRadius:10, textDecoration:'none', fontWeight:700}}>
            Go to Login
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div style={styles.formPage}>
      <div style={styles.formBox}>
        <h2 style={styles.formTitle}>{t('createAccount')}</h2>
        {error && <p style={styles.error}>{error}</p>}
        <input style={styles.input} placeholder={t('fullName')} value={form.name} onChange={e => setForm({...form, name: e.target.value})} />
        <input style={styles.input} placeholder={t('email')} value={form.email} onChange={e => setForm({...form, email: e.target.value})} />
        <input style={styles.input} type="password" placeholder={t('password')} value={form.password} onChange={e => setForm({...form, password: e.target.value})} />
        <input style={styles.input} placeholder={t('phone')} value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} />
        <select style={styles.input} value={form.role} onChange={e => setForm({...form, role: e.target.value})}>
          <option value="CUSTOMER">{t('customer')}</option>
          <option value="VENDOR">{t('vendor')}</option>
        </select>
        {form.role === 'VENDOR' && (
          <div style={{background:'#fff7ed', border:'1px solid #f97316', borderRadius:8, padding:12, marginBottom:12, fontSize:13, color:'#92400e'}}>
            ⚠️ {t('vendorPendingNote')}
          </div>
        )}
        <button style={styles.submitBtn} onClick={handleSubmit}>{t('createAccount')}</button>
        <p style={{textAlign:'center', marginTop:12}}>{t('haveAccount')} <Link to="/login">{t('login')}</Link></p>
      </div>
    </div>
  )
}

function Cart({ onCartUpdate, t = (k) => k }) {
  const [cartData, setCartData] = useState({ items: [], total: 0 })
  const [loading, setLoading] = useState(true)
  const [selectedItems, setSelectedItems] = useState([])
  const navigate = useNavigate()
  const isMobile = window.innerWidth <= 768

  // Auto-select all items when cart loads
  useEffect(() => {
    if (cartData.items.length > 0) {
      setSelectedItems(cartData.items.map(i => i.id))
    }
  }, [cartData.items.length]) // eslint-disable-line

  const toggleItem = (id) => setSelectedItems(prev =>
    prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
  )
  const allSelected = cartData.items.length > 0 && selectedItems.length === cartData.items.length
  const toggleAll = () => setSelectedItems(allSelected ? [] : cartData.items.map(i => i.id))
  const checkedItems = cartData.items.filter(i => selectedItems.includes(i.id))
  const subtotal = checkedItems.reduce((s, i) => s + (i.product.price * i.quantity), 0)

  const loadCart = () => {
    if (!localStorage.getItem('token')) {
      setCartData({ items: [], total: 0 })
      setLoading(false)
      return
    }

    cart.get().then(r => {
      setCartData(r.data)
      setLoading(false)
      const total = (r.data.items || []).reduce((sum, i) => sum + i.quantity, 0)
      if (onCartUpdate) onCartUpdate(total)
    }).catch(() => {
      setCartData({ items: [], total: 0 })
      setLoading(false)
    })
  }

  useEffect(() => { loadCart() }, []) // eslint-disable-line

  const handleUpdate = async (productId, qty) => {
    if (qty < 1) return
    await cart.update(productId, { quantity: qty })
    loadCart()
  }

  const handleRemove = async (productId) => {
    await cart.remove(productId)
    loadCart()
  }

  if (loading) return <p style={{padding:40}}>{t('loading')}</p>

  return (
    <div style={{display:'flex', flexDirection:'column', minHeight:'100vh', paddingTop:16}}>
      <div style={{padding:'0 16px 8px', maxWidth:900, width:'100%', margin:'0 auto'}}>
        <h2 style={{marginBottom:16}}>{t('yourCart')}</h2>
      </div>
      {cartData.items.length === 0 ? (
        <div style={{textAlign:'center', padding:60}}>
          <p style={{marginBottom:20}}>{t('cartEmpty')}</p>
          <Link to="/products" style={styles.heroBtn}>{t('shopNow')}</Link>
        </div>
      ) : (
        <>
          {/* Scrollable items area */}
          <div style={{flex:1, overflowY:'auto', overflowX:'hidden', padding:'0 16px 8px', maxWidth:900, width:'100%', margin:'0 auto', boxSizing:'border-box'}}>
            {/* Select All row */}
            <div style={{display:'flex', alignItems:'center', gap:10, marginBottom:12, padding:'8px 0', borderBottom:'1px solid #e2e8f0'}}>
              <div onClick={toggleAll} style={{width:22, height:22, borderRadius:4, border:'2px solid', borderColor: allSelected ? '#f97316' : '#cbd5e1', background: allSelected ? '#f97316' : '#fff', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', flexShrink:0}}>
                {allSelected && <span style={{color:'#fff', fontSize:13, fontWeight:700}}>✓</span>}
              </div>
              <span style={{fontWeight:600, fontSize:14, color:'#0f1923'}}>{allSelected ? 'Deselect All' : 'Select All'} ({cartData.items.length} items)</span>
            </div>
            {cartData.items.map(item => {
              const checked = selectedItems.includes(item.id)
              return (
                <div key={item.id} style={{...styles.cartItem, flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'stretch' : 'center', opacity: checked ? 1 : 0.5, border: checked ? '1.5px solid #f97316' : '1.5px solid #e2e8f0', borderRadius:10, marginBottom:10, transition:'all 0.15s', boxSizing:'border-box'}}>
                  <div style={{display:'flex', alignItems:'center', minWidth:0}}>
                    <div onClick={() => toggleItem(item.id)} style={{width:22, height:22, borderRadius:4, border:'2px solid', borderColor: checked ? '#f97316' : '#cbd5e1', background: checked ? '#f97316' : '#fff', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', flexShrink:0, marginRight:8}}>
                      {checked && <span style={{color:'#fff', fontSize:13, fontWeight:700}}>✓</span>}
                    </div>
                    <div style={{fontSize:32, flexShrink:0}}>📱</div>
                    <div style={{flex:1, minWidth:0, marginLeft:12}}>
                      <p style={{fontWeight:500, wordBreak:'break-word'}}>{item.product.name}</p>
                      <p style={{color:'#666', fontSize:14}}>{item.product.vendor?.storeName}</p>
                    </div>
                  </div>
                  <div style={{display:'flex', alignItems:'center', gap:12, flexWrap:'wrap', justifyContent: isMobile ? 'space-between' : 'flex-end', marginTop: isMobile ? 12 : 0, paddingLeft: isMobile ? 30 : 0}}>
                    <div style={{display:'flex', alignItems:'center', gap:8}}>
                      <button onClick={() => item.quantity === 1 ? handleRemove(item.productId) : handleUpdate(item.productId, item.quantity - 1)} style={styles.qtyBtn}>-</button>
                      <span style={{fontWeight:500, minWidth:20, textAlign:'center'}}>{item.quantity}</span>
                      <button onClick={() => handleUpdate(item.productId, item.quantity + 1)} style={styles.qtyBtn}>+</button>
                    </div>
                    <p style={{color:'#f97316', fontWeight:500, minWidth: isMobile ? 'auto' : 90, textAlign:'right', margin:0}}>{formatQAR(item.product.price * item.quantity)}</p>
                    <button onClick={() => handleRemove(item.productId)} style={styles.removeBtn}>X</button>
                  </div>
                </div>
              )
            })}
          </div>

          {/* Sticky bottom: total + checkout button — always visible */}
          <div style={{
            position:'sticky', bottom:0, background:'#fff',
            borderTop:'1px solid #e2e8f0',
            padding:'12px 16px',
            paddingBottom:'calc(12px + env(safe-area-inset-bottom, 0px))',
            boxShadow:'0 -3px 14px rgba(0,0,0,0.08)',
            zIndex:10
          }}>
            <div style={{maxWidth:900, margin:'0 auto'}}>
              <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10}}>
                <span style={{color:'#64748b', fontSize:13}}>
                  {checkedItems.length < cartData.items.length
                    ? `${checkedItems.length} of ${cartData.items.length} selected`
                    : `${cartData.items.length} item${cartData.items.length !== 1 ? 's' : ''}`}
                </span>
                <span style={{fontWeight:700, fontSize:17}}>
                  {t('total')}: <span style={{color:'#f97316'}}>{formatQAR(subtotal)}</span>
                </span>
              </div>
              <button
                onClick={() => {
                  localStorage.setItem('selectedCartItems', JSON.stringify(selectedItems))
                  navigate('/checkout')
                }}
                disabled={checkedItems.length === 0}
                style={{...styles.submitBtn, width:'100%', margin:0, opacity: checkedItems.length === 0 ? 0.5 : 1}}
              >
                {t('proceedToCheckout')} ({checkedItems.length} item{checkedItems.length !== 1 ? 's' : ''})
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

function Checkout({ t = (k) => k, language = 'EN' }) {
  const ar = language === 'AR'
  const [form, setForm] = useState({
    name: '', zone: '', street: '', building: '', unit: '',
    city: 'Doha', country: 'Qatar', phone: '',
    altPhone: '', preferredTime: 'any', receiverName: '', deliveryNotes: ''
  })
  const [addressConfirmed, setAddressConfirmed] = useState(false)
  const [editingAddress, setEditingAddress] = useState(true)
  const [paymentMethod, setPaymentMethod] = useState('cod')
  const [cartData, setCartData] = useState({ items: [], total: 0 })
  const [selectedItems, setSelectedItems] = useState([])
  const [loadingCart, setLoadingCart] = useState(true)
  const [placing, setPlacing] = useState(false)
  const [error, setError] = useState('')
  const navigate = useNavigate()

  useEffect(() => {
    cart.get().then(r => {
      setCartData(r.data)
      setSelectedItems((r.data.items || []).map(i => i.id))
      setLoadingCart(false)
    })
    // Pre-fill address from last order if user has one
    const saved = localStorage.getItem('jaspr_last_address')
    if (saved) {
      try {
        const parsed = JSON.parse(saved)
        setForm(f => ({ ...f, ...parsed }))
        setEditingAddress(false)
        setAddressConfirmed(true)
      } catch {}
    }
  }, [])

  const checkedItems = cartData.items.filter(i => selectedItems.includes(i.id))
  const subtotal = checkedItems.reduce((s, i) => s + (i.product.price * i.quantity), 0)
  const deliveryFee = subtotal >= 1000 ? 0 : 15

  const [couponCode, setCouponCode] = useState('')
  const [couponDiscount, setCouponDiscount] = useState(0)
  const [couponMsg, setCouponMsg] = useState('')
  const [couponLoading, setCouponLoading] = useState(false)

  const applyCoupon = async () => {
    if (!couponCode.trim()) return
    setCouponLoading(true); setCouponMsg('')
    try {
      const res = await fetch(`${API_ORIGIN}/api/coupons/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ code: couponCode.trim(), subtotal })
      })
      const data = await res.json()
      if (res.ok && data.discount) {
        setCouponDiscount(data.discount)
        setCouponMsg(`✅ Coupon applied! You save QAR ${data.discount.toFixed(2)}`)
      } else {
        setCouponDiscount(0)
        setCouponMsg(data.error || 'Invalid or expired coupon code.')
      }
    } catch {
      setCouponDiscount(0)
      setCouponMsg('Coupon service unavailable. Please try again.')
    }
    setCouponLoading(false)
  }

  const grandTotal = subtotal + deliveryFee - couponDiscount

  const toggleItem = (id) => {
    setSelectedItems(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])
  }

  const handlePlaceOrder = async () => {
    if (COMING_SOON_MODE) {
      setError(ar
        ? 'الطلبات غير متاحة حالياً. سنقوم بتفعيل الشراء قريباً جداً.'
        : 'Ordering isn\'t open yet — we\'re putting the final touches on checkout. Check back very soon!')
      return
    }
    if (!form.name || !form.zone || !form.street || !form.building || !form.phone) {
      setError(ar ? 'يرجى تعبئة الاسم والمنطقة والشارع والمبنى ورقم الهاتف' : 'Please fill: Full Name, Zone No., Street No., Building No. and Phone')
      return
    }
    if (checkedItems.length === 0) {
      setError('Please select at least one item to order.')
      return
    }
    setPlacing(true); setError('')
    localStorage.setItem('jaspr_last_address', JSON.stringify(form))
    try {
      const res = await orders.place({
        shippingAddress: form,
        paymentMethod,
        itemIds: selectedItems,
        couponCode: couponCode.trim() || undefined,
      })
      const orderId = res.data?.order?.id || res.data?.id
      if (!orderId) {
        setError('Order created but ID missing. Please check My Orders.')
        setPlacing(false)
        return
      }

      // If only SOME items were selected, remove only the ordered ones from cart
      // so the unselected ones remain for later
      const hasUnselected = cartData.items.some(i => !selectedItems.includes(i.id))
      if (hasUnselected) {
        for (const item of checkedItems) {
          try { await cart.remove(item.product.id) } catch {}
        }
      }
      // If ALL items selected, backend order clears cart normally

      if (paymentMethod === 'card') {
        try {
          const pay = await payments.orderPayment({ orderId })
          // Handle all MyFatoorah response field variations
          const url = pay.data?.paymentUrl
            || pay.data?.PaymentURL
            || pay.data?.invoiceURL
            || pay.data?.InvoiceURL
            || pay.data?.data?.PaymentURL
            || pay.data?.Data?.PaymentURL
          console.log('Payment gateway response:', JSON.stringify(pay.data))
          if (url) { await Browser.open({ url }); return }
          console.error('No payment URL found in response:', pay.data)
          setError('Payment gateway did not return a URL. Your order is saved — try paying from My Orders.')
          setPlacing(false); return
        } catch (payErr) {
          console.error('Payment gateway error:', payErr?.response?.data || payErr)
          console.log('Full payment error:', JSON.stringify(payErr?.response?.data))
const payMsg = payErr?.response?.data?.error || payErr?.response?.data?.message || 'Gateway error'
          setError(`Card payment failed: ${payMsg}. Your order is saved — try from My Orders or use Cash on Delivery.`)
          setPlacing(false); return
        }
      }

      navigate(`/orders/${orderId}`)
    } catch (err) {
      console.error('Checkout error:', err?.response?.data || err?.message || err)
      const msg = err?.response?.data?.error || err?.response?.data?.message || err?.message || t('orderFailed')
      setError(`Order failed: ${msg}`)
      setPlacing(false)
    }
  }

  const getProductImage = (item) => {
    const img = item.product?.images?.[0]?.url || item.product?.image || item.product?.imageUrl || ''
    return img || null
  }

  return (
    <div style={{...styles.page, maxWidth:900}}>
      <h2 style={{marginBottom:8}}>{t('checkout')}</h2>
      {COMING_SOON_MODE && (
        <div style={{background:'#fff7ed', border:'1px solid #fdba74', borderRadius:10, padding:'14px 18px', marginBottom:20, display:'flex', alignItems:'center', gap:10}}>
          <span style={{fontSize:20}}>🚧</span>
          <span style={{fontSize:14, color:'#9a3412', fontWeight:600}}>
            {ar
              ? 'الشراء غير متاح حالياً — نحن في مرحلة الإطلاق التجريبي. تصفح المتجر بحرية، وسيتم تفعيل الطلبات قريباً جداً.'
              : "Checkout isn't open yet — we're in soft launch. Feel free to browse the shop; ordering unlocks very soon."}
          </span>
        </div>
      )}
      {error && <p style={styles.error}>{error}</p>}
      <div style={{display:'flex', gap:28, flexWrap:'wrap'}}>

        {/* LEFT — address + payment */}
        <div style={{flex:1, minWidth:280}}>

          {/* Address section */}
          <div style={{background:'#f8f9fa', borderRadius:12, padding:18, marginBottom:20}}>
            <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom: editingAddress ? 16 : 0}}>
              <h3 style={{fontSize:17, margin:0}}>{ar ? 'عنوان التوصيل' : 'Delivery Address'}</h3>
              {addressConfirmed && !editingAddress && (
                <button onClick={() => setEditingAddress(true)} style={{background:'none', border:'1px solid #f97316', color:'#f97316', borderRadius:6, padding:'4px 12px', cursor:'pointer', fontSize:13, fontWeight:600}}>
                  Edit
                </button>
              )}
            </div>

            {/* Confirmed address summary */}
            {addressConfirmed && !editingAddress ? (
              <div style={{fontSize:14, color:'#334155', lineHeight:1.8}}>
                <strong>{form.name}</strong><br/>
                Zone {form.zone}, Street {form.street}, Bldg {form.building}{form.unit ? `, Unit ${form.unit}` : ''}<br/>
                {form.city}, {form.country}<br/>
                📞 {form.phone}
                {form.deliveryNotes && <><br/><span style={{color:'#64748b'}}>📝 {form.deliveryNotes}</span></>}
              </div>
            ) : (
              <>
                <input style={styles.input} placeholder={ar ? 'الاسم الكامل' : 'Full Name'} value={form.name} onChange={e => setForm({...form, name: e.target.value})} />
                <div style={{display:'flex', gap:12}}>
                  <input style={{...styles.input, flex:1}} placeholder={ar ? 'رقم المنطقة' : 'Zone No.'} value={form.zone} onChange={e => setForm({...form, zone: e.target.value})} />
                  <input style={{...styles.input, flex:1}} placeholder={ar ? 'رقم الشارع' : 'Street No.'} value={form.street} onChange={e => setForm({...form, street: e.target.value})} />
                </div>
                <div style={{display:'flex', gap:12}}>
                  <input style={{...styles.input, flex:1}} placeholder={ar ? 'رقم المبنى' : 'Building No.'} value={form.building} onChange={e => setForm({...form, building: e.target.value})} />
                  <input style={{...styles.input, flex:1}} placeholder={ar ? 'الوحدة / الشقة (اختياري)' : 'Unit / Apt (optional)'} value={form.unit} onChange={e => setForm({...form, unit: e.target.value})} />
                </div>
                <div style={{display:'flex', gap:12}}>
                  <input style={{...styles.input, flex:1}} placeholder={ar ? 'المدينة' : 'City'} value={form.city} onChange={e => setForm({...form, city: e.target.value})} />
                  <input style={{...styles.input, flex:1}} placeholder={ar ? 'الدولة' : 'Country'} value={form.country} onChange={e => setForm({...form, country: e.target.value})} />
                </div>
                <input style={styles.input} placeholder={ar ? 'رقم الهاتف' : 'Phone Number'} value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} />
                <select style={styles.input} value={form.preferredTime} onChange={e => setForm({...form, preferredTime: e.target.value})}>
                  <option value="any">{ar ? 'أي وقت' : 'Any time'}</option>
                  <option value="morning">{ar ? 'صباحاً (٨ص - ١٢م)' : 'Morning (8am - 12pm)'}</option>
                  <option value="afternoon">{ar ? 'ظهراً (١٢م - ٤م)' : 'Afternoon (12pm - 4pm)'}</option>
                  <option value="evening">{ar ? 'مساءً (٤م - ٩م)' : 'Evening (4pm - 9pm)'}</option>
                </select>
                <input style={styles.input} placeholder={ar ? 'رقم بديل' : 'Alternative phone (optional)'} value={form.altPhone} onChange={e => setForm({...form, altPhone: e.target.value})} />
                <input style={styles.input} placeholder={ar ? 'اسم المستلم البديل' : 'Receiver name if different'} value={form.receiverName} onChange={e => setForm({...form, receiverName: e.target.value})} />
                <textarea style={{...styles.input, minHeight:60, resize:'vertical', fontFamily:'inherit'}} placeholder={ar ? 'ملاحظات التوصيل' : 'Delivery notes (optional)'} value={form.deliveryNotes} onChange={e => setForm({...form, deliveryNotes: e.target.value})} />
                <button onClick={() => { setAddressConfirmed(true); setEditingAddress(false) }} style={{...styles.submitBtn, marginBottom:0, background:'#0f1923'}}>
                  ✓ {ar ? 'تأكيد العنوان' : 'Confirm Address'}
                </button>
              </>
            )}
          </div>

          {/* Payment method */}
          <h3 style={{marginBottom:12, fontSize:17}}>{t('paymentMethod')}</h3>
          <div onClick={() => setPaymentMethod('cod')} style={{padding:'14px 16px', border: paymentMethod === 'cod' ? '2px solid #f97316' : '2px solid #eee', borderRadius:8, marginBottom:12, background: paymentMethod === 'cod' ? '#fff7ed' : '#fff', cursor:'pointer'}}>
            <label style={{display:'flex', alignItems:'center', gap:10, cursor:'pointer'}}>
              <input type="radio" checked={paymentMethod === 'cod'} onChange={() => setPaymentMethod('cod')} />
              <div>
                <div style={{fontWeight:600}}>💵 {t('cashOnDelivery')}</div>
                <div style={{fontSize:12, color:'#64748b'}}>Pay when your order arrives</div>
              </div>
            </label>
          </div>
          <div onClick={() => setPaymentMethod('card')} style={{border: paymentMethod === 'card' ? '2px solid #f97316' : '2px solid #eee', borderRadius:8, marginBottom:24, background: paymentMethod === 'card' ? '#fff7ed' : '#fff', cursor:'pointer', overflow:'hidden'}}>
            <div style={{padding:'14px 16px'}}>
              <label style={{display:'flex', alignItems:'center', gap:10, cursor:'pointer'}}>
                <input type="radio" checked={paymentMethod === 'card'} onChange={() => setPaymentMethod('card')} />
                <div>
                  <div style={{fontWeight:600}}>💳 {t('creditDebitCard')}</div>
                  <div style={{fontSize:12, color:'#64748b'}}>Visa, Mastercard — secured by MyFatoorah</div>
                </div>
              </label>
            </div>
            {paymentMethod === 'card' && (
              <div style={{padding:'0 16px 14px', borderTop:'1px solid #f0e0d0', fontSize:13, color:'#64748b'}}>
                🔒 You'll be redirected to a secure payment page. We never store card details.
              </div>
            )}
          </div>

          <button onClick={handlePlaceOrder} disabled={COMING_SOON_MODE || placing || checkedItems.length === 0} style={{...styles.submitBtn, opacity: (COMING_SOON_MODE || placing || checkedItems.length === 0) ? 0.6 : 1, cursor: COMING_SOON_MODE ? 'not-allowed' : 'pointer'}}>
            {COMING_SOON_MODE
              ? (ar ? '🚧 قريباً' : '🚧 Coming Soon')
              : (placing ? t('placingOrder') : `${t('placeOrder')} (${checkedItems.length} item${checkedItems.length !== 1 ? 's' : ''})`)}
          </button>
        </div>

        {/* RIGHT — order summary with checkboxes and thumbnails */}
        <div style={{width: window.innerWidth <= 768 ? '100%' : 300}}>
          <h3 style={{marginBottom:12, fontSize:17}}>{t('orderSummary')}</h3>
          <div style={{background:'#f8f9fa', borderRadius:12, padding:16, color:'#0f1923'}}>
            {loadingCart ? <p>{t('loading')}</p> : cartData.items.map(item => {
              const checked = selectedItems.includes(item.id)
              const img = getProductImage(item)
              return (
                <div
                  key={item.id}
                  onClick={() => toggleItem(item.id)}
                  style={{
                    display:'flex', alignItems:'center', gap:10, marginBottom:12,
                    padding:'8px 10px', borderRadius:10, cursor:'pointer',
                    background: checked ? '#fff' : '#f0f0f0',
                    border: checked ? '1.5px solid #f97316' : '1.5px solid #e2e8f0',
                    opacity: checked ? 1 : 0.55,
                    transition:'all 0.15s'
                  }}
                >
                  {/* Checkbox */}
                  <div style={{
                    width:20, height:20, borderRadius:4, border:'2px solid',
                    borderColor: checked ? '#f97316' : '#cbd5e1',
                    background: checked ? '#f97316' : '#fff',
                    display:'flex', alignItems:'center', justifyContent:'center',
                    flexShrink:0
                  }}>
                    {checked && <span style={{color:'#fff', fontSize:12, lineHeight:1}}>✓</span>}
                  </div>
                  {/* Thumbnail */}
                  {img ? (
                    <img src={img} alt={item.product.name} style={{width:44, height:44, objectFit:'cover', borderRadius:6, flexShrink:0, background:'#eee'}} />
                  ) : (
                    <div style={{width:44, height:44, borderRadius:6, background:'#e2e8f0', flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center', fontSize:18}}>📦</div>
                  )}
                  {/* Name + price */}
                  <div style={{flex:1, minWidth:0}}>
                    <div style={{fontSize:12, fontWeight:600, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis'}}>{item.product.name}</div>
                    <div style={{fontSize:11, color:'#64748b'}}>x{item.quantity}</div>
                  </div>
                  <div style={{fontSize:13, fontWeight:700, color:'#f97316', flexShrink:0}}>{formatQAR(item.product.price * item.quantity)}</div>
                </div>
              )
            })}

            <div style={{borderTop:'1px solid #ddd', paddingTop:14, marginTop:8}}>

              {/* Coupon code */}
              <div style={{marginBottom:14}}>
                <div style={{fontSize:13, fontWeight:600, marginBottom:8, color:'#0f1923'}}>🏷️ Coupon Code</div>
                <div style={{display:'flex', gap:8}}>
                  <input
                    style={{flex:1, padding:'9px 12px', borderRadius:8, border:'1.5px solid #e2e8f0', fontSize:13, outline:'none', fontFamily:'inherit'}}
                    placeholder="Enter coupon code"
                    value={couponCode}
                    onChange={e => { setCouponCode(e.target.value.toUpperCase()); setCouponDiscount(0); setCouponMsg('') }}
                  />
                  <button
                    onClick={applyCoupon}
                    disabled={couponLoading || !couponCode.trim()}
                    style={{padding:'9px 14px', borderRadius:8, border:'none', background:'#f97316', color:'#fff', fontWeight:700, fontSize:13, cursor:'pointer', opacity: (!couponCode.trim() || couponLoading) ? 0.6 : 1}}
                  >
                    {couponLoading ? '…' : 'Apply'}
                  </button>
                </div>
                {couponMsg && (
                  <div style={{fontSize:12, marginTop:6, color: couponDiscount > 0 ? '#16a34a' : '#dc2626'}}>{couponMsg}</div>
                )}
              </div>

              <div style={{display:'flex', justifyContent:'space-between', marginBottom:8, fontSize:14}}>
                <span>Subtotal ({checkedItems.length} items)</span>
                <strong>{formatQAR(subtotal)}</strong>
              </div>
              <div style={{display:'flex', justifyContent:'space-between', marginBottom:8, fontSize:14}}>
                <span>Delivery</span>
                <strong style={{color: deliveryFee === 0 ? '#16a34a' : 'inherit'}}>{deliveryFee === 0 ? 'Free 🎉' : formatQAR(deliveryFee)}</strong>
              </div>
              {deliveryFee > 0 && <p style={{fontSize:11, color:'#94a3b8', marginTop:-4, marginBottom:8}}>Free delivery on orders above QAR 1,000</p>}
              {couponDiscount > 0 && (
                <div style={{display:'flex', justifyContent:'space-between', marginBottom:8, fontSize:14, color:'#16a34a'}}>
                  <span>Coupon Discount</span>
                  <strong>- {formatQAR(couponDiscount)}</strong>
                </div>
              )}
              <div style={{display:'flex', justifyContent:'space-between', paddingTop:12, borderTop:'1px solid #eee', fontSize:17, fontWeight:700, color:'#f97316'}}>
                <span>Total</span>
                <span>{formatQAR(grandTotal)}</span>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  )
}

function OrderDetail({ t = (k) => k }) {
  const { id } = useParams()
  const [order, setOrder] = useState(null)
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()

  useEffect(() => {
    orders.getOne(id).then(r => { setOrder(r.data); setLoading(false) }).catch(() => setLoading(false))
  }, [id])

  if (loading) return <p style={{padding:40}}>{t('loading')}</p>
  if (!order) return <p style={{padding:40}}>{t('orderNotFound')}</p>

  const statusColor = { PENDING:'#f97316', CONFIRMED:'#3b82f6', SHIPPED:'#8b5cf6', DELIVERED:'#10b981', CANCELLED:'#ef4444' }

  return (
    <div style={{...styles.page, maxWidth:700}}>
      <div style={{textAlign:'center', padding:'40px 0 32px'}}>
        <div style={{fontSize:64, marginBottom:16}}>🎉</div>
        <h2 style={{fontSize:28, marginBottom:8}}>{t('orderSuccess')}</h2>
        <p style={{color:'#666'}}>{t('orderId')}: <span style={{fontFamily:'monospace', background:'#f8f9fa', padding:'2px 8px', borderRadius:4}}>{order.id?.slice(0,8)}...</span></p>
      </div>
      <div style={{background:'#f8f9fa', borderRadius:12, padding:24, marginBottom:24, color:'#0f1923'}}>
        <div style={{display:'flex', justifyContent:'space-between', marginBottom:16}}><span style={{color:'#666'}}>{t('status')}</span><span style={{fontWeight:700, color: statusColor[order.status]}}>{order.status}</span></div>
        <div style={{display:'flex', justifyContent:'space-between', marginBottom:16}}><span style={{color:'#666'}}>{t('total')}</span><span style={{fontWeight:700, color:'#f97316', fontSize:18}}>{formatQAR(order.totalAmount)}</span></div>
        {/* Payment method + paid status */}
        <div style={{display:'flex', justifyContent:'space-between', marginBottom:16}}>
          <span style={{color:'#666'}}>Payment</span>
          <span style={{fontWeight:700}}>
            {order.shippingAddress?.paymentMethod === 'card'
              ? (order.status === 'PENDING' && !order.paymentRef
                  ? <span style={{color:'#f97316'}}>💳 Card · Awaiting Payment</span>
                  : <span style={{color:'#16a34a'}}>💳 Card · Paid</span>)
              : <span style={{color:'#92400e'}}>💵 Cash on Delivery</span>
            }
          </span>
        </div>
        {/* Vendor(s) */}
        {order.orderItems && order.orderItems.length > 0 && (
          <div style={{display:'flex', justifyContent:'space-between', marginBottom:16}}>
            <span style={{color:'#666'}}>Sold by</span>
            <span style={{fontWeight:600, textAlign:'right'}}>
              {[...new Set(order.orderItems.map(it => it.vendor?.storeName).filter(Boolean))].join(', ') || '—'}
            </span>
          </div>
        )}
        <div style={{borderTop:'1px solid #e5e7eb', paddingTop:16}}>
          <div style={{color:'#666', marginBottom:6}}>{t('deliveryTo')}</div>
          <div style={{fontWeight:500, lineHeight:1.6}}>
            {order.shippingAddress?.name && <div>{order.shippingAddress.name}</div>}
            <div>
              {[
                order.shippingAddress?.zone && `Zone ${order.shippingAddress.zone}`,
                order.shippingAddress?.street && `Street ${order.shippingAddress.street}`,
                order.shippingAddress?.building && `Building ${order.shippingAddress.building}`,
                order.shippingAddress?.unit && `Unit ${order.shippingAddress.unit}`
              ].filter(Boolean).join(', ')}
            </div>
            <div>{[order.shippingAddress?.city, order.shippingAddress?.country].filter(Boolean).join(', ')}</div>
            {order.shippingAddress?.phone && <div>📞 {order.shippingAddress.phone}</div>}
          </div>
          {(order.shippingAddress?.altPhone || order.shippingAddress?.receiverName ||
            (order.shippingAddress?.preferredTime && order.shippingAddress?.preferredTime !== 'any') ||
            order.shippingAddress?.deliveryNotes) && (
            <div style={{marginTop:12, padding:12, background:'#fff7ed', borderRadius:8, fontSize:13, lineHeight:1.7}}>
              <div style={{fontWeight:600, marginBottom:4}}>📦 Delivery instructions</div>
              {order.shippingAddress?.preferredTime && order.shippingAddress.preferredTime !== 'any' &&
                <div>Preferred time: {order.shippingAddress.preferredTime}</div>}
              {order.shippingAddress?.altPhone && <div>Alt. contact: {order.shippingAddress.altPhone}</div>}
              {order.shippingAddress?.receiverName && <div>Receiver: {order.shippingAddress.receiverName}</div>}
              {order.shippingAddress?.deliveryNotes && <div>Notes: {order.shippingAddress.deliveryNotes}</div>}
            </div>
          )}
        </div>
      </div>
      <h3 style={{marginBottom:16}}>{t('itemsOrdered')}</h3>
      {order.orderItems?.map(item => (
        <div key={item.id} style={styles.cartItem}>
          <div style={{fontSize:28}}>📱</div>
          <div style={{flex:1, marginLeft:16}}>
            <p style={{fontWeight:500}}>{item.product?.name}</p>
            <p style={{color:'#666', fontSize:14}}>{t('qty')}: {item.quantity}</p>
          </div>
          <p style={{color:'#f97316', fontWeight:600}}>{formatQAR(item.unitPrice * item.quantity)}</p>
        </div>
      ))}
     <div style={{display:'flex', gap:12, marginTop:32, flexDirection:'column'}}>
        {(order.status === 'PENDING' && order.shippingAddress?.paymentMethod === 'card' && !order.paymentRef) && (
          <>
            <button onClick={async () => {
              try {
                const pay = await payments.orderPayment({ orderId: order.id })
                const url = pay.data?.paymentUrl || pay.data?.PaymentURL || pay.data?.invoiceURL || pay.data?.InvoiceURL
                if (url) { await Browser.open({ url }); return }
                alert('Could not get payment URL. Please try again.')
              } catch (e) {
                alert('Payment error: ' + (e?.response?.data?.error || e.message || 'Please try again.'))
              }
            }} style={{...styles.submitBtn, background:'#16a34a', fontSize:16, padding:'14px'}}>
              💳 Pay Now
            </button>
            <button onClick={async () => {
              try {
                // Re-fetch order to check if payment completed
                const updated = await orders.getOne(order.id)
                if (updated.data?.status === 'CONFIRMED') {
                  window.location.reload()
                } else {
                  alert('Payment not confirmed yet. If you completed payment, please wait a moment and try again.')
                }
              } catch (e) {
                alert('Could not check status. Please try again.')
              }
            }} style={{...styles.submitBtn, background:'#1e3a5f', fontSize:14, padding:'12px'}}>
              🔄 Check Payment Status
            </button>
          </>
        )}
        <div style={{display:'flex', gap:12}}>
          <button onClick={() => navigate('/orders')} style={{...styles.submitBtn, flex:1}}>{t('myOrders')}</button>
          <button onClick={() => navigate('/products')} style={{...styles.submitBtn, flex:1, background:'#1e3a5f'}}>{t('continueShopping')}</button>
        </div>
      </div>
    </div>
    )
  }

function Orders({ user, t = (k) => k }) {
  const [orderList, setOrderList] = useState([])
  const [loading, setLoading] = useState(true)
  const [updatingId, setUpdatingId] = useState(null)
  const navigate = useNavigate()

  const loadOrders = () => {
    if (!localStorage.getItem('token')) {
      setOrderList([])
      setLoading(false)
      return
    }

    orders.getAll()
      .then(r => { setOrderList(Array.isArray(r.data) ? r.data : []); setLoading(false) })
      .catch(() => {
        setOrderList([])
        setLoading(false)
      })
  }

  useEffect(() => { loadOrders() }, [])

  const handleStatusUpdate = async (orderId, status) => {
    setUpdatingId(orderId)
    try { await orders.updateStatus(orderId, status); loadOrders() }
    catch (err) { alert(err.response?.data?.error || t('updateFailed')) }
    setUpdatingId(null)
  }

  const handleBuyAgain = async (items) => {
    if (!items || items.length === 0) return
    try {
      for (const item of items) {
        const productId = item.productId || item.product?.id
        if (productId) await cart.add({ productId, quantity: item.quantity || 1 })
      }
      navigate('/cart')
    } catch {
      alert('Could not add items to cart. Please try again.')
    }
  }

  const statusColor = { PENDING:'#f97316', CONFIRMED:'#3b82f6', SHIPPED:'#8b5cf6', DELIVERED:'#10b981', CANCELLED:'#ef4444' }
  const nextStatus = { PENDING:'CONFIRMED', CONFIRMED:'SHIPPED', SHIPPED:'DELIVERED' }

  if (loading) return <p style={{padding:40}}>{t('loading')}</p>

  return (
    <div style={styles.page}>
      <h2 style={{marginBottom:24}}>{t('myOrders')}</h2>
      {orderList.length === 0 ? (
        <div style={{textAlign:'center', padding:60}}>
          <p style={{marginBottom:20}}>{t('noOrders')}</p>
          <Link to="/products" style={styles.heroBtn}>{t('startShopping')}</Link>
        </div>
      ) : orderList.map(order => (
        <div key={order.id} style={{border:'1px solid #eee', borderRadius:12, padding:16, marginBottom:16, background:'#fff', boxShadow:'0 1px 4px rgba(0,0,0,0.06)'}}>
          {/* Order header */}
          <div style={{display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:12}}>
            <div>
              <p onClick={() => navigate(`/orders/${order.id}`)} style={{fontWeight:700, color:'#1e3a5f', cursor:'pointer', fontSize:14, margin:0}}>
                {t('order')} #{order.id?.slice(0,8)}...
              </p>
              <p style={{color:'#999', fontSize:12, marginTop:3, marginBottom:0}}>{new Date(order.createdAt).toLocaleDateString()}</p>
            </div>
            <span style={{background: statusColor[order.status] + '20', color: statusColor[order.status], padding:'4px 10px', borderRadius:20, fontSize:12, fontWeight:600, flexShrink:0}}>
              {order.status}
            </span>
          </div>

          {/* Items list */}
          {order.orderItems && order.orderItems.length > 0 && (
            <div style={{borderTop:'1px solid #f0f0f0', borderBottom:'1px solid #f0f0f0', padding:'10px 0', marginBottom:12}}>
              {order.orderItems.map((item, idx) => (
                <div key={idx} style={{display:'flex', alignItems:'center', gap:10, paddingBottom: idx < order.orderItems.length - 1 ? 8 : 0, marginBottom: idx < order.orderItems.length - 1 ? 8 : 0, borderBottom: idx < order.orderItems.length - 1 ? '1px dashed #f0f0f0' : 'none'}}>
                  {item.product?.images?.[0] ? (
                    <img src={item.product.images[0]} alt={item.product?.name}
                      style={{width:42, height:42, objectFit:'cover', borderRadius:8, border:'1px solid #eee', flexShrink:0}}
                      onError={e => { e.target.style.display='none' }}
                    />
                  ) : (
                    <div style={{width:42, height:42, borderRadius:8, background:'#f3f4f6', display:'flex', alignItems:'center', justifyContent:'center', fontSize:20, flexShrink:0}}>📱</div>
                  )}
                  <div style={{flex:1, minWidth:0}}>
                    <p style={{fontWeight:500, fontSize:13, margin:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>{item.product?.name || 'Product'}</p>
                    <p style={{color:'#888', fontSize:12, margin:0}}>Qty: {item.quantity} × {formatQAR(item.unitPrice)}</p>
                  </div>
                  <p style={{color:'#f97316', fontWeight:600, fontSize:13, flexShrink:0, margin:0}}>{formatQAR(item.unitPrice * item.quantity)}</p>
                </div>
              ))}
            </div>
          )}

          {/* Footer: total + action buttons */}
          <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:8}}>
            <p style={{color:'#f97316', fontWeight:700, fontSize:16, margin:0}}>{formatQAR(order.totalAmount)}</p>
            <div style={{display:'flex', gap:8, flexWrap:'wrap'}}>
              {user?.role === 'VENDOR' && nextStatus[order.status] && (
                <button onClick={() => handleStatusUpdate(order.id, nextStatus[order.status])} disabled={updatingId === order.id}
                  style={{padding:'6px 12px', background:'#1e3a5f', color:'#fff', border:'none', borderRadius:8, cursor:'pointer', fontSize:12}}>
                  {updatingId === order.id ? '...' : `Mark ${nextStatus[order.status]}`}
                </button>
              )}
              {order.orderItems && order.orderItems.length > 0 && (
                <button onClick={() => handleBuyAgain(order.orderItems)}
                  style={{padding:'6px 14px', background:'#f97316', color:'#fff', border:'none', borderRadius:8, cursor:'pointer', fontSize:12, fontWeight:700}}>
                  🛒 Buy Again
                </button>
              )}
              <button onClick={() => navigate(`/orders/${order.id}`)}
                style={{padding:'6px 12px', background:'#f8f9fa', color:'#1e3a5f', border:'1px solid #e2e8f0', borderRadius:8, cursor:'pointer', fontSize:12, fontWeight:600}}>
                View Details
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}

function ProductDetail({ user, t = (k) => k, language = 'EN' }) {
  const { id } = useParams()
  const navigate = useNavigate()
  const [product, setProduct] = useState(null)
  const [loading, setLoading] = useState(true)
  const [added, setAdded] = useState(false)
  const [qty, setQty] = useState(1)
  const [showCartModal, setShowCartModal] = useState(false)
  const [selectedImage, setSelectedImage] = useState(0)
  const [imageFade, setImageFade] = useState(true)
  const [reviews, setReviews] = useState([])
  const [reviewForm, setReviewForm] = useState({ rating: 5, comment: '' })
  const [submitting, setSubmitting] = useState(false)
  const [reviewError, setReviewError] = useState('')
  const [reviewSuccess, setReviewSuccess] = useState('')

  const loadReviews = () => {
    products.getReviews(id).then(r => setReviews(r.data)).catch(() => {})
  }

  useEffect(() => {
    products.getOne(id).then(r => { setProduct(r.data); setLoading(false) })
    loadReviews()
    // Track recently viewed (cap 8, dedupe, newest first)
    try {
      const prev = JSON.parse(localStorage.getItem('recentlyViewed') || '[]')
      const next = [id, ...prev.filter(x => x !== id)].slice(0, 8)
      localStorage.setItem('recentlyViewed', JSON.stringify(next))
    } catch (e) { /* localStorage unavailable, ignore */ }
  }, [id]) // eslint-disable-line

  const handleAddToCart = async () => {
    if (added) return
    try {
      await cart.add({ productId: id, quantity: qty })
      setAdded(true)
      setShowCartModal(true)
      setTimeout(() => setAdded(false), 3000)
    } catch { alert(t('loginToAdd')) }
  }

  const handleSubmitReview = async () => {
    if (!reviewForm.comment.trim()) { setReviewError(t('writeComment')); return }
    setSubmitting(true); setReviewError(''); setReviewSuccess('')
    try {
      await products.addReview(id, reviewForm)
      setReviewSuccess(t('reviewSubmitted'))
      setReviewForm({ rating: 5, comment: '' })
      loadReviews()
      products.getOne(id).then(r => setProduct(r.data))
    } catch (err) { setReviewError(err.response?.data?.error || t('reviewFailed')) }
    setSubmitting(false)
  }

  const handleDeleteReview = async () => {
    if (!window.confirm(t('deleteReviewConfirm'))) return
    try {
      await products.deleteReview(id)
      loadReviews()
      products.getOne(id).then(r => setProduct(r.data))
    } catch (err) { setReviewError(err.response?.data?.error || t('deleteFailed')) }
  }

  const renderStars = (rating, interactive = false, onRate = null) => (
    <div style={{display:'flex', gap:4}}>
      {[1,2,3,4,5].map(star => (
        <span key={star} onClick={() => interactive && onRate && onRate(star)}
          style={{fontSize: interactive ? 28 : 16, cursor: interactive ? 'pointer' : 'default', color: star <= rating ? '#f97316' : '#ddd'}}>
          &#9733;
        </span>
      ))}
    </div>
  )

  if (loading) return <p style={{padding:40}}>{t('loading')}</p>
  if (!product) return <p style={{padding:40}}>{t('productNotFound')}</p>

  const images = (product.images || []).filter(Boolean)
  const getImgSrc = (img) => getImageSrc(img)
  const userReview = reviews.find(r => r.userId === user?.id)
  const cond = conditionLabel(product.condition, language)
  const avgRating = Math.round(product.avgRating || 0)
  const ratingCounts = [5,4,3,2,1].map(star => reviews.filter(r => r.rating === star).length)

  const cartModal = showCartModal && (
    <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.5)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center'}}>
      <div style={{background:'#fff',borderRadius:24,padding:40,maxWidth:420,width:'90%',textAlign:'center'}}>
        <div style={{fontSize:48,marginBottom:16}}>✅</div>
        <h3 style={{
  marginBottom:8,
  fontSize:24,
  fontWeight:800,
  color:'#0f1923'
}}>
  ✅ Added to Cart
</h3>
        <p style={{
  color:'#555',
  marginBottom:28,
  lineHeight:1.6,
  fontSize:15
}}>
  Your item has been successfully added to cart.
</p>
        <div style={{display:'flex',gap:12}}>
          <button onClick={()=>{setShowCartModal(false); navigate('/products')}} style={{flex:1,padding:'12px',border:'2px solid #f97316',borderRadius:8,background:'#fff',color:'#f97316',fontWeight:600,cursor:'pointer'}}>{t('continueShopping')}</button>
          <button onClick={()=>{setShowCartModal(false); navigate('/cart')}} style={{flex:1,padding:'12px',border:'none',borderRadius:8,background:'#f97316',color:'#fff',fontWeight:600,cursor:'pointer'}}>{t('goToCheckout')}</button>
        </div>
      </div>
    </div>
  )

  return (
    <div style={{...styles.page, maxWidth:1000, position:'relative'}}>
      {cartModal}

      {/* Back / close button — goes back to shopping */}
      <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16, gap:12}}>
        <button
          onClick={() => navigate('/products')}
          style={{
            display:'inline-flex', alignItems:'center', gap:6,
            padding:'8px 14px', borderRadius:10,
            background:'#fff', border:'1px solid #e2e8f0',
            color:'#0f1923', fontWeight:600, fontSize:14, cursor:'pointer'
          }}
          aria-label={language === 'AR' ? 'رجوع' : 'Back'}
        >
          <span style={{fontSize:18, lineHeight:1}}>←</span>
          <span>{language === 'AR' ? 'العودة للتسوق' : 'Back to Shopping'}</span>
        </button>
        <button
          onClick={() => navigate('/products')}
          style={{
            width:38, height:38, borderRadius:'50%',
            background:'#fff', border:'1px solid #e2e8f0',
            fontSize:22, lineHeight:1, color:'#0f1923', cursor:'pointer',
            display:'flex', alignItems:'center', justifyContent:'center',
            boxShadow:'0 2px 6px rgba(0,0,0,0.06)'
          }}
          aria-label={language === 'AR' ? 'إغلاق' : 'Close'}
        >×</button>
      </div>

      {/* Main product grid */}
      <div style={{display:'grid', gridTemplateColumns: window.innerWidth <= 768 ? '1fr' : '1fr 1fr', gap: window.innerWidth <= 768 ? 20 : 40, marginBottom:40}}>

        {/* Left — Images */}
        <div>
          <div
            style={{
              width:'100%',
              minHeight: window.innerWidth <= 768 ? 260 : 430,
              maxHeight: window.innerWidth <= 768 ? 300 : 500,
              background:'#f8f9fa',
              borderRadius:16,
              border:'1px solid #eee',
              display:'flex',
              flexDirection:'column',
              alignItems:'center',
              justifyContent:'center',
              overflow:'hidden',
              position:'relative',
              marginBottom:14,
              padding:20
            }}
          >
            {/* arrows here */}
            {images.length > 1 && (
  <>
    <button
      type="button"
      onMouseEnter={(e) => e.stopPropagation()}
      onMouseMove={(e) => e.stopPropagation()}
      onClick={() => {
  setImageFade(false)
  setTimeout(() => {
    setSelectedImage((selectedImage - 1 + images.length) % images.length)
    setImageFade(true)
  }, 120)
}}
      style={{
        position:'absolute',
        left:14,
        top:'50%',
        transform:'translateY(-50%)',
        width:42,
        height:42,
        borderRadius:'50%',
        border:'none',
        background:'rgba(15,25,35,0.75)',
        color:'#fff',
        fontSize:24,
        cursor:'pointer',
        pointerEvents:'auto',
        zIndex:5
      }}
    >
      ‹
    </button>

    <button
      type="button"
      onMouseEnter={(e) => e.stopPropagation()}
      onMouseMove={(e) => e.stopPropagation()}
      onClick={() => {
  setImageFade(false)
  setTimeout(() => {
    setSelectedImage((selectedImage + 1) % images.length)
    setImageFade(true)
  }, 120)
}}
      style={{
        position:'absolute',
        right:14,
        top:'50%',
        transform:'translateY(-50%)',
        width:42,
        height:42,
        borderRadius:'50%',
        border:'none',
        background:'rgba(15,25,35,0.75)',
        color:'#fff',
        fontSize:24,
        cursor:'pointer',
        zIndex:5
      }}
    >
      ›
    </button>
  </>
)}
            {getImgSrc(images[selectedImage]) ? (
              <img
  src={getImgSrc(images[selectedImage])}
  alt={product.name}

  onMouseMove={(e) => {
    const rect = e.currentTarget.getBoundingClientRect()

    const x = ((e.clientX - rect.left) / rect.width) * 100
    const y = ((e.clientY - rect.top) / rect.height) * 100

    e.currentTarget.style.transformOrigin = `${x}% ${y}%`
  }}

  onMouseEnter={(e) => {
    e.currentTarget.style.transform = 'scale(1.6)'
  }}

  onMouseLeave={(e) => {
    e.currentTarget.style.transform = 'scale(1)'
    e.currentTarget.style.transformOrigin = 'center center'
  }}

  style={{
    width:'100%',
    height:'100%',
    maxHeight: window.innerWidth <= 768 ? 260 : 430,
    objectFit:'contain',
    display:'block',
    transition:'transform 0.3s ease, opacity 0.25s ease',
    opacity:imageFade ? 1 : 0.35,
    cursor:'zoom-in'
  }}
/>
            ) : (
              <div style={{fontSize:64}}>📱</div>
            )}

            {images.length > 1 && (
              <div
                style={{
                  display:'flex',
                  gap:10,
                  flexWrap:'wrap',
                  marginTop:18,
                  justifyContent:'center',
                  width:'100%'
                }}
              >
                {images.map((img, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setSelectedImage(i)}
                    style={{
                      width:72,
                      height:72,
                      borderRadius:12,
                      overflow:'hidden',
                      cursor:'pointer',
                      border:selectedImage === i ? '2px solid #f97316' : '1px solid #ddd',
                      background:'#fff',
                      padding:4,
                      boxShadow:selectedImage === i ? '0 6px 16px rgba(249,115,22,0.25)' : 'none'
                    }}
                  >
                    <img
                      src={getImgSrc(img)}
                      alt={`${product.name} ${i + 1}`}
                      style={{
                        width:'100%',
                        height:'100%',
                        objectFit:'contain',
                        display:'block'
                      }}
                    />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right — Details */}
        <div>
          <span style={{display:'inline-block', padding:'3px 10px', borderRadius:6, fontSize:12, fontWeight:600, background: cond.bg, color: cond.color, marginBottom:10}}>{cond.text}</span>
          <h1 style={{fontSize:22, fontWeight:600, lineHeight:1.4, marginBottom:8, color:'#0f1923'}}>{product.name}</h1>
          <p style={{color:'#888', fontSize:13, marginBottom:10}}>
            {t('soldBy')} <span style={{color:'#f97316', fontWeight:600}}>{product.vendor?.storeName}</span> &nbsp;·&nbsp; {catLabel(product.category?.name, language)}
          </p>

          {/* Stars */}
          <div style={{display:'flex', alignItems:'center', gap:8, marginBottom:14}}>
            {renderStars(avgRating)}
            <span style={{color:'#f97316', fontSize:14, fontWeight:600}}>{product.avgRating > 0 ? product.avgRating.toFixed(1) : ''}</span>
            <span style={{color:'#888', fontSize:13}}>{reviews.length > 0 ? `${reviews.length} ${t('reviews')}` : t('noRatings')}</span>
          </div>

          {/* Price */}
          <div style={{display:'flex', alignItems:'baseline', gap:10, marginBottom:4}}>
            <div style={{marginBottom:12}}>
            {product.compareAtPrice && product.compareAtPrice > product.price && (
            <div style={{
            fontSize:15,
            color:'#888',
            textDecoration:'line-through',
            marginBottom:4
            }}>
            {formatQAR(product.compareAtPrice)}
            </div>
             )}

  <div style={{
    display:'flex',
    alignItems:'center',
    gap:10
  }}>
    <span style={{
      fontSize:32,
      fontWeight:800,
      color:'#f97316'
    }}>
      {formatQAR(product.price)}
    </span>

    {product.compareAtPrice && product.compareAtPrice > product.price && (
      <span style={{
        background:'#dcfce7',
        color:'#166534',
        padding:'4px 10px',
        borderRadius:20,
        fontSize:12,
        fontWeight:700
      }}>
        {language === 'AR'
          ? `${Math.round(((product.compareAtPrice - product.price) / product.compareAtPrice) * 100)}٪ خصم`
          : `${Math.round(((product.compareAtPrice - product.price) / product.compareAtPrice) * 100)}% OFF`}
      </span>
    )}
  </div>
</div>
          </div>
          <p style={{fontSize:12, color:'#888', marginBottom:16}}>{t('inclVAT')}</p>

          <div style={{height:1, background:'#eee', marginBottom:16}} />

          {/* Specs */}
          <div style={{marginBottom:16}}>
            {[
              [t('condition'), cond.text],
              [t('inStock'), `${product.stockQty} ${t('units')}`],
              [t('category'), catLabel(product.category?.name, language)],
            ].map(([label, val]) => (
              <div key={label} style={{display:'flex', justifyContent:'space-between', padding:'7px 0', borderBottom:'1px solid #f3f3f3', fontSize:14}}>
                <span style={{color:'#888'}}>{label}</span>
                <span style={{fontWeight:600, color: label === t('inStock') ? '#10b981' : '#0f1923'}}>{val}</span>
              </div>
            ))}
          </div>

          {/* Description */}
          {product.description && (
            <p style={{color:'#555', lineHeight:1.8, fontSize:14, marginBottom:16}}>{product.description}</p>
          )}

          {/* Qty */}
          <div style={{display:'flex', alignItems:'center', gap:12, marginBottom:16}}>
            <span style={{fontSize:14, color:'#666'}}>{t('qty')}:</span>
            <button onClick={() => setQty(q => Math.max(1, q-1))} style={{width:32, height:32, borderRadius:8, border:'1px solid #ddd', background:'#f8f9fa', cursor:'pointer', fontSize:18, fontWeight:600}}>−</button>
            <span style={{fontSize:16, fontWeight:600, minWidth:24, textAlign:'center'}}>{qty}</span>
            <button onClick={() => setQty(q => Math.min(product.stockQty, q+1))} style={{width:32, height:32, borderRadius:8, border:'1px solid #ddd', background:'#f8f9fa', cursor:'pointer', fontSize:18, fontWeight:600}}>+</button>
          </div>

          {/* Buttons */}
          <button onClick={handleAddToCart} disabled={added}
            style={{width:'100%', padding:'14px', background: added ? '#10b981' : '#f97316', color:'#fff', border:'none', borderRadius:10, fontSize:16, fontWeight:700, cursor: added ? 'default' : 'pointer', marginBottom:10, transition:'background 0.2s'}}>
            {added ? `✓ ${t('addedToCart')}` : t('addToCart')}
          </button>

          {/* Delivery & Trust */}
          <div style={{background:'#f8f9fa', borderRadius:10, padding:14, marginTop:4}}>
            {[
              ['🚚', t('freeDelivery')],
              ['🛡️', t('buyerProtection')],
              ['↩️', t('easyReturns')],
            ].map(([icon, text]) => (
              <div key={text} style={{display:'flex', alignItems:'center', gap:8, fontSize:13, color:'#555', padding:'4px 0'}}>
                <span>{icon}</span><span>{text}</span>
              </div>
            ))}
          </div>

          {/* Trust badges */}
          <div style={{display:'flex', gap:16, marginTop:12, flexWrap:'wrap'}}>
            {[
              ['✅', t('verifiedSeller')],
              ['⚡', t('shipsIn24h')],
              ['📍', t('shipsQatar')],
            ].map(([icon, text]) => (
              <div key={text} style={{display:'flex', alignItems:'center', gap:4, fontSize:12, color:'#666'}}>
                <span>{icon}</span><span>{text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Reviews Section */}
      <div style={{borderTop:'2px solid #f3f3f3', paddingTop:32}}>
        <h2 style={{fontSize:20, fontWeight:700, marginBottom:24, color:'#0f1923'}}>{t('customerReviews')}</h2>

        {/* Rating summary */}
        {reviews.length > 0 && (
          <div style={{display:'flex', gap:32, alignItems:'center', marginBottom:28, background:'#f8f9fa', borderRadius:12, padding:24, flexWrap:'wrap'}}>
            <div style={{textAlign:'center'}}>
              <div style={{fontSize:48, fontWeight:700, color:'#f97316', lineHeight:1}}>{product.avgRating?.toFixed(1) || '0'}</div>
              <div style={{marginTop:6}}>{renderStars(avgRating)}</div>
              <div style={{fontSize:13, color:'#888', marginTop:4}}>{reviews.length} {t('reviews')}</div>
            </div>
            <div style={{flex:1, minWidth:160}}>
              {[5,4,3,2,1].map((star, i) => (
                <div key={star} style={{display:'flex', alignItems:'center', gap:8, marginBottom:5}}>
                  <span style={{fontSize:12, color:'#888', minWidth:28}}>{star} ★</span>
                  <div style={{flex:1, height:6, background:'#e5e7eb', borderRadius:3, overflow:'hidden'}}>
                    <div style={{height:'100%', background:'#f97316', borderRadius:3, width: reviews.length > 0 ? `${(ratingCounts[i]/reviews.length)*100}%` : '0%'}} />
                  </div>
                  <span style={{fontSize:12, color:'#888', minWidth:16}}>{ratingCounts[i]}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Write review */}
        {user && !userReview && (
          <div style={{background:'#fff', border:'1px solid #eee', borderRadius:12, padding:24, marginBottom:24}}>
            <h3 style={{marginBottom:16, fontSize:16, fontWeight:600}}>{t('writeReview')}</h3>
            {reviewError && <p style={{color:'#ef4444', marginBottom:12, fontSize:14}}>{reviewError}</p>}
            {reviewSuccess && <p style={{color:'#10b981', marginBottom:12, fontSize:14}}>{reviewSuccess}</p>}
            <p style={{marginBottom:8, fontWeight:600, fontSize:14}}>{t('yourRating')}</p>
            {renderStars(reviewForm.rating, true, (star) => setReviewForm({...reviewForm, rating: star}))}
            <textarea style={{...styles.input, height:100, resize:'vertical', marginTop:12}} placeholder={t('shareExperience')} value={reviewForm.comment} onChange={e => setReviewForm({...reviewForm, comment: e.target.value})} />
            <button onClick={handleSubmitReview} disabled={submitting} style={{...styles.submitBtn, opacity: submitting ? 0.7 : 1}}>
              {submitting ? t('submitting') : t('submitReview')}
            </button>
          </div>
        )}

        {/* User's own review */}
        {userReview && (
          <div style={{background:'#fff7ed', border:'1px solid #f97316', borderRadius:12, padding:20, marginBottom:20}}>
            <div style={{display:'flex', justifyContent:'space-between', alignItems:'flex-start'}}>
              <div>
                <p style={{fontWeight:600, marginBottom:6}}>{t('yourReview')}</p>
                {renderStars(userReview.rating)}
                <p style={{color:'#555', marginTop:8, fontSize:14}}>{userReview.comment}</p>
              </div>
              <button onClick={handleDeleteReview} style={{background:'none', border:'none', color:'#ef4444', cursor:'pointer', fontSize:13}}>{t('delete')}</button>
            </div>
          </div>
        )}

        {/* All reviews */}
        {reviews.length === 0 ? (
          <div style={{textAlign:'center', padding:40, color:'#888'}}>
            <p style={{fontSize:32, marginBottom:8}}>💬</p>
            <p>{t('noReviews')}</p>
          </div>
        ) : reviews.map(review => (
          <div key={review.id} style={{background:'#fff', border:'1px solid #eee', borderRadius:12, padding:20, marginBottom:12}}>
            <div style={{display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:8}}>
              <div>
                <div style={{display:'flex', alignItems:'center', gap:8, marginBottom:4}}>
                  <div style={{width:34, height:34, borderRadius:'50%', background:'#f97316', color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', fontSize:14, fontWeight:700}}>
                    {review.user?.name?.[0]?.toUpperCase() || '?'}
                  </div>
                  <div>
                    <p style={{fontWeight:600, fontSize:14}}>{review.user?.name}</p>
                    <span style={{fontSize:11, background:'#d1fae5', color:'#065f46', padding:'1px 6px', borderRadius:4}}>✓ {t('verifiedBuyer')}</span>
                  </div>
                </div>
                {renderStars(review.rating)}
              </div>
              <p style={{color:'#aaa', fontSize:12}}>{new Date(review.createdAt).toLocaleDateString()}</p>
            </div>
            <p style={{color:'#555', fontSize:14, lineHeight:1.7, marginTop:8}}>{review.comment}</p>
          </div>
        ))}
      </div>
    </div>
  )
}

function KYCDocumentsTab({ t = (k) => k }) {
  const [vendors, setVendors] = React.useState([])
  const [loading, setLoading] = React.useState(true)
  const [reviewNote, setReviewNote] = React.useState({})
  const [msg, setMsg] = React.useState('')
  const [expandedVendor, setExpandedVendor] = React.useState(null)

  const token = localStorage.getItem('token')
  const API = process.env.REACT_APP_API_URL

  const loadDocs = () => {
    fetch(`${API}/vendors/admin/documents`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(r => r.json()).then(data => {
      if (Array.isArray(data)) setVendors(data)
      setLoading(false)
    }).catch(() => setLoading(false))
  }

  React.useEffect(() => { loadDocs() }, []) // eslint-disable-line

  const handleReview = async (docId, status) => {
    try {
      const res = await fetch(`${API}/vendors/admin/documents/${docId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status, note: reviewNote[docId] || '' })
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error)
      setMsg(data.vendorFullyApproved
        ? '✅ Document approved — Vendor is now fully verified!'
        : `Document ${status.toLowerCase()} successfully`)
      setTimeout(() => setMsg(''), 4000)
      loadDocs()
    } catch (err) {
      setMsg('Failed to update document')
    }
  }

  const docTypeLabel = {
    CR_COPY: 'CR Copy',
    TRADE_LICENSE: 'Trade License',
    SIGNATORY_QID: 'Signatory QID',
    CONTRACT_COPY: 'Contract Copy'
  }

  const statusBadge = (status) => ({
    PENDING: { bg: '#fff7ed', color: '#92400e', text: '⏳ Pending' },
    APPROVED: { bg: '#d1fae5', color: '#065f46', text: '✅ Approved' },
    REJECTED: { bg: '#fee2e2', color: '#991b1b', text: '❌ Rejected' }
  }[status] || { bg: '#f3f4f6', color: '#666', text: status })

  if (loading) return <p style={{padding:40, color:'#666'}}>Loading...</p>

  return (
    <div>
      {msg && <div style={{background:'#d1fae5', color:'#065f46', padding:'12px 16px', borderRadius:8, marginBottom:16, fontWeight:600}}>{msg}</div>}

      {vendors.length === 0 ? (
        <p style={{color:'#888', textAlign:'center', padding:40}}>No vendor documents submitted yet</p>
      ) : vendors.map(vendor => {
        const hasDocs = vendor.documents?.length > 0
        const requiredTypes = ['CR_COPY', 'TRADE_LICENSE', 'SIGNATORY_QID']
        const allApproved = requiredTypes.every(type =>
          vendor.documents?.some(d => d.docType === type && d.status === 'APPROVED')
        )
        const hasPending = vendor.documents?.some(d => d.status === 'PENDING')
        const isExpanded = expandedVendor === vendor.id

        return (
          <div key={vendor.id} style={{border:'1px solid #eee', borderRadius:12, marginBottom:16, overflow:'hidden', background:'#fff'}}>
            <div
              onClick={() => setExpandedVendor(isExpanded ? null : vendor.id)}
              style={{display:'flex', justifyContent:'space-between', alignItems:'center', padding:'16px 20px', cursor:'pointer', background: allApproved ? '#f0fdf4' : hasPending ? '#fff7ed' : '#fafafa'}}
            >
              <div style={{display:'flex', alignItems:'center', gap:12}}>
                <span style={{fontSize:24}}>🏪</span>
                <div>
                  <p style={{fontWeight:700, fontSize:15, marginBottom:2}}>{vendor.storeName}</p>
                  <p style={{color:'#666', fontSize:13}}>{vendor.user?.name} · {vendor.user?.email}</p>
                </div>
              </div>
              <div style={{display:'flex', alignItems:'center', gap:12}}>
                <span style={{padding:'4px 12px', borderRadius:20, fontSize:12, fontWeight:700,
                  background: allApproved ? '#d1fae5' : hasPending ? '#fff7ed' : '#f3f4f6',
                  color: allApproved ? '#065f46' : hasPending ? '#92400e' : '#666'}}>
                  {allApproved ? '✅ KYC Complete' : hasPending ? `⏳ ${vendor.documents?.filter(d=>d.status==='PENDING').length} Pending` : hasDocs ? '❌ Action Needed' : '📭 No Docs'}
                </span>
                <span style={{color:'#999', fontSize:18}}>{isExpanded ? '▲' : '▼'}</span>
              </div>
            </div>

            {isExpanded && (
              <div style={{padding:'0 20px 20px'}}>
                {!hasDocs ? (
                  <p style={{color:'#888', padding:'20px 0', textAlign:'center'}}>No documents uploaded yet</p>
                ) : (
                  <>
                    {(vendor.ibanNumber || vendor.bankName) && (
                      <div style={{background:'#f8f9fa', borderRadius:10, padding:16, marginTop:16, marginBottom:8}}>
                        <p style={{fontWeight:700, fontSize:13, marginBottom:8, color:'#1e3a5f'}}>🏦 Bank Details</p>
                        <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:4, fontSize:13, color:'#555'}}>
                          {vendor.accountHolderName && <p>Holder: {vendor.accountHolderName}</p>}
                          {vendor.bankName && <p>Bank: {vendor.bankName}</p>}
                          {vendor.ibanNumber && <p>IBAN: {vendor.ibanNumber}</p>}
                          {vendor.accountNumber && <p>Account: {vendor.accountNumber}</p>}
                          {vendor.bankBranch && <p>Branch: {vendor.bankBranch}</p>}
                        </div>
                      </div>
                    )}

                    {vendor.documents.map(doc => {
                      const badge = statusBadge(doc.status)
                      return (
                        <div key={doc.id} style={{border:'1px solid #f0f0f0', borderRadius:10, padding:16, marginTop:12}}>
                          <div style={{display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:8}}>
                            <div style={{display:'flex', gap:10, alignItems:'flex-start'}}>
                              <span style={{fontSize:28}}>📄</span>
                              <div>
                                <p style={{fontWeight:600, fontSize:14, marginBottom:2}}>{doc.docName}</p>
                                <p style={{color:'#888', fontSize:12}}>{docTypeLabel[doc.docType] || doc.docType} · Uploaded {new Date(doc.uploadedAt).toLocaleDateString()}</p>
                                {doc.note && <p style={{color:'#ef4444', fontSize:12, marginTop:4}}>Note: {doc.note}</p>}
                              </div>
                            </div>
                            <div style={{display:'flex', alignItems:'center', gap:8}}>
                              <span style={{padding:'3px 10px', borderRadius:20, fontSize:12, fontWeight:600, background: badge.bg, color: badge.color}}>
                                {badge.text}
                              </span>
                              <a href={doc.fileUrl} target="_blank" rel="noopener noreferrer"
                                style={{background:'#1e3a5f', color:'#fff', padding:'6px 12px', borderRadius:6, fontSize:12, textDecoration:'none', fontWeight:600}}>
                                View Doc
                              </a>
                            </div>
                          </div>

                          {doc.status === 'PENDING' && (
                            <div style={{marginTop:12, paddingTop:12, borderTop:'1px solid #f0f0f0'}}>
                              <input
                                style={{width:'100%', padding:'10px 14px', borderRadius:8, border:'1px solid #ddd', fontSize:13, boxSizing:'border-box', marginBottom:8}}
                                placeholder="Rejection note (required if rejecting)"
                                value={reviewNote[doc.id] || ''}
                                onChange={e => setReviewNote({...reviewNote, [doc.id]: e.target.value})}
                              />
                              <div style={{display:'flex', gap:8}}>
                                <button onClick={() => handleReview(doc.id, 'APPROVED')}
                                  style={{flex:1, padding:'8px', background:'#10b981', color:'#fff', border:'none', borderRadius:8, cursor:'pointer', fontSize:13, fontWeight:600}}>
                                  ✅ Approve
                                </button>
                                <button onClick={() => handleReview(doc.id, 'REJECTED')}
                                  style={{flex:1, padding:'8px', background:'#ef4444', color:'#fff', border:'none', borderRadius:8, cursor:'pointer', fontSize:13, fontWeight:600}}>
                                  ❌ Reject
                                </button>
                              </div>
                            </div>
                          )}

                          {doc.status !== 'PENDING' && (
                            <div style={{marginTop:8}}>
                              <button
                                onClick={() => handleReview(doc.id, doc.status === 'APPROVED' ? 'REJECTED' : 'APPROVED')}
                                style={{padding:'6px 14px', background:'#f8f9fa', border:'1px solid #ddd', borderRadius:6, cursor:'pointer', fontSize:12, color:'#555'}}>
                                {doc.status === 'APPROVED' ? 'Revoke Approval' : 'Approve Instead'}
                              </button>
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </>
                )}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
function AdminDashboard({ t = (k) => k }) {
  const [tab, setTab] = useState('overview')
  const [stats, setStats] = useState(null)
  const [allOrders, setAllOrders] = useState([])
  const [allUsers, setAllUsers] = useState([])
  const [allVendors, setAllVendors] = useState([])
  const [payoutsData, setPayoutsData] = useState(null)
  const [payoutMsg, setPayoutMsg] = useState('')
  const [loading, setLoading] = useState(true)
  const [updatingId, setUpdatingId] = useState(null)
  const [approvingId, setApprovingId] = useState(null)
  const [subForm, setSubForm] = useState({ vendorId:'', type:'SETUP', amount:1000, note:'' })
  const [subMsg, setSubMsg] = useState('')
  const [kycVendors, setKycVendors] = useState([])
  const [, setSelectedVendor] = useState(null)

  const loadData = async () => {
    setLoading(true)
    try {
      const [statsRes, ordersRes, usersRes, vendorsRes] = await Promise.all([
        orders.adminGetStats(),
        orders.adminGetAll(),
        orders.adminGetUsers(),
        orders.adminGetVendors()
      ])
      setStats(statsRes.data)
      setAllOrders(ordersRes.data)
      setAllUsers(usersRes.data)
      setAllVendors(vendorsRes.data)
      try {
        const payoutsRes = await vendors.adminGetPayouts()
        setPayoutsData(payoutsRes.data)
      } catch(e) { console.log('payouts error', e) }
    try {
      const kycRes = await fetch('/api/vendors/admin/documents', { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
      setKycVendors(await kycRes.json())
    } catch(e) {}
    } catch (err) { console.error('Admin load error:', err) }
    setLoading(false)
  }
  const updateUserStatus = async (userId, status) => {
  try {
    await orders.updateUserStatus(userId, status)
    loadData()
  } catch (err) {
    alert(err.response?.data?.error || 'Failed to update user status')
  }
}


  useEffect(() => { loadData() }, []) // eslint-disable-line

  const handleStatusUpdate = async (orderId, status) => {
    setUpdatingId(orderId)
    try { await orders.updateStatus(orderId, status); await loadData() }
    catch (err) { alert(err.response?.data?.error || t('updateFailed')) }
    setUpdatingId(null)
  }

  const handleMarkPaid = async (vendorId, amount) => {
    const note = prompt(t('addNote')) || ''
    try {
      await vendors.adminMarkPaid({ vendorId, amount, note })
      setPayoutMsg(t('payoutMarked'))
      loadData()
      setTimeout(() => setPayoutMsg(''), 3000)
    } catch (err) { alert(err.response?.data?.error || t('payoutFailed')) }
  }

  const handleVerifyDoc = async (docId, verified) => {
    try {
      const note = !verified ? prompt('Rejection reason (optional):') || '' : ''
      await fetch('/api/vendors/admin/verify-doc', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ docId, verified, note })
      })
      const kycRes = await fetch('/api/vendors/admin/documents', { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
      setKycVendors(await kycRes.json())
    } catch { alert('Failed to update document') }
  }

const handleVendorApproval = async (vendorId, status, note = '') => {
  setApprovingId(vendorId)
  try {
    await vendors.updateStatus({ vendorId, status, note })
    await loadData()
  } catch (err) {
    alert(err.response?.data?.error || 'Failed to update vendor status')
  }
  setApprovingId(null)
}

  const handleAddSubscription = async () => {
    if (!subForm.vendorId) { alert('Select a vendor'); return }
    try {
      await fetch('/api/vendors/admin/subscription', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify(subForm)
      })
      setSubMsg('Subscription added!')
      setTimeout(() => setSubMsg(''), 3000)
      await loadData()
    } catch (err) { alert('Failed to add subscription') }
    
 
  }

  const statusColor = { PENDING:'#f97316', CONFIRMED:'#3b82f6', SHIPPED:'#8b5cf6', DELIVERED:'#10b981', CANCELLED:'#ef4444' }
  const roleColor = { CUSTOMER:'#6b7280', VENDOR:'#8b5cf6', ADMIN:'#ef4444' }
  const tabBtn = (key, label) => (
    <button onClick={() => setTab(key)} style={{padding:'10px 20px', borderRadius:8, border:'none', cursor:'pointer', fontWeight:600, fontSize:14, background: tab === key ? '#f97316' : '#f8f9fa', color: tab === key ? '#fff' : '#333'}}>{label}</button>
  )

  if (loading) return <p style={{padding:40}}>{t('loading')}</p>

  return (
    <div style={styles.page}>
      <div style={{background:'linear-gradient(135deg, #0f1923 0%, #1e3a5f 100%)', borderRadius:16, padding:32, marginBottom:32, color:'#fff'}}>
        <h2 style={{fontSize:28, marginBottom:4}}>{t('adminDashboard')}</h2>
        <p style={{color:'#94a3b8'}}>{t('adminSubtitle')}</p>
      </div>
      <div style={{display:'flex', gap:12, marginBottom:24, flexWrap:'wrap'}}>
        {tabBtn('overview', t('overview'))}
        {tabBtn('orders', t('allOrders'))}
        {tabBtn('users', t('users'))}
        {tabBtn('vendors', t('vendors'))}
        {tabBtn('approvals', t('approvals'))}
        {tabBtn('kyc', t('kycDocuments'))}
        {tabBtn('subscriptions', t('subscriptions'))}
        {tabBtn('payouts', t('payouts'))}
      </div>

      {tab === 'overview' && stats && (
        <div>
          <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(200px, 1fr))', gap:16, marginBottom:32}}>
            {[
              { label: t('totalOrders'), value: stats.totalOrders, icon:'📦', color:'#f97316' },
              { label: t('totalRevenue'), value: formatQAR(stats.totalRevenue || 0), icon:'💰', color:'#10b981' },
              { label: t('totalUsers'), value: stats.totalUsers, icon:'👥', color:'#3b82f6' },
              { label: t('totalVendors'), value: stats.totalVendors, icon:'🏪', color:'#8b5cf6' },
              { label: t('activeProducts'), value: stats.totalProducts, icon:'📱', color:'#f43f5e' },
            ].map(stat => (
              <div key={stat.label} style={{background:'#fff', border:'1px solid #eee', borderRadius:12, padding:20}}>
                <p style={{fontSize:32, marginBottom:8}}>{stat.icon}</p>
                <p style={{fontSize:28, fontWeight:700, color: stat.color}}>{stat.value}</p>
                <p style={{color:'#666', fontSize:14}}>{stat.label}</p>
              </div>
            ))}
          </div>
          <h3 style={{marginBottom:16}}>{t('recentOrders')}</h3>
          {stats.recentOrders?.map(order => (
            <div key={order.id} style={{border:'1px solid #eee', borderRadius:12, padding:16, marginBottom:12, background:'#fff', display:'flex', justifyContent:'space-between', alignItems:'center'}}>
              <div>
                <p style={{fontWeight:600}}>{t('order')} #{order.id?.slice(0,8)}...</p>
                <p style={{color:'#666', fontSize:13}}>{order.user?.name}</p>
              </div>
              <div style={{display:'flex', alignItems:'center', gap:12}}>
                <p style={{fontWeight:700, color:'#f97316'}}>{formatQAR(order.totalAmount)}</p>
                <span style={{background: statusColor[order.status] + '20', color: statusColor[order.status], padding:'4px 10px', borderRadius:20, fontSize:12, fontWeight:600}}>{order.status}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'orders' && (
        <div>
          <h3 style={{marginBottom:16}}>{t('allOrders')} ({allOrders.length})</h3>
          {allOrders.map(order => (
            <div key={order.id} style={{border:'1px solid #eee', borderRadius:12, padding:20, marginBottom:16, background:'#fff'}}>
              <div style={{display:'flex', justifyContent:'space-between', flexWrap:'wrap', gap:12}}>
                <div>
                  <p style={{fontWeight:600}}>{t('order')} #{order.id?.slice(0,8)}...</p>
                  <p style={{color:'#666', fontSize:13}}>{order.user?.name} ({order.user?.email})</p>
                  <p style={{color:'#666', fontSize:13}}>{new Date(order.createdAt).toLocaleDateString()}</p>
                </div>
                <div style={{textAlign:'right'}}>
                  <p style={{fontWeight:700, color:'#f97316', fontSize:18, marginBottom:8}}>{formatQAR(order.totalAmount)}</p>
                  <select value={order.status} onChange={e => handleStatusUpdate(order.id, e.target.value)} disabled={updatingId === order.id}
                    style={{padding:'6px 10px', borderRadius:8, border:'1px solid #ddd', fontSize:13, cursor:'pointer'}}>
                    <option value="PENDING">PENDING</option>
                    <option value="CONFIRMED">CONFIRMED</option>
                    <option value="SHIPPED">SHIPPED</option>
                    <option value="DELIVERED">DELIVERED</option>
                    <option value="CANCELLED">CANCELLED</option>
                  </select>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'users' && (
        <div>
          <h3 style={{marginBottom:16}}>{t('users')} ({allUsers.length})</h3>
          <div style={{border:'1px solid #eee', borderRadius:12, overflow:'hidden'}}>
            {allUsers.map((u, i) => (
              <div key={u.id} style={{display:'flex', justifyContent:'space-between', alignItems:'center', padding:'14px 20px', background: i % 2 === 0 ? '#fff' : '#f8f9fa', borderBottom:'1px solid #eee'}}>
                <div>
                  <p style={{fontWeight:600}}>{u.name}</p>
                  <p style={{color:'#666', fontSize:13}}>{u.email}</p>
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:10 }}>
  <span style={{
    background: roleColor[u.role] + '20',
    color: roleColor[u.role],
    padding:'4px 10px',
    borderRadius:20,
    fontSize:12,
    fontWeight:600
  }}>
    {u.role}
  </span>

  <span style={{
    background:
      u.approvalStatus === 'ACTIVE'
        ? '#dcfce7'
        : u.approvalStatus === 'BLOCKED'
        ? '#fee2e2'
        : '#fef3c7',
    color:
      u.approvalStatus === 'ACTIVE'
        ? '#166534'
        : u.approvalStatus === 'BLOCKED'
        ? '#991b1b'
        : '#92400e',
    padding:'4px 10px',
    borderRadius:20,
    fontSize:12,
    fontWeight:600
  }}>
    {u.approvalStatus}
  </span>

  <button
    onClick={() => updateUserStatus(u.id, 'ACTIVE')}
    style={{
      background:'#16a34a',
      color:'#fff',
      border:'none',
      padding:'6px 10px',
      borderRadius:8,
      cursor:'pointer'
    }}
  >
    Approve
  </button>

  <button
    onClick={() => updateUserStatus(u.id, 'BLOCKED')}
    style={{
      background:'#dc2626',
      color:'#fff',
      border:'none',
      padding:'6px 10px',
      borderRadius:8,
      cursor:'pointer'
    }}
  >
    Block
  </button>
</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === 'vendors' && (
        <div>
          <h3 style={{marginBottom:16}}>{t('vendors')} ({allVendors.length})</h3>
          <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(300px, 1fr))', gap:16}}>
            {allVendors.map(vendor => (
              <div key={vendor.id} style={{border:'1px solid #eee', borderRadius:12, padding:20, background:'#fff'}}>
                <p style={{fontWeight:700, fontSize:16, marginBottom:4}}>🏪 {vendor.storeName}</p>
                <p style={{color:'#666', fontSize:13}}>{vendor.user?.name}</p>
                <p style={{color:'#aaa', fontSize:12}}>{vendor.user?.email}</p>
                <div style={{ display:'flex', gap:8, marginTop:10, marginBottom:10 }}>
  <span style={{
    background: vendor.subscriptionStatus === 'ACTIVE' ? '#dcfce7' : '#fef3c7',
    color: vendor.subscriptionStatus === 'ACTIVE' ? '#166534' : '#92400e',
    padding:'4px 10px',
    borderRadius:20,
    fontSize:12,
    fontWeight:600
  }}>
    {vendor.subscriptionStatus || 'PENDING'}
  </span>

  {vendor.subscriptionPlan && (
    <span style={{
      background:'#e0f2fe',
      color:'#075985',
      padding:'4px 10px',
      borderRadius:20,
      fontSize:12,
      fontWeight:600
    }}>
      {vendor.subscriptionPlan}
    </span>
  )}
</div>
                <div style={{display:'flex', gap:16, marginTop:12}}>
                  <div style={{textAlign:'center'}}>
                    <p style={{fontWeight:700, color:'#f97316'}}>{vendor._count?.products || 0}</p>
                    <p style={{color:'#666', fontSize:12}}>{t('products')}</p>
                  </div>
                  <div style={{textAlign:'center'}}>
                    <p style={{fontWeight:700, color:'#3b82f6'}}>{vendor._count?.orderItems || 0}</p>
                    <p style={{color:'#666', fontSize:12}}>{t('orders')}</p>
                    <div style={{ marginTop: 16, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
  <button
    onClick={async () => {
      await vendors.updateSubscription(vendor.id, {
        subscriptionStatus: 'ACTIVE',
        subscriptionPlan: 'PRO'
      })
      loadData()
    }}
    style={{
      background: '#16a34a',
      color: '#fff',
      border: 'none',
      padding: '8px 14px',
      borderRadius: 8,
      cursor: 'pointer'
    }}
  >
    Activate Subscription
  </button>

  <button
    onClick={async () => {
      await vendors.updateSubscription(vendor.id, {
        subscriptionStatus: 'EXPIRED'
      })
      loadData()
    }}
    style={{
      background: '#dc2626',
      color: '#fff',
      border: 'none',
      padding: '8px 14px',
      borderRadius: 8,
      cursor: 'pointer'
    }}
  >
    Expire
  </button>
</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === 'approvals' && (
        <div>
          <h3 style={{marginBottom:16}}>{t('vendorApprovals')}</h3>
          {allVendors.filter(v => v.status === 'PENDING').length === 0 && (
            <div style={{textAlign:'center', padding:40, color:'#888'}}>
              <p style={{fontSize:32, marginBottom:8}}>✅</p>
              <p>{t('noPendingVendors')}</p>
            </div>
          )}
          {allVendors.map(vendor => (
            <div key={vendor.id} style={{border:'1px solid #eee', borderRadius:12, padding:20, marginBottom:16, background:'#fff'}}>
              <div style={{display:'flex', justifyContent:'space-between', flexWrap:'wrap', gap:12}}>
                <div>
                  <p style={{fontWeight:700, fontSize:16}}>🏪 {vendor.storeName}</p>
                  <p style={{color:'#666', fontSize:13}}>{vendor.user?.name} · {vendor.user?.email}</p>
                  <p style={{color:'#aaa', fontSize:12}}>📞 {vendor.user?.phone || 'No phone'}</p>
                  <p style={{ fontSize:13, color:'#666' }}> Banking: {vendor.ibanNumber ? 'Added' : 'Missing'}</p>
                  <p style={{fontSize:13, marginTop:6}}>
                    Registered: {new Date(vendor.createdAt).toLocaleDateString()}
                  </p>
                </div>
                <div style={{display:'flex', flexDirection:'column', gap:8, alignItems:'flex-end'}}>
                  <span style={{
                    padding:'4px 12px', borderRadius:20, fontSize:13, fontWeight:600,
                    background: vendor.status === 'APPROVED' ? '#d1fae5' : vendor.status === 'REJECTED' ? '#fee2e2' : '#fff7ed',
                    color: vendor.status === 'APPROVED' ? '#065f46' : vendor.status === 'REJECTED' ? '#991b1b' : '#92400e'
                  }}>{vendor.status}</span>
                  {vendor.status === 'PENDING' && (
                    <div style={{display:'flex', gap:8}}>
                      <button onClick={() => handleVendorApproval(vendor.id, 'APPROVED')} disabled={approvingId === vendor.id}
                        style={{padding:'8px 16px', background:'#10b981', color:'#fff', border:'none', borderRadius:8, cursor:'pointer', fontSize:13, fontWeight:600}}>
                        ✓ {t('approve')}
                      </button>
                      <button onClick={() => { const note = prompt('Rejection reason (optional):') || ''; handleVendorApproval(vendor.id, 'REJECTED', note) }} disabled={approvingId === vendor.id}
                        style={{padding:'8px 16px', background:'#ef4444', color:'#fff', border:'none', borderRadius:8, cursor:'pointer', fontSize:13, fontWeight:600}}>
                        ✗ {t('reject')}
                      </button>
                      <button
                      onClick={() => setSelectedVendor(vendor)}
                      style={{
                      padding:'8px 16px',
                      background:'#2563eb',
                      color:'#fff',
                      border:'none',
                      borderRadius:8,
                      cursor:'pointer'
                      }}
                      >      
                      View KYC
                      </button>
                    </div>
                  )}
                  {vendor.status === 'APPROVED' && (
                    <button onClick={() => { const note = prompt('Rejection reason:') || ''; handleVendorApproval(vendor.id, 'REJECTED', note) }}
                      style={{padding:'6px 12px', background:'#fee2e2', color:'#ef4444', border:'none', borderRadius:8, cursor:'pointer', fontSize:12}}>
                      Revoke
                    </button>
                  )}
                  {vendor.status === 'REJECTED' && (
                    <button onClick={() => handleVendorApproval(vendor.id, 'APPROVED')}
                      style={{padding:'6px 12px', background:'#d1fae5', color:'#065f46', border:'none', borderRadius:8, cursor:'pointer', fontSize:12}}>
                      Re-approve
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'kyc' && (
        <div>
          <h3 style={{marginBottom:4}}>{t('kycDocuments')}</h3>
          <p style={{color:'#888', fontSize:14, marginBottom:24}}>{t('kycNote')}</p>
          {kycVendors.length === 0 ? (
            <p style={{color:'#888', textAlign:'center', padding:40}}>{t('noVendors')}</p>
          ) : kycVendors.map(vendor => (
            <div key={vendor.id} style={{border:'1px solid #eee', borderRadius:12, padding:20, marginBottom:20, background:'#fff'}}>
              {/* Vendor Header */}
              <div style={{display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:16, flexWrap:'wrap', gap:8}}>
                <div>
                  <p style={{fontWeight:700, fontSize:16}}>🏪 {vendor.storeName}</p>
                  <p style={{color:'#666', fontSize:13}}>{vendor.user?.name} · {vendor.user?.email}</p>
                  <p style={{color:'#aaa', fontSize:12}}>📞 {vendor.user?.phone || 'No phone'}</p>
                </div>
                <span style={{padding:'4px 12px', borderRadius:20, fontSize:12, fontWeight:600,
                  background: vendor.status === 'APPROVED' ? '#d1fae5' : vendor.status === 'REJECTED' ? '#fee2e2' : '#fff7ed',
                  color: vendor.status === 'APPROVED' ? '#065f46' : vendor.status === 'REJECTED' ? '#991b1b' : '#92400e'}}>
                  {vendor.status}
                </span>
              </div>

              {/* Bank Details */}
              <div style={{background:'#f8f9fa', borderRadius:10, padding:16, marginBottom:16}}>
                <p style={{fontWeight:600, fontSize:14, marginBottom:10}}>🏦 {t('bankingDetails')}</p>
                <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, fontSize:13}}>
                  {[
                    [t('accountHolderName'), vendor.accountHolderName],
                    [t('bankName'), vendor.bankName],
                    [t('ibanNumber'), vendor.ibanNumber],
                    [t('accountNumber'), vendor.accountNumber],
                    [t('bankBranch'), vendor.bankBranch],
                  ].map(([label, val]) => (
                    <div key={label} style={{background:'#fff', padding:'8px 12px', borderRadius:8, border:'1px solid #eee'}}>
                      <p style={{color:'#888', fontSize:11, marginBottom:2}}>{label}</p>
                      <p style={{fontWeight:600, color: val ? '#0f1923' : '#ccc'}}>{val || 'Not provided'}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Documents */}
              <p style={{fontWeight:600, fontSize:14, marginBottom:10}}>📄 {t('uploadedDocuments')} ({vendor.documents?.length || 0})</p>
              {!vendor.documents?.length ? (
                <p style={{color:'#aaa', fontSize:13, fontStyle:'italic'}}>{t('noDocuments')}</p>
              ) : vendor.documents.map(doc => (
                <div key={doc.id} style={{display:'flex', justifyContent:'space-between', alignItems:'center', padding:'10px 0', borderBottom:'1px solid #f3f3f3'}}>
                  <div style={{display:'flex', alignItems:'center', gap:10}}>
                    <span style={{fontSize:20}}>📄</span>
                    <div>
                      <p style={{fontWeight:600, fontSize:13}}>{doc.docName}</p>
                      <p style={{color:'#888', fontSize:11}}>{doc.docType.replace(/_/g,' ')} · {new Date(doc.uploadedAt).toLocaleDateString()}</p>
                      {doc.note && <p style={{color:'#ef4444', fontSize:11}}>{doc.note}</p>}
                    </div>
                  </div>
                  <div style={{display:'flex', alignItems:'center', gap:8}}>
                    <a href={doc.docUrl} target="_blank" rel="noopener noreferrer"
                      style={{color:'#f97316', fontSize:12, fontWeight:600, textDecoration:'none'}}>View →</a>
                    {!doc.verified ? (
                      <button onClick={() => handleVerifyDoc(doc.id, true)}
                        style={{padding:'5px 12px', background:'#10b981', color:'#fff', border:'none', borderRadius:6, cursor:'pointer', fontSize:12, fontWeight:600}}>
                        ✓ Verify
                      </button>
                    ) : (
                      <button onClick={() => handleVerifyDoc(doc.id, false)}
                        style={{padding:'5px 12px', background:'#fee2e2', color:'#ef4444', border:'none', borderRadius:6, cursor:'pointer', fontSize:12}}>
                        Unverify
                      </button>
                    )}
                    <span style={{padding:'3px 8px', borderRadius:10, fontSize:11, fontWeight:600,
                      background: doc.verified ? '#d1fae5' : '#fff7ed',
                      color: doc.verified ? '#065f46' : '#92400e'}}>
                      {doc.verified ? '✓ Verified' : '⏳ Pending'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      {tab === 'subscriptions' && (
        <div>
          {subMsg && <div style={{background:'#d1fae5', color:'#065f46', padding:'12px 16px', borderRadius:8, marginBottom:16}}>{subMsg}</div>}
          <div style={{background:'#fff', border:'1px solid #eee', borderRadius:12, padding:24, marginBottom:32}}>
            <h3 style={{marginBottom:16}}>{t('addSubscription')}</h3>
            <div style={{display:'flex', gap:12, flexWrap:'wrap', alignItems:'flex-end'}}>
              <select value={subForm.vendorId} onChange={e => setSubForm({...subForm, vendorId: e.target.value})}
                style={{...styles.input, marginBottom:0, minWidth:200}}>
                <option value="">{t('selectVendor')}</option>
                {allVendors.filter(v => v.status === 'APPROVED').map(v => (
                  <option key={v.id} value={v.id}>{v.storeName}</option>
                ))}
              </select>
              <select value={subForm.type} onChange={e => {
                const amounts = { SETUP: 1000, MONTHLY: 250, ANNUAL: 500 }
                setSubForm({...subForm, type: e.target.value, amount: amounts[e.target.value]})
              }} style={{...styles.input, marginBottom:0}}>
                <option value="SETUP">Setup Fee — QAR 1,000</option>
                <option value="MONTHLY">Monthly — QAR 250</option>
                <option value="ANNUAL">Annual Renewal — QAR 500</option>
              </select>
              <input style={{...styles.input, marginBottom:0, width:120}} type="number" value={subForm.amount}
                onChange={e => setSubForm({...subForm, amount: parseFloat(e.target.value)})} />
              <input style={{...styles.input, marginBottom:0, flex:1}} placeholder="Note (optional)"
                value={subForm.note} onChange={e => setSubForm({...subForm, note: e.target.value})} />
              <button onClick={handleAddSubscription} style={{...styles.submitBtn, width:'auto', padding:'12px 20px', marginBottom:0}}>
                {t('addSubscription')}
              </button>
            </div>
          </div>
          <h3 style={{marginBottom:16}}>{t('subscriptionPricing')}</h3>
          <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(220px, 1fr))', gap:16, marginBottom:32}}>
            {[
              { label:'Setup Fee', amount:'QAR 1,000', icon:'🚀', desc:'One-time store setup', color:'#3b82f6' },
              { label:'Monthly Fee', amount:'QAR 250', icon:'📅', desc:'Per month subscription', color:'#f97316' },
              { label:'Annual Renewal', amount:'QAR 500', icon:'🔄', desc:'Yearly renewal discount', color:'#10b981' },
            ].map(item => (
              <div key={item.label} style={{background:'#fff', border:'1px solid #eee', borderRadius:12, padding:20, textAlign:'center'}}>
                <p style={{fontSize:32, marginBottom:8}}>{item.icon}</p>
                <p style={{fontSize:22, fontWeight:800, color: item.color}}>{item.amount}</p>
                <p style={{fontWeight:600, marginBottom:4}}>{item.label}</p>
                <p style={{color:'#888', fontSize:13}}>{item.desc}</p>
              </div>
            ))}
          </div>
          <h3 style={{marginBottom:16}}>{t('subscriptionHistory')}</h3>
          {allVendors.filter(v => v.status === 'APPROVED').map(vendor => (
            vendor.subscriptions?.length > 0 && (
              <div key={vendor.id} style={{border:'1px solid #eee', borderRadius:12, padding:20, marginBottom:16, background:'#fff'}}>
                <p style={{fontWeight:700, marginBottom:12}}>🏪 {vendor.storeName}</p>
                {vendor.subscriptions.map(sub => (
                  <div key={sub.id} style={{display:'flex', justifyContent:'space-between', padding:'8px 0', borderBottom:'1px solid #f3f3f3', fontSize:14}}>
                    <span style={{color:'#666'}}>{sub.type} — {new Date(sub.createdAt).toLocaleDateString()}</span>
                    <span style={{fontWeight:600, color:'#10b981'}}>QAR {sub.amount}</span>
                  </div>
                ))}
              </div>
            )
          ))}
        </div>
      )}

      {tab === 'payouts' && (
        <div>
          {payoutMsg && <div style={{background:'#d1fae5', color:'#065f46', padding:'12px 16px', borderRadius:8, marginBottom:16}}>{payoutMsg}</div>}
          {payoutsData ? (
            <>
              <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(220px, 1fr))', gap:16, marginBottom:32}}>
                <div style={{background:'#fff', border:'1px solid #eee', borderRadius:12, padding:20}}>
                  <p style={{fontSize:28, marginBottom:8}}>🏦</p>
                  <p style={{fontSize:22, fontWeight:700, color:'#10b981'}}>{formatQAR(payoutsData.summary?.totalPlatformRevenue || 0)}</p>
                  <p style={{color:'#666', fontSize:14}}>{t('totalPlatformRevenue')}</p>
                </div>
                <div style={{background:'#fff', border:'1px solid #eee', borderRadius:12, padding:20}}>
                  <p style={{fontSize:28, marginBottom:8}}>⏳</p>
                  <p style={{fontSize:22, fontWeight:700, color:'#f97316'}}>{formatQAR(payoutsData.summary?.totalPendingPayouts || 0)}</p>
                  <p style={{color:'#666', fontSize:14}}>{t('totalPendingPayouts')}</p>
                </div>
              </div>
              <h3 style={{marginBottom:16}}>{t('vendorPayoutSummary')}</h3>
              {payoutsData.vendors?.map(vendor => (
                <div key={vendor.id} style={{border:'1px solid #eee', borderRadius:12, padding:20, marginBottom:16, background:'#fff'}}>
                  <div style={{display:'flex', justifyContent:'space-between', flexWrap:'wrap', gap:12}}>
                    <div>
                      <p style={{fontWeight:700, fontSize:16, marginBottom:4}}>🏪 {vendor.storeName}</p>
                      <p style={{color:'#666', fontSize:13}}>{vendor.ownerName} - {vendor.ownerEmail}</p>
                      <p style={{color:'#aaa', fontSize:12}}>IBAN: {vendor.ibanNumber || t('notProvided')}</p>
                    </div>
                    <div style={{textAlign:'right'}}>
                      <div style={{display:'flex', gap:12, marginBottom:12, flexWrap:'wrap'}}>
                        <div style={{textAlign:'center', background:'#f8f9fa', padding:'8px 12px', borderRadius:8}}>
                          <p style={{fontWeight:700, color:'#1e3a5f', fontSize:14}}>{formatQAR(vendor.totalSales)}</p>
                          <p style={{color:'#666', fontSize:11}}>{t('totalSales')}</p>
                        </div>
                        
                        <div style={{textAlign:'center', background:'#d1fae5', padding:'8px 12px', borderRadius:8}}>
                          <p style={{fontWeight:700, color:'#065f46', fontSize:14}}>{formatQAR(vendor.totalEarnings)}</p>
                          <p style={{color:'#666', fontSize:11}}>{t('vendorEarning')}</p>
                        </div>
                      </div>
                      <div style={{display:'flex', gap:12, marginBottom:12, flexWrap:'wrap'}}>
  {[
    {
      title: 'Platform Enrollment Fee',
      amount: `QAR ${1000 - (1000 * (vendor.subscriptionDiscountPercent || 0) / 100)}`,
      status: vendor.enrollmentPaid ? 'Paid' : 'Pending',
      paid: vendor.enrollmentPaid,
      update: { enrollmentPaid: true }
    },
    {
      title: 'Monthly Fee',
      amount: `QAR ${250 - (250 * (vendor.subscriptionDiscountPercent || 0) / 100)}`,
      status: vendor.monthlyFeePaid ? 'Paid' : 'Pending',
      paid: vendor.monthlyFeePaid,
      update: { monthlyFeePaid: true }
    },
    {
      title: 'Annual Renewal Fee',
      amount: `QAR ${500 - (500 * (vendor.subscriptionDiscountPercent || 0) / 100)}`,
      status: vendor.annualRenewalPaid ? 'Paid' : 'Pending',
      paid: vendor.annualRenewalPaid,
      update: { annualRenewalPaid: true }
    }
  ].map((fee, i) => (
    <div key={i} style={{
      minWidth:150,
      background: fee.paid ? '#dcfce7' : '#fff7ed',
      border:'1px solid #eee',
      borderRadius:10,
      padding:'10px 12px'
    }}>
      <p style={{fontSize:12, color:'#666', marginBottom:4}}>{fee.title}</p>
      <p style={{fontSize:15, fontWeight:800, color:'#0f1923', marginBottom:4}}>{fee.amount}</p>
      <span style={{
        fontSize:11,
        fontWeight:700,
        color: fee.paid ? '#16a34a' : '#f97316'
      }}>
        {fee.status}
      </span>
      {!fee.paid && (
        <div>
          <div style={{ marginTop:8 }}>
            <select
              defaultValue={vendor.subscriptionDiscountPercent || 0}
              onChange={(e) => {
                vendor.subscriptionDiscountPercent = parseInt(e.target.value)
              }}
              style={{ padding:'6px', borderRadius:6, border:'1px solid #ddd', marginRight:6, fontSize:12 }}
            >
              <option value={0}>No Discount</option>
              <option value={10}>10% Discount</option>
              <option value={20}>20% Discount</option>
              <option value={30}>30% Discount</option>
              <option value={40}>40% Discount</option>
              <option value={50}>50% Discount</option>
            </select>
            <input
              placeholder="Reason"
              defaultValue={vendor.subscriptionDiscountReason || ''}
              onChange={(e) => {
                vendor.subscriptionDiscountReason = e.target.value
              }}
              style={{ padding:'6px', borderRadius:6, border:'1px solid #ddd', fontSize:12, width:120 }}
            />
          </div>
          <button
            onClick={() =>
              vendors.updateFees(vendor.id, {
                ...fee.update,
                subscriptionDiscountPercent: vendor.subscriptionDiscountPercent || 0,
                subscriptionDiscountReason: vendor.subscriptionDiscountReason || ''
              })
              .then(() => loadData())
              .catch(err => alert(err.response?.data?.error || 'Failed'))
            }
            style={{ marginTop:8, padding:'6px 10px', border:'none', borderRadius:8, background:'#10b981', color:'#fff', fontSize:11, fontWeight:700, cursor:'pointer' }}
          >
            Mark Paid
          </button>
        </div>
      )}
    </div>
  ))}
</div>
                      <div style={{display:'flex', alignItems:'center', gap:12, justifyContent:'flex-end'}}>
                        <div>
                          <p style={{fontSize:13, color:'#666'}}>{t('paid')}: {formatQAR(vendor.totalPaid)}</p>
                          <p style={{fontSize:15, fontWeight:700, color: vendor.pendingPayout > 0 ? '#ef4444' : '#10b981'}}>{t('pending')}: {formatQAR(vendor.pendingPayout)}</p>
                        </div>
                        {vendor.pendingPayout > 0 && (
                          <button onClick={() => handleMarkPaid(vendor.id, vendor.pendingPayout)} style={{padding:'8px 16px', background:'#10b981', color:'#fff', border:'none', borderRadius:8, cursor:'pointer', fontSize:13, fontWeight:600}}>{t('markPaid')}</button>
                        )}
                        
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </>
          ) : <p style={{padding:40, color:'#666'}}>{t('loading')}</p>}
        </div>
      )}
    {tab === 'kyc' && <KYCDocumentsTab t={t} />}
    </div>
  )
}


function VendorDashboard({ t = (k) => k, language = 'EN' }) {
  const [store, setStore] = useState(null)
  const [loading, setLoading] = useState(true)
  const [tab, setTab] = useState('products')
  const [form, setForm] = useState({ name: '', description: '', price: '', stockQty: '', categoryId: '', condition: 'NEW' })
  const [imageFile, setImageFile] = useState(null)
  const [imagePreview, setImagePreview] = useState(null)
  const [imageTab, setImageTab] = useState('upload')
  const [imageUrl, setImageUrl] = useState('')
  const [excelFile, setExcelFile] = useState(null)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')
  const [editingId, setEditingId] = useState(null)
  const [categories, setCategories] = useState([])
  const [earnings, setEarnings] = useState(null)
  const [iban, setIban] = useState('')
  const [ibanMsg, setIbanMsg] = useState('')
  const [docs, setDocs] = useState([])
  const [docUploading, setDocUploading] = useState(false)
  const [docMsg, setDocMsg] = useState('')
  const [docType, setDocType] = useState('CR_COPY')
  const [docFile, setDocFile] = useState(null)
  const [bankForm, setBankForm] = useState({ ibanNumber:'', bankName:'', accountHolderName:'', accountNumber:'', bankBranch:'' })
  const [bankMsg, setBankMsg] = useState('')
  const [vendorOrders, setVendorOrders] = useState([])
  const [ordersLoading, setOrdersLoading] = useState(false)

  const loadStore = () => {
    vendors.getMyStore().then(r => {
      setStore(r.data)
      setLoading(false)
      // Pre-fill bank form from store data
      if (r.data) {
        setBankForm({
          ibanNumber: r.data.ibanNumber || '',
          bankName: r.data.bankName || '',
          accountHolderName: r.data.accountHolderName || '',
          accountNumber: r.data.accountNumber || '',
          bankBranch: r.data.bankBranch || '',
        })
      }
    }).catch(() => setLoading(false))
  }

const loadDocs = () => {
  fetch(`${API_ORIGIN}/api/vendors/documents`, {
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  }).then(r => r.json()).then(data => {
    if (Array.isArray(data)) setDocs(data)
    else setDocs([])
  }).catch(() => setDocs([]))
}

  const loadEarnings = () => {
    vendors.getMyEarnings().then(r => {
      setEarnings(r.data)
      setIban(r.data.vendor?.ibanNumber || '')
    }).catch(() => {})
  }

  useEffect(() => { loadStore(); loadEarnings(); loadDocs() }, []) // eslint-disable-line

  // Load this vendor's orders when the Orders tab is opened
  useEffect(() => {
    if (tab === 'orders') {
      setOrdersLoading(true)
      orders.vendorOrders()
        .then(r => { setVendorOrders(r.data || []); setOrdersLoading(false) })
        .catch(() => setOrdersLoading(false))
    }
  }, [tab])

  useEffect(() => {
    products.getCategories().then(r => setCategories(r.data)).catch(() => {})
  }, [])

  const handleImageChange = (e) => {
    const file = e.target.files[0]
    if (!file) return
    setImageFile(file)
    setImagePreview(URL.createObjectURL(file))
  }

  const resetImageFields = () => {
    setImageFile(null); setImagePreview(null); setImageUrl(''); setImageTab('upload')
  }

  const handleSaveIban = async () => {
    try {
      await vendors.updateIban({ ibanNumber: iban })
      setIbanMsg(t('ibanSaved'))
      setTimeout(() => setIbanMsg(''), 3000)
    } catch { setIbanMsg(t('ibanFailed')) }
  }

  const handleUploadDoc = async () => {
    if (!docFile) { setDocMsg('Please select a file'); return }
    setDocUploading(true); setDocMsg('')
    try {
      const formData = new FormData()
      formData.append('document', docFile)
      formData.append('docType', docType)
      formData.append('docName', docFile.name)
      const res = await fetch(`${API_ORIGIN}/api/vendors/documents/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: formData
      })
      if (!res.ok) throw new Error('Upload failed')
      setDocMsg('Document uploaded successfully!')
      setDocFile(null)
      loadDocs()
    } catch (err) { setDocMsg('Upload failed. Please try again.') }
    setDocUploading(false)
  }

  const handleSaveBankDetails = async () => {
    try {
      const res = await fetch(`${API_ORIGIN}/api/vendors/bank-details`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify(bankForm)
      })
      if (!res.ok) throw new Error('Failed')
      setBankMsg('Bank details saved successfully!')
      setTimeout(() => setBankMsg(''), 3000)
    } catch { setBankMsg('Failed to save bank details.') }
  }

  const handleAddProduct = async () => {
    if (!form.name || !form.price || !form.stockQty) { setError(t('productRequired')); return }
    setSaving(true); setError(''); setMessage('')
    try {
      let finalImageUrl = null
      if (imageTab === 'upload' && imageFile) {
        const formData = new FormData()
        formData.append('image', imageFile)
        const imgRes = await vendors.uploadImage(formData)
        finalImageUrl = imgRes.data.imageUrl
      } else if (imageTab === 'url' && imageUrl) {
        finalImageUrl = imageUrl
      }
      if (editingId) {
        await products.update(editingId, { ...form, price: parseFloat(form.price), stockQty: parseInt(form.stockQty), isActive: true, ...(finalImageUrl && { images: [finalImageUrl] }) })
        setMessage(t('productUpdated'))
      } else {
        await products.create({ ...form, price: parseFloat(form.price), stockQty: parseInt(form.stockQty), images: finalImageUrl ? [finalImageUrl] : [] })
        setMessage(t('productAdded'))
      }
      setForm({ name: '', description: '', price: '', stockQty: '', categoryId: '', condition: 'NEW' })
      resetImageFields(); setEditingId(null); loadStore(); setTab('products')
    } catch (err) { setError(err.response?.data?.error || t('saveFailed')) }
    setSaving(false)
  }

  const handleEdit = (product) => {
    setForm({ name: product.name, description: product.description, price: String(product.price), stockQty: String(product.stockQty), categoryId: product.categoryId || '', condition: product.condition || 'NEW' })
    setEditingId(product.id); setTab('add'); setMessage(''); setError('')
  }

  const handleDelete = async (id) => {
    if (!window.confirm(t('removeProductConfirm'))) return
    await products.remove(id); loadStore()
  }

  const handleBulkUpload = async () => {
    if (!excelFile) { setError(t('selectExcel')); return }
    setSaving(true); setError(''); setMessage('')
    try {
      const formData = new FormData()
      formData.append('excel', excelFile)
      const res = await vendors.bulkUpload(formData)
      setMessage(res.data.message)
      setExcelFile(null); loadStore()
    } catch (err) { setError(err.response?.data?.error || t('bulkFailed')) }
    setSaving(false)
  }

  if (loading) return <p style={{padding:40}}>{t('loading')}</p>

  // Show pending approval notice
if (false && store && store.status === 'PENDING') return (
    <div style={{...styles.page, maxWidth:600, textAlign:'center', paddingTop:80}}>
      <div style={{fontSize:64, marginBottom:24}}>⏳</div>
      <h2 style={{fontSize:24, fontWeight:700, marginBottom:12, color:'#0f1923'}}>{t('pendingApproval')}</h2>
      <p style={{color:'#666', fontSize:16, marginBottom:16}}>{t('pendingApprovalMsg')}</p>
      <div style={{background:'#fff7ed', border:'1px solid #f97316', borderRadius:12, padding:20, fontSize:14, color:'#92400e'}}>
        {t('subscriptionInfo')}
      </div>
    </div>
  )

  if (store && store.status === 'REJECTED') return (
    <div style={{...styles.page, maxWidth:600, textAlign:'center', paddingTop:80}}>
      <div style={{fontSize:64, marginBottom:24}}>❌</div>
      <h2 style={{fontSize:24, fontWeight:700, marginBottom:12, color:'#ef4444'}}>{t('storeRejected')}</h2>
      <p style={{color:'#666', marginBottom:12}}>{t('storeRejectedMsg')}</p>
      {store.rejectionNote && <p style={{background:'#fee2e2', borderRadius:8, padding:12, color:'#991b1b', fontSize:14}}>{store.rejectionNote}</p>}
    </div>
  )

  if (!store) return (
    <div style={{...styles.page, maxWidth:500}}>
      <h2 style={{marginBottom:24}}>{t('createStore')}</h2>
      <CreateStore
  onCreated={(nextTab) => {
    loadStore()
    if (nextTab) setTab(nextTab)
  }}
  t={t}
/>
    </div>
)


  const tabBtn = (key, label) => (
    <button onClick={() => { setTab(key); setMessage(''); setError('') }}
      style={{padding:'10px 24px', borderRadius:8, border:'none', cursor:'pointer', fontWeight:600, fontSize:14,
        background: tab === key ? '#f97316' : '#f8f9fa', color: tab === key ? '#fff' : '#333'}}>
      {label}
    </button>
  )

  return (
    <div style={styles.page}>
      {store && store.monthlyFeePaid === false && (
  <div style={{
    background:'#fff7ed',
    border:'1px solid #fdba74',
    color:'#9a3412',
    padding:'16px 20px',
    borderRadius:12,
    marginBottom:24,
    fontWeight:600
  }}>
    ⚠️ Monthly vendor fee of QAR 250 is pending. Please pay before the 1st of next month to avoid store interruption.
  </div>
)}
      <div style={{background:'linear-gradient(135deg, #0f1923 0%, #1e3a5f 100%)', borderRadius:16, padding:32, marginBottom:32, color:'#fff'}}>
        <h2 style={{fontSize:28, marginBottom:8}}>🏪 {store.storeName}</h2>
        <p style={{color:'#94a3b8'}}>{store.description}</p>
        <div style={{display:'flex', gap:24, marginTop:20}}>
          <div style={{background:'rgba(255,255,255,0.1)', padding:'12px 20px', borderRadius:10}}>
            <p style={{fontSize:24, fontWeight:700}}>{store.products?.length || 0}</p>
            <p style={{fontSize:12, color:'#94a3b8'}}>{t('products')}</p>
          </div>
          {earnings && (
            <div style={{background:'rgba(255,255,255,0.1)', padding:'12px 20px', borderRadius:10}}>
              <p style={{fontSize:24, fontWeight:700}}>{formatQAR(earnings.summary?.pendingPayout || 0)}</p>
              <p style={{fontSize:12, color:'#94a3b8'}}>{t('pendingPayout')}</p>
            </div>
          )}
        </div>
      </div>

      <div style={{display:'flex', gap:12, marginBottom:24, flexWrap:'wrap'}}>
        {tabBtn('products', t('myProducts'))}
        {tabBtn('orders', 'Orders')}
        {tabBtn('add', editingId ? t('editProduct') : t('addProduct'))}
        {tabBtn('bulk', t('bulkUpload'))}
        {tabBtn('documents', t('myDocuments'))}
        {tabBtn('banking', t('bankingDetails'))}
        {tabBtn('earnings', t('myEarnings'))}
        {tabBtn('subscription', 'Subscription')}
      </div>

      {message && <div style={{background:'#d1fae5', color:'#065f46', padding:'12px 16px', borderRadius:8, marginBottom:16}}>{message}</div>}
      {error && <div style={{background:'#fee2e2', color:'#991b1b', padding:'12px 16px', borderRadius:8, marginBottom:16}}>{error}</div>}

      {tab === 'products' && (
        <div>
          {store.products?.length === 0 ? (
            <div style={{textAlign:'center', padding:60, background:'#f8f9fa', borderRadius:12}}>
              <p style={{color:'#666', marginBottom:20}}>{t('noProducts')}</p>
              <button onClick={() => setTab('add')} style={styles.submitBtn}>{t('addFirstProduct')}</button>
            </div>
          ) : (
            <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(280px, 1fr))', gap:16}}>
              {store.products.map(p => {
                const imgSrc = getProductImageSrc(p)
                const cond = conditionLabel(p.condition)
                return (
                  <div key={p.id} style={{border:'1px solid #eee', borderRadius:12, overflow:'hidden', background:'#fff'}}>
                    <div style={{height:140, background:'#f8f9fa', display:'flex', alignItems:'center', justifyContent:'center', fontSize:48, overflow:'hidden'}}>
                      <img src={imgSrc || FALLBACK_IMG} alt={p.name} style={{width:'100%', height:'100%', objectFit:'contain'}} onError={(e) => { if (e.currentTarget.src !== FALLBACK_IMG) e.currentTarget.src = FALLBACK_IMG }} />
                    </div>
                    <div style={{padding:16}}>
                      <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:4}}>
                        <h3 style={{fontSize:15, fontWeight:600}}>{p.name}</h3>
                        <span style={{padding:'2px 8px', borderRadius:4, fontSize:11, fontWeight:600, background: cond.bg, color: cond.color}}>{cond.text}</span>
                      </div>
                      <div style={{marginBottom:12}}>
  {p.compareAtPrice && p.compareAtPrice > p.price && (
    <div style={{
      fontSize:13,
      color:'#888',
      textDecoration:'line-through',
      marginBottom:4
    }}>
      {formatQAR(p.compareAtPrice)}
    </div>
  )}

  <div style={{display:'flex',alignItems:'center',gap:8}}>
    <p style={{
      color:'#f97316',
      fontWeight:700,
      margin:0
    }}>
      {formatQAR(p.price)}
    </p>

    {p.compareAtPrice && p.compareAtPrice > p.price && (
      <span style={{
        background:'#dcfce7',
        color:'#166534',
        padding:'2px 8px',
        borderRadius:20,
        fontSize:11,
        fontWeight:700
      }}>
        {Math.round(((p.compareAtPrice - p.price) / p.compareAtPrice) * 100)}% OFF
      </span>
    )}
  </div>
</div>
                      <p style={{color:'#666', fontSize:13, marginBottom:12}}>{t('stock')}: {p.stockQty}</p>
                      <div style={{display:'flex', gap:8}}>
                        <button onClick={() => handleEdit(p)} style={{flex:1, padding:'8px', background:'#1e3a5f', color:'#fff', border:'none', borderRadius:8, cursor:'pointer', fontSize:13}}>{t('edit')}</button>
                        <button onClick={() => handleDelete(p.id)} style={{flex:1, padding:'8px', background:'#fee2e2', color:'#ef4444', border:'none', borderRadius:8, cursor:'pointer', fontSize:13}}>{t('remove')}</button>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}

      {tab === 'orders' && (
        <div>
          <h3 style={{marginBottom:16}}>Orders for your products</h3>
          {ordersLoading ? (
            <p style={{color:'#64748b'}}>Loading orders…</p>
          ) : vendorOrders.length === 0 ? (
            <div style={{background:'#fff', borderRadius:12, padding:'40px 20px', textAlign:'center', color:'#64748b'}}>
              No orders yet. When a customer buys one of your products, it will appear here.
            </div>
          ) : (
            <div style={{display:'flex', flexDirection:'column', gap:12}}>
              {vendorOrders.map(o => {
                const statusColor = { PENDING:'#f97316', CONFIRMED:'#3b82f6', SHIPPED:'#8b5cf6', DELIVERED:'#10b981', CANCELLED:'#ef4444' }
                const addr = o.shippingAddress || {}
                return (
                  <div key={o.id} style={{background:'#fff', border:'1px solid #eef0f3', borderRadius:12, padding:18}}>
                    <div style={{display:'flex', justifyContent:'space-between', flexWrap:'wrap', gap:8, marginBottom:10}}>
                      <div>
                        <div style={{fontWeight:700, color:'#0f1923'}}>Order #{o.id.slice(0,8).toUpperCase()}</div>
                        <div style={{fontSize:12, color:'#94a3b8'}}>{new Date(o.createdAt).toLocaleString()}</div>
                      </div>
                      <div style={{textAlign:'right'}}>
                        <span style={{fontWeight:700, color: statusColor[o.status] || '#64748b'}}>{o.status}</span>
                        <div style={{fontSize:12, marginTop:2}}>
                          {o.paid
                            ? <span style={{color:'#16a34a', fontWeight:600}}>💳 Paid</span>
                            : <span style={{color:'#92400e', fontWeight:600}}>💵 Cash on Delivery</span>}
                        </div>
                      </div>
                    </div>
                    <div style={{borderTop:'1px solid #f1f5f9', paddingTop:10}}>
                      {o.items.map((it, idx) => (
                        <div key={idx} style={{display:'flex', justifyContent:'space-between', fontSize:14, marginBottom:4, color:'#0f1923'}}>
                          <span>{it.name} × {it.quantity}</span>
                          <span style={{fontWeight:600}}>{formatQAR(it.unitPrice * it.quantity)}</span>
                        </div>
                      ))}
                      <div style={{display:'flex', justifyContent:'space-between', marginTop:8, paddingTop:8, borderTop:'1px dashed #e2e8f0'}}>
                        <span style={{color:'#64748b', fontSize:13}}>Customer: {o.customerName}</span>
                        <span style={{fontWeight:700, color:'#f97316'}}>{formatQAR(o.vendorTotal)}</span>
                      </div>
                    </div>
                    <div style={{marginTop:10, padding:10, background:'#f8fafc', borderRadius:8, fontSize:12, color:'#475569', lineHeight:1.6}}>
                      📍 {[
                        addr.zone && `Zone ${addr.zone}`,
                        addr.street && `Street ${addr.street}`,
                        addr.building && `Building ${addr.building}`,
                        addr.unit && `Unit ${addr.unit}`
                      ].filter(Boolean).join(', ')}
                      {addr.city ? `, ${addr.city}` : ''}
                      {addr.phone ? ` · 📞 ${addr.phone}` : ''}
                      {addr.preferredTime && addr.preferredTime !== 'any' ? ` · ⏰ ${addr.preferredTime}` : ''}
                      {addr.deliveryNotes ? ` · 📝 ${addr.deliveryNotes}` : ''}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}

      {tab === 'add' && (
        <div style={{maxWidth:600}}>
          <h3 style={{marginBottom:24}}>{editingId ? t('editProduct') : t('addNewProduct')}</h3>
          <input style={styles.input} placeholder={t('productName')} value={form.name} onChange={e => setForm({...form, name: e.target.value})} />
          <textarea style={{...styles.input, height:100, resize:'vertical'}} placeholder={t('description')} value={form.description} onChange={e => setForm({...form, description: e.target.value})} />
          <div style={{display:'flex', gap:12}}>
            <input style={{...styles.input, flex:1}} placeholder={t('priceQAR')} type="number" value={form.price} onChange={e => setForm({...form, price: e.target.value})} />
            <input style={{...styles.input, flex:1}} placeholder={t('stockQty')} type="number" value={form.stockQty} onChange={e => setForm({...form, stockQty: e.target.value})} />
          </div>
          <select style={styles.input} value={form.categoryId || ''} onChange={e => setForm({...form, categoryId: e.target.value})}>
            <option value="">{t('selectCategory')}</option>
            {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select style={styles.input} value={form.condition} onChange={e => setForm({...form, condition: e.target.value})}>
            <option value="NEW">{t('conditionNew')}</option>
            <option value="LIKE_NEW">{t('conditionLikeNew')}</option>
            <option value="GOOD">{t('conditionGood')}</option>
            <option value="FAIR">{t('conditionFair')}</option>
          </select>
          <div style={{marginBottom:12}}>
            <p style={{fontWeight:600, marginBottom:8, fontSize:14}}>{t('productImage')}</p>
            <div style={{display:'flex', gap:8, marginBottom:12}}>
              <button onClick={() => setImageTab('upload')} style={{flex:1, padding:'8px', borderRadius:8, border:'none', cursor:'pointer', background: imageTab === 'upload' ? '#f97316' : '#f8f9fa', color: imageTab === 'upload' ? '#fff' : '#333', fontWeight:600}}>{t('uploadFile')}</button>
              <button onClick={() => setImageTab('url')} style={{flex:1, padding:'8px', borderRadius:8, border:'none', cursor:'pointer', background: imageTab === 'url' ? '#f97316' : '#f8f9fa', color: imageTab === 'url' ? '#fff' : '#333', fontWeight:600}}>{t('imageUrl')}</button>
            </div>
            {imageTab === 'upload' ? (
              <div style={{border:'2px dashed #ddd', borderRadius:12, padding:24, textAlign:'center', cursor:'pointer'}} onClick={() => document.getElementById('imgInput').click()}>
                {imagePreview ? <img src={imagePreview} alt="preview" style={{maxHeight:150, maxWidth:'100%', borderRadius:8}} /> : <p style={{color:'#666'}}>{t('clickToUpload')}</p>}
                <input id="imgInput" type="file" accept="image/*" style={{display:'none'}} onChange={handleImageChange} />
              </div>
            ) : (
              <div>
                <input style={styles.input} placeholder={t('pasteImageUrl')} value={imageUrl} onChange={e => { setImageUrl(e.target.value); setImagePreview(e.target.value) }} />
                {imageUrl && <img src={imageUrl} alt="preview" style={{maxHeight:150, maxWidth:'100%', borderRadius:8}} onError={e => { e.target.style.display='none' }} />}
              </div>
            )}
            {(imagePreview || imageUrl) && <button onClick={resetImageFields} style={{background:'none', border:'none', color:'#ef4444', cursor:'pointer', marginTop:8}}>{t('removeImage')}</button>}
          </div>
          <div style={{display:'flex', gap:12}}>
            <button onClick={handleAddProduct} disabled={saving} style={{...styles.submitBtn, flex:1, opacity: saving ? 0.7 : 1}}>
              {saving ? t('saving') : editingId ? t('updateProduct') : t('addProduct')}
            </button>
            {editingId && <button onClick={() => { setEditingId(null); setForm({ name:'', description:'', price:'', stockQty:'', categoryId:'', condition:'NEW' }); resetImageFields(); setTab('products') }} style={{...styles.submitBtn, flex:1, background:'#6b7280'}}>{t('cancel')}</button>}
          </div>
        </div>
      )}

      {tab === 'bulk' && (
        <div style={{maxWidth:600}}>
          <h3 style={{marginBottom:8}}>{t('bulkUpload')}</h3>
          <p style={{color:'#666', marginBottom:24}}>{t('bulkColumns')}</p>
          <div style={{border:'2px dashed #ddd', borderRadius:12, padding:40, textAlign:'center', marginBottom:16, cursor:'pointer'}} onClick={() => document.getElementById('excelInput').click()}>
            {excelFile ? <p style={{color:'#065f46', fontWeight:600}}>{excelFile.name}</p> : <p style={{color:'#666'}}>{t('selectExcelFile')}</p>}
            <input id="excelInput" type="file" accept=".xlsx,.xls" style={{display:'none'}} onChange={e => setExcelFile(e.target.files[0])} />
          </div>
          <button onClick={handleBulkUpload} disabled={saving || !excelFile} style={{...styles.submitBtn, opacity: saving || !excelFile ? 0.7 : 1}}>
            {saving ? t('uploading') : t('uploadProducts')}
          </button>
        </div>
      )}

      {tab === 'documents' && (
        <div style={{maxWidth:700}}>
          <h3 style={{marginBottom:8, fontSize:18, fontWeight:700}}>{t('myDocuments')}</h3>
          <p style={{color:'#888', fontSize:14, marginBottom:24}}>{t('documentsNote')}</p>

          {/* Upload Section */}
          <div style={{background:'#fff', border:'1px solid #eee', borderRadius:12, padding:24, marginBottom:24}}>
            <h4 style={{marginBottom:16, fontWeight:600}}>{t('uploadDocument')}</h4>
            {docMsg && <div style={{padding:'10px 14px', borderRadius:8, marginBottom:16, background: docMsg.includes('success') ? '#d1fae5' : '#fee2e2', color: docMsg.includes('success') ? '#065f46' : '#991b1b', fontSize:14}}>{docMsg}</div>}
            <select style={styles.input} value={docType} onChange={e => setDocType(e.target.value)}>
              <option value="CR_COPY">{t('crCopy')}</option>
              <option value="TRADE_LICENSE">{t('tradeLicense')}</option>
              <option value="SIGNATORY_QID">{t('signatoryQID')}</option>
              <option value="OTHER">{t('otherDoc')}</option>
            </select>
            <div style={{border:'2px dashed #ddd', borderRadius:12, padding:24, textAlign:'center', cursor:'pointer', marginBottom:12}}
              onClick={() => document.getElementById('docInput').click()}>
              {docFile
                ? <p style={{color:'#065f46', fontWeight:600}}>📎 {docFile.name}</p>
                : <div><p style={{fontSize:32, marginBottom:8}}>📄</p><p style={{color:'#888'}}>{t('clickToUploadDoc')}</p><p style={{color:'#aaa', fontSize:12, marginTop:4}}>PDF, JPG, PNG — max 10MB</p></div>
              }
              <input id="docInput" type="file" accept=".pdf,.jpg,.jpeg,.png" style={{display:'none'}} onChange={e => setDocFile(e.target.files[0])} />
            </div>
            <button onClick={handleUploadDoc} disabled={docUploading || !docFile}
              style={{...styles.submitBtn, opacity: docUploading || !docFile ? 0.7 : 1}}>
              {docUploading ? t('uploading') : t('uploadDocument')}
            </button>
          </div>

          {/* Uploaded Documents */}
          <div style={{background:'#fff', border:'1px solid #eee', borderRadius:12, padding:24}}>
            <h4 style={{marginBottom:16, fontWeight:600}}>{t('uploadedDocuments')}</h4>
            {docs.length === 0 ? (
              <p style={{color:'#888', textAlign:'center', padding:24}}>{t('noDocuments')}</p>
            ) : docs.map(doc => (
              <div key={doc.id} style={{display:'flex', justifyContent:'space-between', alignItems:'center', padding:'12px 0', borderBottom:'1px solid #f3f3f3'}}>
                <div style={{display:'flex', alignItems:'center', gap:12}}>
                  <span style={{fontSize:24}}>📄</span>
                  <div>
                    <p style={{fontWeight:600, fontSize:14}}>{doc.docName}</p>
                    <p style={{color:'#888', fontSize:12}}>{doc.docType.replace('_', ' ')} · {new Date(doc.uploadedAt).toLocaleDateString()}</p>
                  </div>
                </div>
                <div style={{display:'flex', alignItems:'center', gap:10}}>
                  <span style={{padding:'3px 10px', borderRadius:20, fontSize:12, fontWeight:600,
                    background: doc.verified ? '#d1fae5' : '#fff7ed',
                    color: doc.verified ? '#065f46' : '#92400e'}}>
                    {doc.verified ? '✓ Verified' : '⏳ Pending'}
                  </span>
                  <a href={doc.docUrl} target="_blank" rel="noopener noreferrer"
                    style={{color:'#f97316', fontSize:13, textDecoration:'none', fontWeight:600}}>View →</a>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === 'banking' && (
        <div style={{maxWidth:600}}>
          <h3 style={{marginBottom:8, fontSize:18, fontWeight:700}}>{t('bankingDetails')}</h3>
          <p style={{color:'#888', fontSize:14, marginBottom:24}}>{t('bankingNote')}</p>
          <div style={{background:'#fff', border:'1px solid #eee', borderRadius:12, padding:24}}>
            {bankMsg && <div style={{padding:'10px 14px', borderRadius:8, marginBottom:16, background: bankMsg.includes('success') ? '#d1fae5' : '#fee2e2', color: bankMsg.includes('success') ? '#065f46' : '#991b1b', fontSize:14}}>{bankMsg}</div>}
            <div style={{display:'grid', gap:0}}>
              <label style={{fontSize:13, fontWeight:600, color:'#555', marginBottom:4}}>{t('accountHolderName')} *</label>
              <input style={styles.input} placeholder="e.g. Mohammed Al-Rashidi" value={bankForm.accountHolderName} onChange={e => setBankForm({...bankForm, accountHolderName: e.target.value})} />
              <label style={{fontSize:13, fontWeight:600, color:'#555', marginBottom:4}}>{t('bankName')} *</label>
              <input style={styles.input} placeholder="e.g. Qatar National Bank (QNB)" value={bankForm.bankName} onChange={e => setBankForm({...bankForm, bankName: e.target.value})} />
              <label style={{fontSize:13, fontWeight:600, color:'#555', marginBottom:4}}>{t('ibanNumber')} *</label>
              <input style={styles.input} placeholder="QA57 DOHB 0000 1234 5678 90AB CDEF" value={bankForm.ibanNumber} onChange={e => setBankForm({...bankForm, ibanNumber: e.target.value})} />
              <label style={{fontSize:13, fontWeight:600, color:'#555', marginBottom:4}}>{t('accountNumber')}</label>
              <input style={styles.input} placeholder="Account number" value={bankForm.accountNumber} onChange={e => setBankForm({...bankForm, accountNumber: e.target.value})} />
              <label style={{fontSize:13, fontWeight:600, color:'#555', marginBottom:4}}>{t('bankBranch')}</label>
              <input style={styles.input} placeholder="e.g. West Bay Branch, Doha" value={bankForm.bankBranch} onChange={e => setBankForm({...bankForm, bankBranch: e.target.value})} />
            </div>
            <button onClick={handleSaveBankDetails} style={styles.submitBtn}>{t('saveBankDetails')}</button>
          </div>
          <div style={{background:'#fff7ed', border:'1px solid #f97316', borderRadius:10, padding:14, marginTop:16, fontSize:13, color:'#92400e'}}>
            🔒 {t('bankingSecurityNote')}
          </div>
        </div>
      )}

{tab === 'subscription' && (
  <div style={{
    background:'#fff',
    borderRadius:16,
    padding:24,
    boxShadow:'0 4px 12px rgba(0,0,0,0.06)'
  }}>
    
    <h2 style={{ marginBottom:20 }}>Subscription Plan</h2>

    {/* Current subscription status banner */}
    <div style={{
      display:'flex', alignItems:'center', gap:10, marginBottom:20,
      padding:'12px 16px', borderRadius:12,
      background: store?.subscriptionStatus === 'ACTIVE' ? '#dcfce7' : '#fef3c7',
      border: store?.subscriptionStatus === 'ACTIVE' ? '1px solid #86efac' : '1px solid #fcd34d'
    }}>
      <span style={{ fontSize:20 }}>{store?.subscriptionStatus === 'ACTIVE' ? '✅' : '⚠️'}</span>
      <div>
        <div style={{ fontWeight:700, color: store?.subscriptionStatus === 'ACTIVE' ? '#166534' : '#92400e' }}>
          {store?.subscriptionStatus === 'ACTIVE'
            ? (language === 'AR' ? 'اشتراكك نشط' : 'Your subscription is active')
            : (language === 'AR' ? 'اشتراكك غير مفعّل' : 'Your subscription is not active')}
        </div>
        {store?.subscriptionExpiry && store?.subscriptionStatus === 'ACTIVE' && (
          <div style={{ fontSize:12, color:'#166534' }}>
            {language === 'AR' ? 'ينتهي في: ' : 'Expires: '}
            {new Date(store.subscriptionExpiry).toLocaleDateString()}
          </div>
        )}
      </div>
    </div>

    <div style={{
      display:'grid',
      gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))',
      gap:16,
      marginBottom:24
    }}>
      
      <div style={{
        border:'2px solid #f97316',
        borderRadius:14,
        padding:20
      }}>
        <h3>Monthly Plan</h3>
        <p style={{ fontSize:28, fontWeight:700 }}>QAR 1,000</p>
        <div style={{ marginTop:8, color:'#666' }}>
        <div>One-time Registration Fee</div>
        <div>QAR 250 monthly recurring fee</div>
        <div>Due between 1st-7th every month</div>
        </div>


        <button
          onClick={async () => {
            try {
              setMessage(''); setError('')
              const r = await payments.vendorSubscription({
                amount: 5, // KWD test amount (sandbox). Change to real amount for QAR go-live.
                vendorId: store.id,
                subscriptionType: 'MONTHLY'
              })
              if (r.data.paymentUrl) {
                window.location = r.data.paymentUrl
              } else {
                setError('Could not start payment. Please try again.')
              }
            } catch (e) {
              setError('Payment could not be started. Please try again.')
            }
          }}
          style={{
            marginTop:16,
            width:'100%',
            padding:'12px',
            border:'none',
            borderRadius:10,
            background:'#f97316',
            color:'#fff',
            fontWeight:700,
            cursor:'pointer'
          }}
        >
          {language === 'AR' ? 'اشترك شهرياً' : 'Pay Monthly'}
        </button>
      </div>

      <div style={{
        border:'2px solid #10b981',
        borderRadius:14,
        padding:20
      }}>
        <h3>Yearly Plan</h3>
        <p style={{ fontSize:28, fontWeight:700 }}>QAR 500</p>
        <div style={{ marginTop:8, color:'#666' }}>
        <div>Annual Renewal Fee</div>
        <div>Due between 1st-7th January every year</div>
        </div>

        <button
          onClick={async () => {
            try {
              setMessage(''); setError('')
              const r = await payments.vendorSubscription({
                amount: 5, // KWD test amount (sandbox). Change to real amount for QAR go-live.
                vendorId: store.id,
                subscriptionType: 'ANNUAL'
              })
              if (r.data.paymentUrl) {
                window.location = r.data.paymentUrl
              } else {
                setError('Could not start payment. Please try again.')
              }
            } catch (e) {
              setError('Payment could not be started. Please try again.')
            }
          }}
          style={{
            marginTop:16,
            width:'100%',
            padding:'12px',
            border:'none',
            borderRadius:10,
            background:'#10b981',
            color:'#fff',
            fontWeight:700,
            cursor:'pointer'
          }}
        >
          {language === 'AR' ? 'اشترك سنوياً' : 'Pay Yearly'}
        </button>
      </div>

    </div>

    <div style={{
      background:'#fff7ed',
      padding:16,
      borderRadius:12,
      border:'1px solid #fdba74'
    }}>
      Admin can apply:
      <ul>
        <li>0% discount</li>
        <li>25% discount</li>
        <li>50% discount</li>
        <li>100% free onboarding</li>
      </ul>
    </div>

  </div>
)}
      {tab === 'earnings' && (
        <div>
          {earnings ? (
            <>
              <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(180px, 1fr))', gap:16, marginBottom:32}}>
                {[
                  { label: t('totalSales'), value: formatQAR(earnings.summary?.totalSales || 0), icon:'🛒', color:'#1e3a5f' },

{
    label:'Platform Subscription',
    value: store.enrollmentPaid ? 'Paid' : 'Unpaid',
    icon:'🏦',
    color: store.enrollmentPaid ? '#10b981' : '#ef4444'
  },

  {
    label:'Present Monthly Fee',
    value: store.monthlyFeePaid ? 'Paid' : 'Unpaid',
    icon:'📅',
    color: store.monthlyFeePaid ? '#10b981' : '#ef4444'
  },

  {
    label:'AMC Renewal Fee',
    value: store.annualRenewalPaid ? 'Paid' : 'Not Due',
    icon:'🔁',
    color: store.annualRenewalPaid ? '#10b981' : '#f97316'
  },

  {
    label: t('yourEarnings'),
    value: formatQAR(earnings.summary?.totalEarnings || 0),
    icon:'💰',
    color:'#10b981'
  },

  {
    label: t('totalPaid'),
    value: formatQAR(earnings.summary?.totalPaid || 0),
    icon:'✅',
    color:'#3b82f6'
  },

  {
    label: t('pendingPayout'),
    value: formatQAR(earnings.summary?.pendingPayout || 0),
    icon:'⏳',
    color:'#ef4444'
  }
]
                .map(stat => (
                  <div key={stat.label} style={{background:'#fff', border:'1px solid #eee', borderRadius:12, padding:20}}>
                    <p style={{fontSize:28, marginBottom:8}}>{stat.icon}</p>
                    <p style={{fontSize:20, fontWeight:700, color: stat.color}}>{stat.value}</p>
                    <p style={{color:'#666', fontSize:13}}>{stat.label}</p>
                  </div>
                ))}
              </div>
              <div style={{background:'#f8f9fa', borderRadius:12, padding:20, marginBottom:24}}>
                <h3 style={{marginBottom:12, fontSize:16}}>{t('yourBankIban')}</h3>
                <div style={{display:'flex', gap:12}}>
                  <input style={{...styles.input, flex:1, marginBottom:0}} placeholder="QA57DOHB..." value={iban} onChange={e => setIban(e.target.value)} />
                  <button onClick={handleSaveIban} style={{...styles.submitBtn, width:'auto', padding:'12px 20px'}}>{t('save')}</button>
                </div>
                {ibanMsg && <p style={{color:'#10b981', marginTop:8, fontSize:13}}>{ibanMsg}</p>}
              </div>
              <h3 style={{marginBottom:16}}>{t('orderHistory')}</h3>
              {earnings.orderItems?.length === 0 ? (
                <p style={{color:'#888', textAlign:'center', padding:32}}>{t('noOrders')}</p>
              ) : earnings.orderItems?.map(item => (
                <div key={item.id} style={{border:'1px solid #eee', borderRadius:10, padding:16, marginBottom:12, background:'#fff'}}>
                  <div style={{display:'flex', justifyContent:'space-between', flexWrap:'wrap', gap:8}}>
                    <div>
                      <p style={{fontWeight:600}}>{item.product?.name}</p>
                      <p style={{color:'#666', fontSize:13}}>{t('order')} #{item.order?.id?.slice(0,8)}... - {t('qty')}: {item.quantity}</p>
                    </div>
                    <div style={{textAlign:'right'}}>
                      <p style={{color:'#666', fontSize:13}}>{t('sale')}: {formatQAR(item.unitPrice * item.quantity)}</p>
                      <p style={{color:'#f97316', fontSize:13}}>{t('fee')}: -{formatQAR(item.platformFee)}</p>
                      <p style={{color:'#10b981', fontWeight:700}}>{t('youGet')}: {formatQAR(item.vendorEarning)}</p>
                    </div>
                  </div>
                </div>
              ))}
            </>
          ) : <p style={{padding:40, color:'#666'}}>{t('loading')}</p>}
        </div>
      )}
    </div>
  )
}

function CreateStore({ onCreated, t = (k) => k }) {
  const [form, setForm] = useState({ storeName: '', description: '' })
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)

  const handleCreate = async () => {
    if (!form.storeName) { setError(t('storeNameRequired')); return }
    setSaving(true)
    try {
      await vendors.createStore(form)
      onCreated('documents')
    } catch (err) {
      setError(err.response?.data?.error || t('createStoreFailed'))
      setSaving(false)
    }
  }

  return (
    <div>
      {error && <p style={styles.error}>{error}</p>}
      <input style={styles.input} placeholder={t('storeName')} value={form.storeName} onChange={e => setForm({...form, storeName: e.target.value})} />
      <textarea style={{...styles.input, height:100, resize:'vertical'}} placeholder={t('storeDescription')} value={form.description} onChange={e => setForm({...form, description: e.target.value})} />
      <button onClick={handleCreate} disabled={saving} style={{...styles.submitBtn, opacity: saving ? 0.7 : 1}}>
        {saving ? t('creating') : t('createMyStore')}
      </button>
    </div>
  )
}

function App() {
  const [language, setLanguage] = useState(localStorage.getItem('language') || 'EN')
  const t = (key) => translations[language][key] || key
  useEffect(() => {
    if (language === 'AR') {
      document.documentElement.setAttribute('dir', 'rtl')
      document.documentElement.setAttribute('lang', 'ar')
    } else {
      document.documentElement.setAttribute('dir', 'ltr')
      document.documentElement.setAttribute('lang', 'en')
    }
    localStorage.setItem('language', language)
  }, [language])

  // ── Deep link handler ────────────────────────────────────────
  // When MyFatoorah redirects to com.jasprmarket.app://payment-success?paymentId=XXXX
  // Android closes the browser and fires appUrlOpen — we verify the payment here.
  useEffect(() => {
    let listener = null
    const setupDeepLink = async () => {
      try {
        listener = await CapApp.addListener('appUrlOpen', async (event) => {
          const url = event.url || ''
          // Parse the deep link URL
          // e.g. com.jasprmarket.app://payment-success?paymentId=XXXX&orderId=YYY
          if (url.includes('payment-success')) {
            const queryString = url.split('?')[1] || ''
            const params = new URLSearchParams(queryString)
            const paymentId = params.get('paymentId')
            const orderId = params.get('orderId')

            if (paymentId) {
              try {
                const result = await payments.verify(paymentId)
                if (result.data?.success) {
                  // Navigate to order confirmation
                  const targetOrderId = orderId || result.data?.orderId
                  if (targetOrderId) {
                    window.location.href = `/orders/${targetOrderId}`
                  } else {
                    window.location.href = '/orders'
                  }
                } else {
                  window.location.href = '/orders'
                }
              } catch (e) {
                console.error('Deep link verify error:', e)
                window.location.href = '/orders'
              }
            }
          } else if (url.includes('payment-failed')) {
            // Payment failed — go back to orders so user can retry
            window.location.href = '/orders'
          }
        })
      } catch (e) {
        // CapApp not available in web browser — safe to ignore
        console.log('Deep link listener not available (web mode)')
      }
    }
    setupDeepLink()
    return () => { if (listener) listener.remove() }
  }, [])

  // Theme (light / dark)
  const [theme, setTheme] = useState(localStorage.getItem('theme') || 'light')
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme)
    localStorage.setItem('theme', theme)
  }, [theme])
  const toggleTheme = () => setTheme(theme === 'light' ? 'dark' : 'light')

  // Cart drawer
  const [cartOpen, setCartOpen] = useState(false)
  const [user, setUser] = useState(null)
  const [cartCount, setCartCount] = useState(0) // eslint-disable-line
  const [wishlist, setWishlist] = useState(
  JSON.parse(localStorage.getItem('wishlist') || '[]')
)

const toggleWishlist = (productId) => {
  setWishlist(prev =>
    prev.includes(productId)
      ? prev.filter(id => id !== productId)
      : [...prev, productId]
  )
}
useEffect(() => {
  localStorage.setItem('wishlist', JSON.stringify(wishlist))
}, [wishlist])

  useEffect(() => {
    const token = localStorage.getItem('token')
    if (token) {
      auth.me().then(r => setUser(r.data)).catch(() => localStorage.removeItem('token'))
    }
  }, [])

  useEffect(() => {
    if (user) {
      cart.get().then(r => {
        const total = r.data.items.reduce((sum, i) => sum + i.quantity, 0)
        setCartCount(total)
      }).catch(() => {})
    } else {
      setCartCount(0)
    }
  }, [user])

  // Listen for cart updates fired from Quick View (and anywhere else that
  // dispatches 'jaspr-cart-updated') so the navbar badge stays in sync.
  useEffect(() => {
    const handler = (e) => {
      if (typeof e.detail === 'number') setCartCount(e.detail)
    }
    window.addEventListener('jaspr-cart-updated', handler)
    return () => window.removeEventListener('jaspr-cart-updated', handler)
  }, [])

  const handleLogout = () => {
    localStorage.removeItem('token')
    setUser(null)
    window.location.href = '/'
  }

  return (
    <BrowserRouter>
    <ScrollToTop />
      <Navbar user={user} cartCount={cartCount} onLogout={handleLogout} language={language} setLanguage={setLanguage} t={t} theme={theme} toggleTheme={toggleTheme} onCartClick={() => setCartOpen(true)} />
      <CartDrawer open={cartOpen} onClose={() => setCartOpen(false)} t={t} onCartUpdate={setCartCount} language={language} />
      <Routes>
        <Route path="/" element={<Home t={t} language={language} />} />
        <Route path="/products" element={<Products t={t} language={language} wishlist={wishlist} toggleWishlist={toggleWishlist} /> } />
        <Route
  path="/wishlist"
  element={
    <Products
      t={t}
      language={language}
      wishlist={wishlist}
      toggleWishlist={toggleWishlist}
      wishlistOnly={true}
    />
  }
/>
        <Route path="/products/:id" element={<ProductDetail user={user} t={t} language={language} />} />
        <Route path="/login" element={<Login onLogin={setUser} t={t} />} />
        <Route path="/register" element={<Register onLogin={setUser} t={t} />} />
        <Route path="/profile" element={<Profile user={user} onLogout={() => setUser(null)} t={t} language={language} />} />
        <Route path="/cart" element={<Cart onCartUpdate={setCartCount} t={t} />} />
        <Route path="/payment-success" element={<PaymentSuccess t={t} language={language} />} />
        <Route path="/payment-failed" element={<PaymentFailed t={t} language={language} />} />
        <Route path="/checkout" element={<Checkout t={t} language={language} />} />
        <Route path="/orders" element={<Orders user={user} t={t} />} />
        <Route path="/orders/:id" element={<OrderDetail t={t} />} />
        <Route path="/vendor" element={<VendorDashboard t={t} language={language} />} />
        <Route path="/admin" element={<AdminDashboard t={t} />} />
        <Route path="/terms" element={<Terms t={t} language={language} />} />
        <Route path="/privacy" element={<Privacy t={t} language={language} />} />
        <Route path="/refund-policy" element={<RefundPolicy t={t} language={language} />} />
        <Route path="/shipping" element={<Shipping t={t} language={language} />} />
        <Route path="/contact" element={<Contact t={t} language={language} />} />
        <Route path="/cookie-policy" element={<CookiePolicy language={language} />} />
        <Route path="/vendor-policy" element={<VendorPolicy language={language} />} />
        <Route path="/account-deletion" element={<AccountDeletionPolicy language={language} />} />
      </Routes>
      <MobileBottomNav cartCount={cartCount} t={t} />
      <SiteFooter t={t} language={language} />
    </BrowserRouter>
  )
}

function MobileBottomNav({ cartCount = 0, t = (k) => k }) {
  return (
    <div
      style={{
        position:'fixed',
        bottom:0,
        left:0,
        right:0,
        height:68,
        background:'#0f1923',
        display:'flex',
        justifyContent:'space-around',
        alignItems:'center',
        zIndex:9999,
        borderTop:'1px solid rgba(255,255,255,0.08)',
        boxShadow:'0 -4px 20px rgba(0,0,0,0.12)'
      }}
    >
      <Link
        to="/"
        style={{
          color:'#fff',
          textDecoration:'none',
          fontSize:12,
          display:'flex',
          flexDirection:'column',
          alignItems:'center',
          gap:4
        }}
      >
        <span style={{fontSize:20}}>🏠</span>
        {t('home')}
      </Link>

      <Link
        to="/wishlist"
        style={{
          color:'#fff',
          textDecoration:'none',
          fontSize:12,
          display:'flex',
          flexDirection:'column',
          alignItems:'center',
          gap:4
        }}
      >
        <span style={{fontSize:20}}>❤️</span>
        {t('wishlist')}
      </Link>

      <Link
        to="/cart"
        style={{
          color:'#fff',
          textDecoration:'none',
          fontSize:12,
          display:'flex',
          flexDirection:'column',
          alignItems:'center',
          gap:4,
          position:'relative'
        }}
      >
        <span style={{fontSize:20}}>🛒</span>

        {cartCount > 0 && (
          <span
            style={{
              position:'absolute',
              top:-2,
              right:-10,
              background:'#f97316',
              color:'#fff',
              borderRadius:'50%',
              minWidth:18,
              height:18,
              fontSize:10,
              display:'flex',
              alignItems:'center',
              justifyContent:'center',
              fontWeight:700
            }}
          >
            {cartCount}
          </span>
        )}

        {t('cart')}
      </Link>

      <Link
        to="/orders"
        style={{
          color:'#fff',
          textDecoration:'none',
          fontSize:12,
          display:'flex',
          flexDirection:'column',
          alignItems:'center',
          gap:4
        }}
      >
        <span style={{fontSize:20}}>📦</span>
        {t('myOrders')}
      </Link>

      <Link
        to={localStorage.getItem('token') ? '/profile' : '/login'}
        style={{
          color:'#fff',
          textDecoration:'none',
          fontSize:12,
          display:'flex',
          flexDirection:'column',
          alignItems:'center',
          gap:4
        }}
      >
        <span style={{fontSize:20}}>👤</span>
        {t('account')}
      </Link>
    </div>
  )
}

function ProductSkeleton() {
  return (
    <div className="jm-card" style={{border:'1px solid #eef0f3', borderRadius:24, overflow:'hidden', background:'#fff', boxShadow:'0 10px 30px rgba(15,25,35,0.08)', display:'flex', flexDirection:'column', height:500}}>
      <div className="jm-skel" style={{height:210, borderRadius:0}} />
      <div style={{padding:'14px 16px', display:'flex', flexDirection:'column', flex:1, gap:10}}>
        <div className="jm-skel" style={{height:10, width:'40%'}} />
        <div className="jm-skel" style={{height:16, width:'85%'}} />
        <div className="jm-skel" style={{height:16, width:'70%'}} />
        <div style={{display:'flex', gap:6}}>
          <div className="jm-skel" style={{height:20, width:50, borderRadius:20}} />
          <div className="jm-skel" style={{height:20, width:80, borderRadius:20}} />
        </div>
        <div className="jm-skel" style={{height:12, width:'30%'}} />
        <div className="jm-skel" style={{height:24, width:'55%'}} />
        <div style={{marginTop:'auto', display:'flex', flexDirection:'column', gap:8}}>
          <div className="jm-skel" style={{height:36, borderRadius:12}} />
          <div className="jm-skel" style={{height:36, borderRadius:10}} />
        </div>
      </div>
    </div>
  )
}

function RecentlyViewedStrip({ items = [], t = (k) => k }) {
  const [ids, setIds] = useState([])
  useEffect(() => {
    try {
      const raw = localStorage.getItem('recentlyViewed') || '[]'
      setIds(JSON.parse(raw))
    } catch (e) { setIds([]) }
  }, [])
  // Match against the products already loaded; that way no extra API call
  // and we silently drop stale ids (deleted products, etc).
  const matched = ids.map(id => items.find(p => p.id === id)).filter(Boolean)
  if (matched.length === 0) return null
  return (
    <div className="jm-card" style={{marginBottom:24, padding:'16px 20px', background:'#fff', borderRadius:16, border:'1px solid #eef0f3', boxShadow:'0 4px 14px rgba(15,25,35,0.05)'}}>
      <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12}}>
        <h4 className="jm-card-title" style={{margin:0, fontSize:14, fontWeight:700, color:'#0f1923', textTransform:'uppercase', letterSpacing:0.5}}>
          {t('recentlyViewed')}
        </h4>
        <button
          onClick={() => { localStorage.removeItem('recentlyViewed'); setIds([]) }}
          style={{background:'none', border:'none', color:'#94a3b8', fontSize:12, cursor:'pointer'}}
          aria-label="Clear recently viewed"
        >
          {t('clear')}
        </button>
      </div>
      <div style={{display:'flex', gap:14, overflowX:'auto', paddingBottom:6}}>
        {matched.map(p => {
          const img = p.images?.[0]
          const src = getImageSrc(img)
          return (
            <Link
              key={p.id}
              to={`/products/${p.id}`}
              style={{flex:'0 0 110px', textDecoration:'none', color:'inherit'}}
            >
              <div className="jm-card-img" style={{width:110, height:110, borderRadius:12, background:'#f8fafc', border:'1px solid #eef0f3', display:'flex', alignItems:'center', justifyContent:'center', overflow:'hidden', marginBottom:6}}>
                {src ? (
                  <img src={src || FALLBACK_IMG} alt={p.name} style={{width:'100%', height:'100%', objectFit:'contain'}} onError={(e) => { if (e.currentTarget.src !== FALLBACK_IMG) e.currentTarget.src = FALLBACK_IMG }} />
                ) : (
                  <span style={{fontSize:36}}>📱</span>
                )}
              </div>
              <div className="jm-card-title" style={{fontSize:11, fontWeight:600, color:'#0f1923', lineHeight:1.3, overflow:'hidden', textOverflow:'ellipsis', display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical'}}>
                {p.name}
              </div>
              <div style={{fontSize:11, color:'#f97316', fontWeight:700, marginTop:2}}>
                {formatQAR(p.price)}
              </div>
            </Link>
          )
        })}
      </div>
    </div>
  )
}

function CartDrawer({ open, onClose, t = (k) => k, onCartUpdate, language = 'EN' }) {
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!open) return
    setLoading(true)
    cart.get()
      .then(r => { setItems(r.data.items || []); setLoading(false) })
      .catch(() => { setItems([]); setLoading(false) })
  }, [open])

  const updateQty = async (productId, newQty) => {
    if (newQty < 1) return
    try {
      await cart.update(productId, { quantity: newQty })
      const r = await cart.get()
      setItems(r.data.items || [])
      onCartUpdate && onCartUpdate(r.data.items.reduce((s, i) => s + i.quantity, 0))
    } catch (e) {}
  }

  const removeItem = async (productId) => {
    try {
      await cart.remove(productId)
      const r = await cart.get()
      setItems(r.data.items || [])
      onCartUpdate && onCartUpdate(r.data.items.reduce((s, i) => s + i.quantity, 0))
    } catch (e) {}
  }

  const total = items.reduce((s, i) => s + (i.product?.price || 0) * i.quantity, 0)
  const itemCount = items.reduce((s, i) => s + i.quantity, 0)
  const sideStyle = language === 'AR'
    ? { left: 0, transform: open ? 'translateX(0)' : 'translateX(-100%)', borderRight: '1px solid #eef0f3' }
    : { right: 0, transform: open ? 'translateX(0)' : 'translateX(100%)', borderLeft: '1px solid #eef0f3' }

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position:'fixed', inset:0, background:'rgba(15,25,35,0.45)',
          opacity: open ? 1 : 0, pointerEvents: open ? 'auto' : 'none',
          transition:'opacity 0.25s', zIndex: 9998
        }}
      />
      {/* Drawer */}
      <div
        style={{
          position:'fixed', top:0, height:'100dvh', width:380, maxWidth:'92vw',
          background:'#fff', boxShadow:'-12px 0 40px rgba(0,0,0,0.18)',
          transition:'transform 0.28s ease', zIndex:9999,
          display:'flex', flexDirection:'column', ...sideStyle
        }}
      >
        {/* Header */}
        <div style={{padding:'18px 20px', borderBottom:'1px solid #eef0f3', display:'flex', justifyContent:'space-between', alignItems:'center', flexShrink:0}}>
          <div>
            <div style={{fontSize:16, fontWeight:700, color:'#0f1923'}}>{t('yourCart')}</div>
            <div style={{fontSize:12, color:'#94a3b8'}}>{itemCount} {itemCount === 1 ? 'item' : 'items'}</div>
          </div>
          <button
            onClick={onClose}
            style={{background:'none', border:'none', fontSize:24, cursor:'pointer', color:'#64748b', padding:4, lineHeight:1}}
            aria-label="Close cart"
          >×</button>
        </div>

        {/* Items */}
        <div style={{flex:1, overflowY:'auto', padding:'12px 20px'}}>
          {loading ? (
            <div style={{padding:'40px 0', textAlign:'center', color:'#94a3b8', fontSize:13}}>{t('loading')}</div>
          ) : items.length === 0 ? (
            <div style={{padding:'60px 20px', textAlign:'center'}}>
              <div style={{fontSize:48, marginBottom:12}}>🛒</div>
              <div style={{fontSize:15, fontWeight:600, color:'#0f1923', marginBottom:6}}>{t('cartEmpty')}</div>
              <div style={{fontSize:12, color:'#94a3b8'}}>Browse products to add something here.</div>
            </div>
          ) : items.map(i => {
            const img = i.product?.images?.[0]
            const src = getImageSrc(img)
            return (
              <div key={i.productId || i.product?.id} style={{display:'flex', gap:12, padding:'12px 0', borderBottom:'1px solid #f1f5f9'}}>
                <div className="jm-card-img" style={{width:64, height:64, borderRadius:10, background:'#f8fafc', display:'flex', alignItems:'center', justifyContent:'center', overflow:'hidden', flexShrink:0}}>
                  <img src={src || FALLBACK_IMG} alt="" style={{width:'100%', height:'100%', objectFit:'contain'}} onError={(e) => { if (e.currentTarget.src !== FALLBACK_IMG) e.currentTarget.src = FALLBACK_IMG }} />
                </div>
                <div style={{flex:1, minWidth:0}}>
                  <div style={{fontSize:13, fontWeight:600, color:'#0f1923', marginBottom:4, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>
                    {i.product?.name}
                  </div>
                  <div style={{fontSize:13, fontWeight:700, color:'#f97316', marginBottom:6}}>{formatQAR(i.product?.price || 0)}</div>
                  <div style={{display:'flex', alignItems:'center', gap:6}}>
                    <button onClick={() => updateQty(i.productId || i.product?.id, i.quantity - 1)} style={{width:26, height:26, border:'1px solid #e2e8f0', background:'#fff', borderRadius:6, cursor:'pointer', fontWeight:600}}>−</button>
                    <span style={{fontSize:13, fontWeight:600, minWidth:24, textAlign:'center'}}>{i.quantity}</span>
                    <button onClick={() => updateQty(i.productId || i.product?.id, i.quantity + 1)} style={{width:26, height:26, border:'1px solid #e2e8f0', background:'#fff', borderRadius:6, cursor:'pointer', fontWeight:600}}>+</button>
                    <button onClick={() => removeItem(i.productId || i.product?.id)} style={{marginLeft:'auto', background:'none', border:'none', color:'#ef4444', cursor:'pointer', fontSize:12}} aria-label="Remove">{t('remove') || 'Remove'}</button>
                  </div>
                </div>
              </div>
            )
          })}
        </div>

        {/* Footer */}
        {items.length > 0 && (
          <div style={{padding:'16px 20px', borderTop:'1px solid #eef0f3', background:'#fff', flexShrink:0}}>
            <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12}}>
              <span style={{fontSize:14, fontWeight:600, color:'#0f1923'}}>{t('total')}</span>
              <span style={{fontSize:18, fontWeight:800, color:'#f97316'}}>{formatQAR(total)}</span>
            </div>
            <Link
              to="/cart"
              onClick={onClose}
              style={{display:'block', textAlign:'center', background:'#f97316', color:'#fff', padding:'12px 16px', borderRadius:10, textDecoration:'none', fontWeight:700, fontSize:14, marginBottom:8}}
            >
              {t('proceedToCheckout')}
            </Link>
            <Link
              to="/cart"
              onClick={onClose}
              style={{display:'block', textAlign:'center', background:'#fff', color:'#0f1923', padding:'10px 16px', borderRadius:10, textDecoration:'none', fontWeight:600, fontSize:13, border:'1px solid #e2e8f0'}}
            >
              View full cart
            </Link>
          </div>
        )}
      </div>
    </>
  )
}

function RecommendedForYou({ items = [], t = (k) => k }) {
  const [viewedIds, setViewedIds] = useState([])
  useEffect(() => {
    try { setViewedIds(JSON.parse(localStorage.getItem('recentlyViewed') || '[]')) }
    catch (e) { setViewedIds([]) }
  }, [])

  // Recommendation logic:
  //  1. Collect categories of recently-viewed products.
  //  2. Suggest products in those categories that the user has NOT already viewed.
  //  3. If no viewing history (or no matches), fall back to highest-rated products.
  let recs = []
  if (viewedIds.length > 0 && items.length > 0) {
    const viewed = viewedIds.map(id => items.find(p => p.id === id)).filter(Boolean)
    const preferredCategories = new Set(viewed.map(p => p.category?.name).filter(Boolean))
    recs = items
      .filter(p => !viewedIds.includes(p.id))
      .filter(p => preferredCategories.has(p.category?.name))
      .slice(0, 6)
  }
  if (recs.length < 4) {
    // top up with highest-rated products not already in recs and not viewed
    const used = new Set([...viewedIds, ...recs.map(r => r.id)])
    const fillers = items
      .filter(p => !used.has(p.id))
      .sort((a, b) => (b.avgRating || 0) - (a.avgRating || 0))
      .slice(0, 6 - recs.length)
    recs = [...recs, ...fillers]
  }
  if (recs.length === 0) return null

  return (
    <div className="jm-card" style={{marginBottom:24, padding:'18px 20px', background:'#fff', borderRadius:16, border:'1px solid #eef0f3', boxShadow:'0 4px 14px rgba(15,25,35,0.05)'}}>
      <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14}}>
        <h4 className="jm-card-title" style={{margin:0, fontSize:14, fontWeight:700, color:'#0f1923', textTransform:'uppercase', letterSpacing:0.5, display:'flex', alignItems:'center', gap:8}}>
          <span style={{fontSize:18}}>✨</span>
          {t('recommendedForYou')}
        </h4>
        {viewedIds.length === 0 && (
          <span className="jm-card-meta" style={{fontSize:11, color:'#94a3b8'}}>{t('topRated')}</span>
        )}
      </div>
      <div style={{display:'flex', gap:14, overflowX:'auto', paddingBottom:6}}>
        {recs.map(p => {
          const img = p.images?.[0]
          const src = getImageSrc(img)
          return (
            <Link
              key={p.id}
              to={`/products/${p.id}`}
              style={{flex:'0 0 140px', textDecoration:'none', color:'inherit'}}
            >
              <div className="jm-card-img" style={{width:140, height:140, borderRadius:12, background:'linear-gradient(180deg,#f8fafc,#eef2f7)', border:'1px solid #eef0f3', display:'flex', alignItems:'center', justifyContent:'center', overflow:'hidden', marginBottom:8, padding:8}}>
                {src ? (
                  <img src={src || FALLBACK_IMG} alt={p.name} style={{width:'100%', height:'100%', objectFit:'contain'}} onError={(e) => { if (e.currentTarget.src !== FALLBACK_IMG) e.currentTarget.src = FALLBACK_IMG }} />
                ) : (
                  <span style={{fontSize:40}}>📱</span>
                )}
              </div>
              <div className="jm-card-title" style={{fontSize:12, fontWeight:600, color:'#0f1923', lineHeight:1.3, marginBottom:4, overflow:'hidden', textOverflow:'ellipsis', display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical', minHeight:32}}>
                {p.name}
              </div>
              <div style={{fontSize:13, color:'#f97316', fontWeight:800}}>
                {formatQAR(p.price)}
              </div>
            </Link>
          )
        })}
      </div>
    </div>
  )
}

const styles = {
  nav: {
    display:'flex',
    justifyContent:'space-between',
    alignItems:'center',
    gap:12,
    padding:'14px 24px',
    background:'#0f1923',
    position:'sticky',
    top:0,
    zIndex:1000,
  },
  navMobile: {
    display:'flex',
    justifyContent:'space-between',
    alignItems:'center',
    gap:8,
    padding:'8px 12px',
    background:'#0f1923',
    position:'sticky',
    top:0,
    zIndex:1000,
    boxShadow:'0 1px 0 rgba(255,255,255,0.06)',
  },
  logo: {
    color:'#fff',
    fontSize:22,
    fontWeight:700,
    textDecoration:'none',
    whiteSpace:'nowrap',
  },
  logoMobile: {
    color:'#fff',
    fontSize:15,
    fontWeight:800,
    textDecoration:'none',
    whiteSpace:'nowrap',
    flex:1,
    letterSpacing:-0.2,
  },
  navLinks: {
    display:'flex',
    alignItems:'center',
    gap:24,
    marginLeft:'auto',
  },
  mobileActions: {
    display:'flex',
    alignItems:'center',
    gap:6,
    marginLeft:'auto',
  },
  smallBtn: {
    background:'rgba(255,255,255,0.04)',
    border:'1px solid rgba(255,255,255,0.75)',
    color:'white',
    padding:'4px 9px',
    borderRadius:6,
    cursor:'pointer',
    fontWeight:'bold',
    fontSize:12,
    minWidth:35,
    height:28,
    lineHeight:1,
  },
  menuBtn: {
    background:'rgba(255,255,255,0.04)',
    border:'1px solid rgba(255,255,255,0.75)',
    color:'white',
    fontSize:18,
    padding:'3px 8px',
    borderRadius:6,
    cursor:'pointer',
    lineHeight:1,
    minWidth:34,
    height:28,
  },
  mobileMenuBackdrop: {
    position:'fixed',
    inset:0,
    background:'rgba(0,0,0,0.34)',
    zIndex:9998,
  },
  mobileMenu: {
    position:'fixed',
    top:0,
    right:0,
    bottom:0,
    width:'74%',
    maxWidth:260,
    background:'#0f1923',
    borderLeft:'1px solid #334155',
    display:'flex',
    flexDirection:'column',
    zIndex:9999,
    boxShadow:'-12px 0 30px rgba(0,0,0,0.35)',
    overflowY:'auto',
  },
  mobileMenuHeader: {
    display:'flex',
    alignItems:'center',
    justifyContent:'space-between',
    padding:'16px',
    color:'#fff',
    borderBottom:'1px solid #1e293b',
    background:'#111c29',
  },
  mobileCloseBtn: {
    background:'transparent',
    border:'1px solid #475569',
    color:'#fff',
    width:30,
    height:30,
    borderRadius:8,
    fontSize:20,
    cursor:'pointer',
    lineHeight:1,
  },
  mobileMenuItem: {
    color:'#fff',
    textDecoration:'none',
    padding:'12px 16px',
    borderBottom:'1px solid #1e293b',
    fontSize:14,
    fontWeight:600,
  },
  mobileMenuPrimary: {
    color:'#fff',
    textDecoration:'none',
    margin:'14px 16px',
    padding:'13px 18px',
    borderRadius:10,
    fontSize:15,
    fontWeight:700,
    background:'#f97316',
    textAlign:'center',
  },
  mobileMenuButton: {
    background:'transparent',
    color:'#fff',
    border:'none',
    textAlign:'left',
    padding:'14px 18px',
    cursor:'pointer',
    fontSize:15,
    fontWeight:600,
    fontFamily:'inherit',
    borderBottom:'1px solid #1e293b',
  },
  navLink: { color:'#ccc', textDecoration:'none', fontSize:14 },
  registerBtn: { background:'#f97316', color:'#fff', padding:'8px 16px', borderRadius:8, textDecoration:'none', fontSize:14 },
  logoutBtn: { background:'transparent', border:'1px solid #555', color:'#ccc', padding:'6px 14px', borderRadius:8, cursor:'pointer', fontSize:14 },
  badge: { background:'#ef4444', color:'#fff', borderRadius:10, padding:'1px 6px', fontSize:11, marginLeft:4 },
  hero: { minHeight:'48vh', display:'flex', flexDirection:'column', justifyContent:'center', alignItems:'center', background:'linear-gradient(135deg, #0f1923 0%, #1e3a5f 100%)', color:'#fff', textAlign:'center', padding:32 },
  heroTitle: { fontSize:34, fontWeight:700, marginBottom:16 },
  heroSub: { fontSize:16, color:'#94a3b8', marginBottom:32 },
  heroBtn: { background:'#f97316', color:'#fff', padding:'14px 32px', borderRadius:12, textDecoration:'none', fontSize:16, fontWeight:600 },
  page: { maxWidth:1100, margin:'0 auto', padding:32 },
  search: { width:'100%', padding:'12px 16px', borderRadius:10, border:'1px solid #ddd', fontSize:15, marginBottom:24, boxSizing:'border-box' },
  grid: { display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(240px, 1fr))', gap:20 },
  gridMobile: { display:'grid', gridTemplateColumns:'repeat(2, minmax(0, 1fr))', gap:12 },
  card: { border:'1px solid #eee', borderRadius:12, overflow:'hidden', background:'#fff', boxShadow:'0 2px 8px rgba(0,0,0,0.06)' },
  cardImg: { height:140, display:'flex', alignItems:'center', justifyContent:'center', fontSize:56, background:'#f8f9fa', overflow:'hidden' },
  cardBody: { padding:16 },
  cardVendor: { fontSize:12, color:'#888', marginBottom:4 },
  cardName: { fontSize:15, fontWeight:600, marginBottom:8 },
  cardPrice: { fontSize:18, color:'#f97316', fontWeight:700, marginBottom:12 },
  cardBtn: { display:'block', textAlign:'center', background:'#f97316', color:'#fff', padding:'8px 16px', borderRadius:8, textDecoration:'none', fontSize:14 },
  formPage: { minHeight:'80vh', display:'flex', alignItems:'center', justifyContent:'center', background:'#f8f9fa' },
  formBox: { background:'#fff', padding:40, borderRadius:16, width:400, boxShadow:'0 4px 20px rgba(0,0,0,0.08)' },
  formTitle: { fontSize:24, fontWeight:700, marginBottom:24, textAlign:'center' },
  input: { width:'100%', padding:'12px 14px', borderRadius:8, border:'1px solid #ddd', fontSize:14, marginBottom:12, boxSizing:'border-box' },
  submitBtn: { width:'100%', padding:14, background:'#f97316', color:'#fff', border:'none', borderRadius:8, fontSize:15, fontWeight:600, cursor:'pointer' },
  error: { color:'#ef4444', fontSize:14, marginBottom:12 },
  cartItem: { display:'flex', alignItems:'center', padding:'16px 0', borderBottom:'1px solid #eee' },
  cartTotal: { marginTop:24, textAlign:'right' },
  qtyBtn: { width:30, height:30, borderRadius:6, border:'1px solid #ddd', background:'#f8f9fa', cursor:'pointer', fontSize:16 },
  removeBtn: { background:'transparent', border:'none', color:'#ef4444', cursor:'pointer', fontSize:16, padding:'4px 8px' },
}

export default App
